<template>
  <div id="app">
    <!-- Sidebar card: shortcut reference -->
    <div v-if="deviceType && !sendLabel" class="sidebar-card">
      <div style="font-weight: bold; font-size: 17px; color: #303133; margin-bottom: 14px; padding-bottom: 10px; border-bottom: 2px solid #409EFF;">{{ $t('index.shortcutTitle') }}</div>

      <!-- 配置提示 - 移到最上面 -->
      <div style="margin-bottom: 16px; padding: 12px 14px; background: #fff9e6; border-radius: 12px; border: 1px solid #f5e6b8;">
        <div style="font-weight: bold; color: #D4880F; font-size: 14px; margin-bottom: 4px;">{{ $t('index.shortcutTip') }}</div>
        <div style="color: #8a7340; font-size: 13px; line-height: 1.6;">{{ $t('index.shortcutTipDesc') }}</div>
      </div>
      
      <div v-for="(group, gi) in shortcutGroups" :key="gi" style="margin-bottom: 18px;">
        <div style="font-weight: bold; color: #606266; font-size: 14px; margin-bottom: 6px; padding-left: 4px;">{{ group.name }}</div>
        <div v-for="(item, ii) in group.items" :key="ii" style="padding: 4px 4px; display: flex; align-items: center; gap: 10px; border-radius: 4px;">
          <code style="background: #e6f0ff; color: #409EFF; padding: 2px 8px; border-radius: 6px; font-size: 13px; white-space: nowrap; font-family: monospace; min-width: 90px; text-align: center;">{{ item.key }}</code>
          <span style="color: #606266; font-size: 13px;">{{ item.desc }}</span>
        </div>
      </div>
    </div>
    <!-- Main card: settings content -->
    <div class="main-card">
      <!-- Top-right hidden tools menu -->
      <el-popover
        v-model="showMainHelp"
        placement="bottom-end"
        width="210"
        trigger="click"
        popper-class="main-help-popover"
        @show="showMoreMenu = false"
        @hide="showMoreMenu = false">
        <div class="main-help-menu">
          <div
            v-if="!showMoreMenu"
            class="main-help-item"
            @click="showMoreMenu = true">
            <span>{{ $t('index.more') }}</span>
            <i class="el-icon-arrow-right"></i>
          </div>
          <div v-if="!showMoreMenu" class="main-help-item" @click="showAbout">
            <span>{{ $t('index.about') }}</span>
          </div>
          <template v-else>
            <div class="main-help-back" @click="showMoreMenu = false">
              <i class="el-icon-arrow-left"></i> {{ $t('index.back') }}
            </div>
            <div class="main-help-item" @click="openRf24PairFromHelp">
              <span>{{ $t('rf24Pair.entry') }}</span>
            </div>
          </template>
        </div>
        <el-button
          slot="reference"
          class="main-help-btn"
          circle
          icon="el-icon-question"
          :title="$t('index.more')"></el-button>
      </el-popover>

      <!-- Right-side floating tip bar -->
      <div v-if="deviceType && !sendLabel && !rightTipClosed" class="right-tip-bar">
        <button class="right-tip-close" type="button" @click="rightTipClosed = true">&times;</button>
        <div class="tip-icon">
          <i class="el-icon-info"></i>
        </div>
        <div class="tip-content">
          <div class="tip-title">{{ $t('index.submitTipTitle') }}</div>
          <div class="tip-desc">{{ $t('index.submitTipDesc') }}</div>
        </div>
        <div class="tip-arrow"></div>
      </div>
      <div class="main-header">
        <h1>{{ $t('index.pageTitle') }}</h1>
        <div class="main-header-actions">
          <el-button v-if="(deviceType==='D6' || deviceType==='D9' || deviceType==='D2') && !sendLabel" type="info" plain size="large" icon="el-icon-s-tools" @click="openFactoryTest">{{ $t('index.factoryTest') }}</el-button>
          <el-button type="primary" plain size="large" icon="el-icon-thumb" @click="showKeyTester = true">{{ $t('workspace.test') }}</el-button>
          <el-button 
            type="warning" 
            size="large" 
            class="video-tutorial-btn"
            @click="showVideoModal = true">
            <i class="el-icon-video-play"></i>{{ $t('keyConfig.videoTutorial') }}
          </el-button>
        </div>
      </div>
      <!-- Device layout diagram - only show for C4 -->
      <el-row v-if="deviceType==='C4'" style="margin-bottom: 20px; display: flex; justify-content: center;">
        <div style="display: flex; align-items: center; gap: 24px; padding: 16px 30px; background: #f8f9fa; border-radius: 16px;">
          <!-- 2x2 key grid (田字型) -->
          <div style="display: flex; flex-direction: column; gap: 8px;">
            <div style="display: flex; gap: 8px;">
              <div style="width: 54px; height: 54px; background: linear-gradient(135deg, #e6f0ff, #d4e5ff); border: 2px solid #409EFF; border-radius: 10px; display: flex; align-items: center; justify-content: center; font-size: 18px; font-weight: bold; color: #409EFF; box-shadow: 0 2px 6px rgba(64,158,255,0.25);">1</div>
              <div style="width: 54px; height: 54px; background: linear-gradient(135deg, #e6f0ff, #d4e5ff); border: 2px solid #409EFF; border-radius: 10px; display: flex; align-items: center; justify-content: center; font-size: 18px; font-weight: bold; color: #409EFF; box-shadow: 0 2px 6px rgba(64,158,255,0.25);">2</div>
            </div>
            <div style="display: flex; gap: 8px;">
              <div style="width: 54px; height: 54px; background: linear-gradient(135deg, #e6f0ff, #d4e5ff); border: 2px solid #409EFF; border-radius: 10px; display: flex; align-items: center; justify-content: center; font-size: 18px; font-weight: bold; color: #409EFF; box-shadow: 0 2px 6px rgba(64,158,255,0.25);">3</div>
              <div style="width: 54px; height: 54px; background: linear-gradient(135deg, #e6f0ff, #d4e5ff); border: 2px solid #409EFF; border-radius: 10px; display: flex; align-items: center; justify-content: center; font-size: 18px; font-weight: bold; color: #409EFF; box-shadow: 0 2px 6px rgba(64,158,255,0.25);">4</div>
            </div>
          </div>
          <!-- Knob on the right, vertically centered -->
          <div style="width: 72px; height: 72px; background: linear-gradient(135deg, #f0f2f5, #e4e7ed); border: 3px solid #909399; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 16px; font-weight: bold; color: #606266; box-shadow: 0 2px 10px rgba(0,0,0,0.1);">{{ $t('index.knobLabel') }}</div>
        </div>
      </el-row>
      <el-row class="change-language">
        <el-button type="text" @click="changeLang('zh')" :class="[$i18n.locale === 'zh' ? 'light' : 'normal']">中文</el-button>
        <span>&nbsp;|&nbsp;</span>
        <el-button type="text" @click="changeLang('en')" :class="[$i18n.locale === 'en' ? 'light' : 'normal']">English</el-button>
      </el-row>
      <el-row>
      <el-popover
        placement="bottom"
        width="800"
        trigger="click">
        <div style="text-align: left;font-family: Helvetica, sans-serif; padding: 0 15px; max-height: 600px; overflow-y: auto;">
          <el-row style="margin-bottom: 12px; text-align: center;"><h2>{{ $t('index.helpTitle') }}</h2></el-row>
          <el-row style="margin-bottom: 8px;font-size: 15px;font-weight: bold;color: #303133;">{{ $t('index.helpTips0') }}</el-row>
          <el-row style="margin-bottom: 6px;padding-left: 15px;">{{ $t('index.helpTips1') }}</el-row>
          <el-row style="margin-bottom: 6px;padding-left: 15px;color: #909399;">{{ $t('index.helpTips1_1') }}</el-row>
          <el-row style="margin-bottom: 6px;padding-left: 15px;">{{ $t('index.helpTips2') }}</el-row>
          <el-row v-if="deviceType!=='D6' && deviceType!=='D9'" style="margin-bottom: 6px;padding-left: 15px;color: #909399;">{{ $t('index.helpTips2_1') }}</el-row>
          <el-row style="margin-bottom: 6px;padding-left: 15px;">{{ $t('index.helpTips3') }}</el-row>
          <el-row style="margin-bottom: 6px;padding-left: 15px;">{{ $t('index.helpTips4') }}</el-row>
          <el-row style="margin-bottom: 6px;padding-left: 15px;">{{ $t('index.helpTips5') }}</el-row>
          <el-row style="margin-bottom: 6px;padding-left: 15px;">{{ $t('index.helpTips6') }}</el-row>
          <el-row style="margin-bottom: 8px;font-size: 15px;font-weight: bold;color: #303133;margin-top: 15px;border-top: 1px dashed #ebeef5;padding-top: 10px;">{{ $t('index.troubleshootTitle') }}</el-row>
          <el-row style="margin-bottom: 4px;padding-left: 15px;"><strong>{{ $t('index.troubleshoot1') }}</strong>: {{ $t('index.troubleshoot1_desc') }}</el-row>
          <el-row style="margin-bottom: 4px;padding-left: 15px;"><strong>{{ $t('index.troubleshoot2') }}</strong>: {{ $t('index.troubleshoot2_desc') }}</el-row>
          <el-row style="margin-bottom: 4px;padding-left: 15px;"><strong>{{ $t('index.troubleshoot3') }}</strong>: {{ $t('index.troubleshoot3_desc') }}</el-row>
          <el-row style="margin-bottom: 4px;padding-left: 15px;"><strong>{{ $t('index.troubleshoot4') }}</strong>: {{ $t('index.troubleshoot4_desc') }}</el-row>
          <el-row style="margin-bottom: 4px;padding-left: 15px;"><strong>{{ $t('index.troubleshoot5') }}</strong>: {{ $t('index.troubleshoot5_desc') }}</el-row>
          <el-row style="margin-bottom: 4px;padding-left: 15px;"><strong>{{ $t('index.troubleshoot6') }}</strong>: {{ $t('index.troubleshoot6_desc') }}</el-row>
          <el-row style="margin-bottom: 4px;padding-left: 15px;"><strong>{{ $t('index.troubleshoot7') }}</strong>: {{ $t('index.troubleshoot7_desc') }}</el-row>
          <el-row style="margin-bottom: 8px;font-size: 15px;font-weight: bold;color: #303133;margin-top: 15px;border-top: 1px dashed #ebeef5;padding-top: 10px;">{{ $t('index.windowsTipsTitle') }}</el-row>
          <el-row style="margin-bottom: 4px;padding-left: 15px;">{{ $t('index.windowsTips1') }}</el-row>
          <el-row style="margin-bottom: 4px;padding-left: 15px;">{{ $t('index.windowsTips2') }}</el-row>
          <el-row style="margin-bottom: 4px;padding-left: 15px;">{{ $t('index.windowsTips3') }}</el-row>
          <el-row style="margin-bottom: 4px;padding-left: 15px;">{{ $t('index.windowsTips4') }}</el-row>
          <el-row style="margin-bottom: 8px;font-size: 15px;font-weight: bold;color: #303133;margin-top: 15px;border-top: 1px dashed #ebeef5;padding-top: 10px;">{{ $t('index.macTipsTitle') }}</el-row>
          <el-row style="margin-bottom: 4px;padding-left: 15px;"><strong>{{ $t('index.macTips1') }}</strong>: {{ $t('index.macTips1_desc') }}</el-row>
          <el-row style="margin-bottom: 4px;padding-left: 15px;"><strong>{{ $t('index.macTips2') }}</strong>: {{ $t('index.macTips2_desc') }}</el-row>
          <el-row style="margin-bottom: 4px;padding-left: 15px;"><strong>{{ $t('index.macTips3') }}</strong>: {{ $t('index.macTips3_desc') }}</el-row>
          <el-row style="margin-bottom: 4px;padding-left: 15px;"><strong>{{ $t('index.macTips4') }}</strong>: {{ $t('index.macTips4_desc') }}</el-row>
          <el-row style="margin-bottom: 4px;padding-left: 15px;"><strong>{{ $t('index.macTips5') }}</strong>: {{ $t('index.macTips5_desc') }}</el-row>
          <el-row style="margin-bottom: 4px;padding-left: 15px;"><strong>{{ $t('index.macTips6') }}</strong>: {{ $t('index.macTips6_desc') }}</el-row>
        </div>
        <el-button slot="reference" type="info" icon="el-icon-chat-dot-round">{{ $t('index.helpButton') }}</el-button>
      </el-popover>
      </el-row>
      <el-row>      
        <el-button :type="conButtonType" @click="conClick()" :disabled="savingV62 || deviceConnecting" icon="el-icon-cpu" style="font-size: 18px; height: 50px; width: 220px;">{{ conButton }}</el-button>
      </el-row>
      <div class="connection-tip-row">
        <i class="el-icon-warning-outline"></i>
        {{ $t('index.connectionTip1') }}
        <strong>3mode_6key</strong> {{ $t('index.connectionTip2') }} <strong>3mode_9key</strong>{{ $t('index.connectionTip3') }} <strong>2.4 RX Head</strong>{{ $t('index.connectionTip4') }}
      </div>
      <div v-if="!sendLabel">
        <div v-if="deviceType && deviceType!=='B3' && deviceType!=='C4' && deviceType!=='B6' && deviceType!=='D2' && deviceType!=='B4' && deviceType!=='E6' && deviceType!=='D6' && deviceType!=='D9'">
      <div class="key-card">
      <el-row>
        <key-config ref="key1config" :labelstr="$t('index.b3key1')" :stringshow="true" :mouseoptshow="true" :mousedoubleEnable="deviceType==='B2' && deviceVersion==='02'"></key-config>
      </el-row>
      </div>
      <el-row style="margin-bottom: 20px;">
        <label>{{ $t('index.loopInterval') }} - {{ $t('index.hour') }}: </label>
        <el-input-number v-model="timerHour" :min="0" :max="23" :controls="true" style="width: 120px;"></el-input-number>
        <label style="margin-left: 20px;">{{ $t('index.minute') }}: </label>
        <el-input-number v-model="timerMinute" :min="0" :max="59" :controls="true" style="width: 120px;"></el-input-number>
        <label style="margin-left: 20px;">{{ $t('index.second') }}: </label>
        <el-input-number v-model="timerSecond" :min="0" :max="59" :controls="true" style="width: 120px;"></el-input-number>
      </el-row> 
      <el-row style="margin-bottom: 20px;">
        <label>{{ $t('index.triggerMode') }}: </label>
        <el-select v-model="triggerMode" style="width: 150px;">
          <el-option :label="$t('index.triggerModeManual')" :value="0"></el-option>
          <el-option :label="$t('index.triggerModeTimer')" :value="1"></el-option>
          <el-option :label="$t('index.triggerModeBoth')" :value="2"></el-option>
        </el-select>
      </el-row>
      </div>
      <div v-if="deviceType==='B3'">
      <div class="b3-key-schematic">
        <h4 class="b3-sch-title"><i class="el-icon-magic-stick"></i>{{ $t('index.schematicTitle') }}</h4>
        <div class="b3-sch-grid">
          <div class="b3-sch-key" :class="{ 'b3-sch-active': selectedB3Key === 1 }" :style="{ '--backlight-color': getBacklightColor(backlightColor1) }" @click="selectB3Key(1)">
            <div class="b3-sch-key-num">1</div>
            <div class="b3-sch-key-label">{{ $t('index.b3key1') }}</div>
            <div class="b3-sch-key-value">{{ b3KeyDesc1 }}</div>
          </div>
          <div class="b3-sch-key" :class="{ 'b3-sch-active': selectedB3Key === 2 }" :style="{ '--backlight-color': getBacklightColor(backlightColor2) }" @click="selectB3Key(2)">
            <div class="b3-sch-key-num">2</div>
            <div class="b3-sch-key-label">{{ $t('index.b3key2') }}</div>
            <div class="b3-sch-key-value">{{ b3KeyDesc2 }}</div>
          </div>
          <div class="b3-sch-key" :class="{ 'b3-sch-active': selectedB3Key === 3 }" :style="{ '--backlight-color': getBacklightColor(backlightColor3) }" @click="selectB3Key(3)">
            <div class="b3-sch-key-num">3</div>
            <div class="b3-sch-key-label">{{ $t('index.b3key3') }}</div>
            <div class="b3-sch-key-value">{{ b3KeyDesc3 }}</div>
          </div>
        </div>
      </div>
      <div class="key-card">
      <el-row>
        <key-config ref="b3key1config" :labelstr="$t('index.b3key1')" :stringshow="true" :mouseoptshow="true" :mouseclickonly="true" :mousedoubleEnable="parseInt(deviceVersion,10)>=2" :hidden-media-values="compactKeyboardHiddenMediaValues"></key-config>
      </el-row>
      </div>
      <div class="key-card">
      <el-row>
        <key-config ref="b3key2config" :labelstr="$t('index.b3key2')" :stringshow="true" :mouseoptshow="true" :mouseclickonly="true" :mousedoubleEnable="parseInt(deviceVersion,10)>=2" :hidden-media-values="compactKeyboardHiddenMediaValues"></key-config>
      </el-row>
      </div>
      <div class="key-card">
      <el-row>
        <key-config ref="b3key3config" :labelstr="$t('index.b3key3')" :stringshow="true" :mouseoptshow="true" :mouseclickonly="true" :mousedoubleEnable="parseInt(deviceVersion,10)>=2" :hidden-media-values="compactKeyboardHiddenMediaValues"></key-config>
      </el-row>
      </div>
      <div v-if="deviceType==='B3'" class="backlight-card">
        <h4 style="margin-top: 0; margin-bottom: 12px; color: #606266;">{{ $t('index.backlightTitle') }}</h4>
        <el-row style="margin-bottom: 12px;">
          <span style="margin-right: 10px; line-height: 40px;">{{ $t('index.backlightMode') }}:</span>
          <el-radio-group v-model="backlightMode">
            <el-radio :label="0">{{ $t('index.backlightMode0') }}</el-radio>
            <el-radio :label="1">{{ $t('index.backlightMode1') }}</el-radio>
            <el-radio :label="2">{{ $t('index.backlightMode2') }}</el-radio>
            <el-radio :label="3">{{ $t('index.backlightMode3') }}</el-radio>
            <el-radio :label="4" v-if="parseInt(deviceVersion,10)>=2">{{ $t('index.backlightMode4') }}</el-radio>
          </el-radio-group>
        </el-row>
        <el-row style="margin-bottom: 12px;">
          <span style="margin-right: 10px; line-height: 40px;">{{ $t('index.backlightColor1') }}:</span>
          <el-select v-model="backlightColor1" size="small" style="width: 100px;">
            <el-option :value="1" :label="$t('index.colorBlue')"></el-option>
            <el-option :value="2" :label="$t('index.colorGreen')"></el-option>
            <el-option :value="3" :label="$t('index.colorRed')"></el-option>
            <el-option :value="4" :label="$t('index.colorYellow')"></el-option>
            <el-option :value="5" :label="$t('index.colorCyan')"></el-option>
            <el-option :value="6" :label="$t('index.colorPurple')"></el-option>
            <el-option :value="7" :label="$t('index.colorWhite')"></el-option>
          </el-select>
          <span style="margin: 0 15px; line-height: 40px;">{{ $t('index.backlightColor2') }}:</span>
          <el-select v-model="backlightColor2" size="small" style="width: 100px;">
            <el-option :value="1" :label="$t('index.colorBlue')"></el-option>
            <el-option :value="2" :label="$t('index.colorGreen')"></el-option>
            <el-option :value="3" :label="$t('index.colorRed')"></el-option>
            <el-option :value="4" :label="$t('index.colorYellow')"></el-option>
            <el-option :value="5" :label="$t('index.colorCyan')"></el-option>
            <el-option :value="6" :label="$t('index.colorPurple')"></el-option>
            <el-option :value="7" :label="$t('index.colorWhite')"></el-option>
          </el-select>
          <span style="margin: 0 15px; line-height: 40px;">{{ $t('index.backlightColor3') }}:</span>
          <el-select v-model="backlightColor3" size="small" style="width: 100px;">
            <el-option :value="1" :label="$t('index.colorBlue')"></el-option>
            <el-option :value="2" :label="$t('index.colorGreen')"></el-option>
            <el-option :value="3" :label="$t('index.colorRed')"></el-option>
            <el-option :value="4" :label="$t('index.colorYellow')"></el-option>
            <el-option :value="5" :label="$t('index.colorCyan')"></el-option>
            <el-option :value="6" :label="$t('index.colorPurple')"></el-option>
            <el-option :value="7" :label="$t('index.colorWhite')"></el-option>
          </el-select>
        </el-row>
        <el-row>
          <div style="text-align: center;">{{ $t('index.backlightBrightness') }}:</div>
          <div style="display: flex; justify-content: center;">
            <el-slider v-model="backlightBrightness" :min="0" :max="255" style="width: 300px;"></el-slider>
          </div>
        </el-row>
      </div>
      </div>
      <div v-if="deviceType==='C4'">
      <div class="c4-key-schematic">
        <h4 class="c4-sch-title"><i class="el-icon-magic-stick"></i>{{ $t('index.schematicTitle') }}</h4>
        <div class="c4-sch-layout">
          <div class="c4-sch-grid">
            <div class="c4-sch-key" :class="{ 'c4-sch-active': selectedC4Key === 1 }" :style="{ '--backlight-color': getBacklightColor(backlightColorC4_1) }" @click="selectC4Key(1)">
              <div class="c4-sch-key-num">1</div>
              <div class="c4-sch-key-label">{{ $t('index.c4key1') }}</div>
              <div class="c4-sch-key-value">{{ c4KeyDesc1 }}</div>
            </div>
            <div class="c4-sch-key" :class="{ 'c4-sch-active': selectedC4Key === 2 }" :style="{ '--backlight-color': getBacklightColor(backlightColorC4_2) }" @click="selectC4Key(2)">
              <div class="c4-sch-key-num">2</div>
              <div class="c4-sch-key-label">{{ $t('index.c4key2') }}</div>
              <div class="c4-sch-key-value">{{ c4KeyDesc2 }}</div>
            </div>
            <div class="c4-sch-key" :class="{ 'c4-sch-active': selectedC4Key === 3 }" :style="{ '--backlight-color': getBacklightColor(backlightColorC4_3) }" @click="selectC4Key(3)">
              <div class="c4-sch-key-num">3</div>
              <div class="c4-sch-key-label">{{ $t('index.c4key3') }}</div>
              <div class="c4-sch-key-value">{{ c4KeyDesc3 }}</div>
            </div>
            <div class="c4-sch-key" :class="{ 'c4-sch-active': selectedC4Key === 4 }" :style="{ '--backlight-color': getBacklightColor(backlightColorC4_4) }" @click="selectC4Key(4)">
              <div class="c4-sch-key-num">4</div>
              <div class="c4-sch-key-label">{{ $t('index.c4key4') }}</div>
              <div class="c4-sch-key-value">{{ c4KeyDesc4 }}</div>
            </div>
          </div>
          <div class="c4-sch-knob-container">
            <div class="c4-sch-knob-row">
              <div class="c4-sch-knob-arrow left" :class="{ 'c4-sch-active': selectedC4Key === 6 }" @click="selectC4Key(6)">
                <span class="c4-sch-knob-arrow-value">{{ c4Ec11CCWDesc }}</span>
              </div>
              <div class="c4-sch-knob" :class="{ 'c4-sch-active': selectedC4Key === 7 }" @click="selectC4Key(7)">
                <span class="c4-sch-knob-inner">O</span>
                <span class="c4-sch-knob-value-inner">{{ c4Ec11BtnDesc }}</span>
              </div>
              <div class="c4-sch-knob-arrow right" :class="{ 'c4-sch-active': selectedC4Key === 5 }" @click="selectC4Key(5)">
                <span class="c4-sch-knob-arrow-value">{{ c4Ec11CWDesc }}</span>
              </div>
            </div>
            <div class="c4-sch-knob-label">{{ $t('index.knobLabel') }}</div>
          </div>
        </div>
      </div>
      <div class="key-card"><el-row><key-config ref="c4key1config" :labelstr="$t('index.c4key1')" :stringshow="true" :mouseoptshow="true" :mouseclickonly="true" :mousedoubleEnable="true" :stringsize="10"></key-config></el-row></div>
      <div class="key-card"><el-row><key-config ref="c4key2config" :labelstr="$t('index.c4key2')" :stringshow="true" :mouseoptshow="true" :mouseclickonly="true" :mousedoubleEnable="true" :stringsize="10"></key-config></el-row></div>
      <div class="key-card"><el-row><key-config ref="c4key3config" :labelstr="$t('index.c4key3')" :stringshow="true" :mouseoptshow="true" :mouseclickonly="true" :mousedoubleEnable="true" :stringsize="10"></key-config></el-row></div>
      <div class="key-card"><el-row><key-config ref="c4key4config" :labelstr="$t('index.c4key4')" :stringshow="true" :mouseoptshow="true" :mouseclickonly="true" :mousedoubleEnable="true" :stringsize="10"></key-config></el-row></div>
      <div class="backlight-card">
        <h4 style="margin-top: 0; margin-bottom: 12px; color: #606266;">{{ $t('index.ec11Title') }}</h4>
        <el-row style="margin-bottom: 12px;">
          <span style="margin-right: 10px; line-height: 40px; font-weight: bold;">{{ $t('index.ec11CW') }}:</span>
          <el-select v-model="ec11CW_func" size="small" style="width: 120px; margin-right: 10px;">
            <el-option :value="0" :label="$t('index.ec11FuncKey')"></el-option>
            <el-option :value="1" :label="$t('index.ec11FuncMedia')"></el-option>
            <el-option :value="2" :label="$t('index.ec11FuncFn')"></el-option>
            <el-option :value="3" :label="$t('index.ec11FuncScroll')"></el-option>
            <el-option :value="4" :label="$t('index.ec11FuncMouse')"></el-option>
            <el-option :value="5" :label="$t('index.ec11FuncMix')" v-if="parseInt(deviceVersion,10)>=3"></el-option>
          </el-select>
          <template v-if="ec11CW_func===0">
            <span style="margin-right: 5px; line-height: 40px;">{{ $t('index.ec11KeyCode') }}:</span>
            <el-input v-model="ec11CW_keyChar" maxlength="1" size="small" style="width: 70px;" show-word-limit></el-input>
          </template>
          <template v-if="ec11CW_func===1">
            <el-select v-model="ec11CW_mediaVal" size="small" style="width: 180px;">
              <el-option v-for="(m,i) in mediaKeyOptions" :key="i" :value="m.value" :label="m.label"></el-option>
            </el-select>
          </template>
          <template v-if="ec11CW_func===2">
            <el-select v-model="ec11CW_fnVal" size="small" style="width: 180px;">
              <el-option v-for="(f,i) in fnKeyOptions" :key="i" :value="f.value" :label="f.label"></el-option>
            </el-select>
          </template>
          <template v-if="ec11CW_func===3">
            <el-select v-model="ec11CW_scrollDir" size="small" style="width: 120px;">
              <el-option :value="0" :label="$t('index.ec11ScrollUp')"></el-option>
              <el-option :value="1" :label="$t('index.ec11ScrollDown')"></el-option>
            </el-select>
          </template>
          <template v-if="ec11CW_func===4">
            <el-select v-model="ec11CW_mouseBtn" size="small" style="width: 100px; margin-right: 8px;">
              <el-option :value="1" :label="$t('index.ec11MouseLeft')"></el-option>
              <el-option :value="4" :label="$t('index.ec11MouseMiddle')"></el-option>
              <el-option :value="2" :label="$t('index.ec11MouseRight')"></el-option>
            </el-select>
            <el-select v-model="ec11CW_mouseMode" size="small" style="width: 120px;">
              <el-option :value="0" :label="$t('index.ec11ClickSingle')"></el-option>
              <el-option :value="1" :label="$t('index.ec11ClickContinuous')"></el-option>
              <el-option :value="2" :label="$t('index.ec11ClickPress')"></el-option>
            </el-select>
          </template>
        </el-row>
        <template v-if="ec11CW_func===5 && parseInt(deviceVersion,10)>=3">
        <el-row style="margin-top: 4px;">
          <template v-if="ec11CW_mixCountArray[0]">
            <el-select v-model="ec11CW_mixFuncSelectValue[0]" :placeholder="$t('keyConfig.select')" style="width:200px" v-if="ec11CW_mixShowArray[0]">
              <el-option v-for="item in fnKeyOptions" :key="item.value" :label="item.label" :value="item.value"></el-option>
            </el-select>
            <el-input style="width:70px" type="text" v-model="ec11CW_mixText[0]" maxlength="1" show-word-limit v-if="!ec11CW_mixShowArray[0]"></el-input>
            <div style="display:inline-block;" v-if="ec11CW_mixCountArray[1]">
            +
              <el-select v-model="ec11CW_mixFuncSelectValue[1]" :placeholder="$t('keyConfig.select')" style="width:200px"  v-if="ec11CW_mixShowArray[1]">
                <el-option v-for="item in fnKeyOptions" :key="item.value" :label="item.label" :value="item.value"></el-option>
              </el-select>
              <el-input style="width:70px" type="text" v-model="ec11CW_mixText[1]" maxlength="1" show-word-limit v-if="!ec11CW_mixShowArray[1]"></el-input>
            </div>
            <div style="display:inline-block;" v-if="ec11CW_mixCountArray[2]">
            +
              <el-select v-model="ec11CW_mixFuncSelectValue[2]" :placeholder="$t('keyConfig.select')" style="width:200px" v-if="ec11CW_mixShowArray[2]">
                <el-option v-for="item in fnKeyOptions" :key="item.value" :label="item.label" :value="item.value"></el-option>
              </el-select>
              <el-input style="width:70px" type="text" v-model="ec11CW_mixText[2]" maxlength="1" show-word-limit v-if="!ec11CW_mixShowArray[2]"></el-input>
            </div>
          </template>
        </el-row>
        <el-row style="margin-top: 4px;" v-if="ec11CW_mixCountArray[3]">
          +
          <el-select v-model="ec11CW_mixFuncSelectValue[3]" :placeholder="$t('keyConfig.select')" style="width:200px" v-if="ec11CW_mixShowArray[3]">
            <el-option v-for="item in fnKeyOptions" :key="item.value" :label="item.label" :value="item.value"></el-option>
          </el-select>
          <el-input style="width:70px" type="text" v-model="ec11CW_mixText[3]" maxlength="1" show-word-limit v-if="!ec11CW_mixShowArray[3]"></el-input>
          <div style="display:inline-block;" v-if="ec11CW_mixCountArray[4]">
          +
            <el-select v-model="ec11CW_mixFuncSelectValue[4]" :placeholder="$t('keyConfig.select')" style="width:200px" v-if="ec11CW_mixShowArray[4]">
              <el-option v-for="item in fnKeyOptions" :key="item.value" :label="item.label" :value="item.value"></el-option>
            </el-select>
            <el-input style="width:70px" type="text" v-model="ec11CW_mixText[4]" maxlength="1" show-word-limit v-if="!ec11CW_mixShowArray[4]"></el-input>
          </div>
        </el-row>
        <el-row style="margin-top: 4px;">
          <label v-html="ec11CW_mixTips"></label>
        </el-row>
        <el-row style="margin-top: 4px;">
          <el-button type="success" size="mini" @click="ec11CWMixAdd(1)">{{ $t('keyConfig.mix1') }}</el-button>
          <el-button type="success" size="mini" @click="ec11CWMixAdd(2)">{{ $t('keyConfig.mix2') }}</el-button>
          <el-button type="danger" size="mini" @click="ec11CWMixDelete()">{{ $t('keyConfig.mix3') }}</el-button>
          <el-button type="danger" size="mini" @click="ec11CWMixReset()">{{ $t('keyConfig.mix4') }}</el-button>
        </el-row>
        </template>
        <el-row style="margin-bottom: 12px;">
          <span style="margin-right: 10px; line-height: 40px; font-weight: bold;">{{ $t('index.ec11CCW') }}:</span>
          <el-select v-model="ec11CCW_func" size="small" style="width: 120px; margin-right: 10px;">
            <el-option :value="0" :label="$t('index.ec11FuncKey')"></el-option>
            <el-option :value="1" :label="$t('index.ec11FuncMedia')"></el-option>
            <el-option :value="2" :label="$t('index.ec11FuncFn')"></el-option>
            <el-option :value="3" :label="$t('index.ec11FuncScroll')"></el-option>
            <el-option :value="4" :label="$t('index.ec11FuncMouse')"></el-option>
            <el-option :value="5" :label="$t('index.ec11FuncMix')" v-if="parseInt(deviceVersion,10)>=3"></el-option>
          </el-select>
          <template v-if="ec11CCW_func===0">
            <span style="margin-right: 5px; line-height: 40px;">{{ $t('index.ec11KeyCode') }}:</span>
            <el-input v-model="ec11CCW_keyChar" maxlength="1" size="small" style="width: 70px;" show-word-limit></el-input>
          </template>
          <template v-if="ec11CCW_func===1">
            <el-select v-model="ec11CCW_mediaVal" size="small" style="width: 180px;">
              <el-option v-for="(m,i) in mediaKeyOptions" :key="i" :value="m.value" :label="m.label"></el-option>
            </el-select>
          </template>
          <template v-if="ec11CCW_func===2">
            <el-select v-model="ec11CCW_fnVal" size="small" style="width: 180px;">
              <el-option v-for="(f,i) in fnKeyOptions" :key="i" :value="f.value" :label="f.label"></el-option>
            </el-select>
          </template>
          <template v-if="ec11CCW_func===3">
            <el-select v-model="ec11CCW_scrollDir" size="small" style="width: 120px;">
              <el-option :value="0" :label="$t('index.ec11ScrollUp')"></el-option>
              <el-option :value="1" :label="$t('index.ec11ScrollDown')"></el-option>
            </el-select>
          </template>
          <template v-if="ec11CCW_func===4">
            <el-select v-model="ec11CCW_mouseBtn" size="small" style="width: 100px; margin-right: 8px;">
              <el-option :value="1" :label="$t('index.ec11MouseLeft')"></el-option>
              <el-option :value="4" :label="$t('index.ec11MouseMiddle')"></el-option>
              <el-option :value="2" :label="$t('index.ec11MouseRight')"></el-option>
            </el-select>
            <el-select v-model="ec11CCW_mouseMode" size="small" style="width: 120px;">
              <el-option :value="0" :label="$t('index.ec11ClickSingle')"></el-option>
              <el-option :value="1" :label="$t('index.ec11ClickContinuous')"></el-option>
              <el-option :value="2" :label="$t('index.ec11ClickPress')"></el-option>
            </el-select>
          </template>
        </el-row>
        <template v-if="ec11CCW_func===5 && parseInt(deviceVersion,10)>=3">
        <el-row style="margin-top: 4px;">
          <template v-if="ec11CCW_mixCountArray[0]">
            <el-select v-model="ec11CCW_mixFuncSelectValue[0]" :placeholder="$t('keyConfig.select')" style="width:200px" v-if="ec11CCW_mixShowArray[0]">
              <el-option v-for="item in fnKeyOptions" :key="item.value" :label="item.label" :value="item.value"></el-option>
            </el-select>
            <el-input style="width:70px" type="text" v-model="ec11CCW_mixText[0]" maxlength="1" show-word-limit v-if="!ec11CCW_mixShowArray[0]"></el-input>
            <div style="display:inline-block;" v-if="ec11CCW_mixCountArray[1]">
            +
              <el-select v-model="ec11CCW_mixFuncSelectValue[1]" :placeholder="$t('keyConfig.select')" style="width:200px"  v-if="ec11CCW_mixShowArray[1]">
                <el-option v-for="item in fnKeyOptions" :key="item.value" :label="item.label" :value="item.value"></el-option>
              </el-select>
              <el-input style="width:70px" type="text" v-model="ec11CCW_mixText[1]" maxlength="1" show-word-limit v-if="!ec11CCW_mixShowArray[1]"></el-input>
            </div>
            <div style="display:inline-block;" v-if="ec11CCW_mixCountArray[2]">
            +
              <el-select v-model="ec11CCW_mixFuncSelectValue[2]" :placeholder="$t('keyConfig.select')" style="width:200px" v-if="ec11CCW_mixShowArray[2]">
                <el-option v-for="item in fnKeyOptions" :key="item.value" :label="item.label" :value="item.value"></el-option>
              </el-select>
              <el-input style="width:70px" type="text" v-model="ec11CCW_mixText[2]" maxlength="1" show-word-limit v-if="!ec11CCW_mixShowArray[2]"></el-input>
            </div>
          </template>
        </el-row>
        <el-row style="margin-top: 4px;" v-if="ec11CCW_mixCountArray[3]">
          +
          <el-select v-model="ec11CCW_mixFuncSelectValue[3]" :placeholder="$t('keyConfig.select')" style="width:200px" v-if="ec11CCW_mixShowArray[3]">
            <el-option v-for="item in fnKeyOptions" :key="item.value" :label="item.label" :value="item.value"></el-option>
          </el-select>
          <el-input style="width:70px" type="text" v-model="ec11CCW_mixText[3]" maxlength="1" show-word-limit v-if="!ec11CCW_mixShowArray[3]"></el-input>
          <div style="display:inline-block;" v-if="ec11CCW_mixCountArray[4]">
          +
            <el-select v-model="ec11CCW_mixFuncSelectValue[4]" :placeholder="$t('keyConfig.select')" style="width:200px" v-if="ec11CCW_mixShowArray[4]">
              <el-option v-for="item in fnKeyOptions" :key="item.value" :label="item.label" :value="item.value"></el-option>
            </el-select>
            <el-input style="width:70px" type="text" v-model="ec11CCW_mixText[4]" maxlength="1" show-word-limit v-if="!ec11CCW_mixShowArray[4]"></el-input>
          </div>
        </el-row>
        <el-row style="margin-top: 4px;">
          <label v-html="ec11CCW_mixTips"></label>
        </el-row>
        <el-row style="margin-top: 4px;">
          <el-button type="success" size="mini" @click="ec11CCWMixAdd(1)">{{ $t('keyConfig.mix1') }}</el-button>
          <el-button type="success" size="mini" @click="ec11CCWMixAdd(2)">{{ $t('keyConfig.mix2') }}</el-button>
          <el-button type="danger" size="mini" @click="ec11CCWMixDelete()">{{ $t('keyConfig.mix3') }}</el-button>
          <el-button type="danger" size="mini" @click="ec11CCWMixReset()">{{ $t('keyConfig.mix4') }}</el-button>
        </el-row>
        </template>
        <el-row>
          <span style="margin-right: 10px; line-height: 40px; font-weight: bold;">{{ $t('index.ec11Btn') }}:</span>
          <el-select v-model="ec11Btn_func" size="small" style="width: 120px; margin-right: 10px;">
            <el-option :value="0" :label="$t('index.ec11FuncKey')"></el-option>
            <el-option :value="1" :label="$t('index.ec11FuncMedia')"></el-option>
            <el-option :value="2" :label="$t('index.ec11FuncFn')"></el-option>
            <el-option :value="4" :label="$t('index.ec11FuncMouse')"></el-option>
            <el-option :value="5" :label="$t('index.ec11FuncMix')" v-if="parseInt(deviceVersion,10)>=2"></el-option>
          </el-select>
          <template v-if="ec11Btn_func===0">
            <span style="margin-right: 5px; line-height: 40px;">{{ $t('index.ec11KeyCode') }}:</span>
            <el-input v-model="ec11Btn_keyChar" maxlength="1" size="small" style="width: 70px;" show-word-limit></el-input>
          </template>
          <template v-if="ec11Btn_func===1">
            <el-select v-model="ec11Btn_mediaVal" size="small" style="width: 180px;">
              <el-option v-for="(m,i) in mediaKeyOptions" :key="i" :value="m.value" :label="m.label"></el-option>
            </el-select>
          </template>
          <template v-if="ec11Btn_func===2">
            <el-select v-model="ec11Btn_fnVal" size="small" style="width: 180px;">
              <el-option v-for="(f,i) in fnKeyOptions" :key="i" :value="f.value" :label="f.label"></el-option>
            </el-select>
          </template>
          <template v-if="ec11Btn_func===4">
            <el-select v-model="ec11Btn_mouseBtn" size="small" style="width: 100px; margin-right: 8px;">
              <el-option :value="1" :label="$t('index.ec11MouseLeft')"></el-option>
              <el-option :value="4" :label="$t('index.ec11MouseMiddle')"></el-option>
              <el-option :value="2" :label="$t('index.ec11MouseRight')"></el-option>
            </el-select>
            <el-select v-model="ec11Btn_mouseMode" size="small" style="width: 120px;">
              <el-option :value="0" :label="$t('index.ec11ClickSingle')"></el-option>
              <el-option :value="1" :label="$t('index.ec11ClickContinuous')"></el-option>
              <el-option :value="2" :label="$t('index.ec11ClickPress')"></el-option>
            </el-select>
          </template>
        </el-row>
        <template v-if="ec11Btn_func===5 && parseInt(deviceVersion,10)>=2">
        <el-row style="margin-top: 4px;">
          <template v-if="ec11Btn_mixCountArray[0]">
            <el-select v-model="ec11Btn_mixFuncSelectValue[0]" :placeholder="$t('keyConfig.select')" style="width:200px" v-if="ec11Btn_mixShowArray[0]">
              <el-option v-for="item in fnKeyOptions" :key="item.value" :label="item.label" :value="item.value"></el-option>
            </el-select>
            <el-input style="width:70px" type="text" v-model="ec11Btn_mixText[0]" maxlength="1" show-word-limit v-if="!ec11Btn_mixShowArray[0]"></el-input>
            <div style="display:inline-block;" v-if="ec11Btn_mixCountArray[1]">
            +
              <el-select v-model="ec11Btn_mixFuncSelectValue[1]" :placeholder="$t('keyConfig.select')" style="width:200px"  v-if="ec11Btn_mixShowArray[1]">
                <el-option v-for="item in fnKeyOptions" :key="item.value" :label="item.label" :value="item.value"></el-option>
              </el-select>
              <el-input style="width:70px" type="text" v-model="ec11Btn_mixText[1]" maxlength="1" show-word-limit v-if="!ec11Btn_mixShowArray[1]"></el-input>
            </div>
            <div style="display:inline-block;" v-if="ec11Btn_mixCountArray[2]">
            +
              <el-select v-model="ec11Btn_mixFuncSelectValue[2]" :placeholder="$t('keyConfig.select')" style="width:200px" v-if="ec11Btn_mixShowArray[2]">
                <el-option v-for="item in fnKeyOptions" :key="item.value" :label="item.label" :value="item.value"></el-option>
              </el-select>
              <el-input style="width:70px" type="text" v-model="ec11Btn_mixText[2]" maxlength="1" show-word-limit v-if="!ec11Btn_mixShowArray[2]"></el-input>
            </div>
          </template>
        </el-row>
        <el-row style="margin-top: 4px;" v-if="ec11Btn_mixCountArray[3]">
          +
          <el-select v-model="ec11Btn_mixFuncSelectValue[3]" :placeholder="$t('keyConfig.select')" style="width:200px" v-if="ec11Btn_mixShowArray[3]">
            <el-option v-for="item in fnKeyOptions" :key="item.value" :label="item.label" :value="item.value"></el-option>
          </el-select>
          <el-input style="width:70px" type="text" v-model="ec11Btn_mixText[3]" maxlength="1" show-word-limit v-if="!ec11Btn_mixShowArray[3]"></el-input>
          <div style="display:inline-block;" v-if="ec11Btn_mixCountArray[4]">
          +
            <el-select v-model="ec11Btn_mixFuncSelectValue[4]" :placeholder="$t('keyConfig.select')" style="width:200px" v-if="ec11Btn_mixShowArray[4]">
              <el-option v-for="item in fnKeyOptions" :key="item.value" :label="item.label" :value="item.value"></el-option>
            </el-select>
            <el-input style="width:70px" type="text" v-model="ec11Btn_mixText[4]" maxlength="1" show-word-limit v-if="!ec11Btn_mixShowArray[4]"></el-input>
          </div>
        </el-row>
        <el-row style="margin-top: 4px;">
          <label v-html="ec11Btn_mixTips"></label>
        </el-row>
        <el-row style="margin-top: 4px;">
          <el-button type="success" size="mini" @click="ec11BtnMixAdd(1)">{{ $t('keyConfig.mix1') }}</el-button>
          <el-button type="success" size="mini" @click="ec11BtnMixAdd(2)">{{ $t('keyConfig.mix2') }}</el-button>
          <el-button type="danger" size="mini" @click="ec11BtnMixDelete()">{{ $t('keyConfig.mix3') }}</el-button>
          <el-button type="danger" size="mini" @click="ec11BtnMixReset()">{{ $t('keyConfig.mix4') }}</el-button>
        </el-row>
        </template>
      </div>
      <div class="backlight-card">
        <h4 style="margin-top: 0; margin-bottom: 12px; color: #606266;">{{ $t('index.backlightTitle') }}</h4>
        <el-row style="margin-bottom: 12px;">
          <span style="margin-right: 10px; line-height: 40px;">{{ $t('index.backlightMode') }}:</span>
          <el-radio-group v-model="backlightModeC4">
            <el-radio :label="0">{{ $t('index.backlightMode0') }}</el-radio>
            <el-radio :label="1">{{ $t('index.backlightMode1') }}</el-radio>
            <el-radio :label="2">{{ $t('index.backlightMode2') }}</el-radio>
            <el-radio :label="3">{{ $t('index.backlightMode3') }}</el-radio>
            <el-radio :label="4">{{ $t('index.backlightMode4') }}</el-radio>
          </el-radio-group>
        </el-row>
        <el-row style="margin-bottom: 12px;">
          <span style="margin-right: 10px; line-height: 40px;">{{ $t('index.backlightColor1') }}:</span>
          <el-select v-model="backlightColorC4_1" size="small" style="width: 100px;">
            <el-option v-for="c in colorOptions" :key="c.value" :value="c.value" :label="c.label"></el-option>
          </el-select>
          <span style="margin: 0 10px; line-height: 40px;">{{ $t('index.backlightColor2') }}:</span>
          <el-select v-model="backlightColorC4_2" size="small" style="width: 100px;">
            <el-option v-for="c in colorOptions" :key="c.value" :value="c.value" :label="c.label"></el-option>
          </el-select>
        </el-row>
        <el-row style="margin-bottom: 12px;">
          <span style="margin-right: 10px; line-height: 40px;">{{ $t('index.backlightColor3') }}:</span>
          <el-select v-model="backlightColorC4_3" size="small" style="width: 100px;">
            <el-option v-for="c in colorOptions" :key="c.value" :value="c.value" :label="c.label"></el-option>
          </el-select>
          <span style="margin: 0 10px; line-height: 40px;">{{ $t('index.backlightColor4') }}:</span>
          <el-select v-model="backlightColorC4_4" size="small" style="width: 100px;">
            <el-option v-for="c in colorOptions" :key="c.value" :value="c.value" :label="c.label"></el-option>
          </el-select>
        </el-row>
        <el-row>
          <div style="text-align: center;">{{ $t('index.backlightBrightness') }}:</div>
          <div style="display: flex; justify-content: center;">
            <el-slider v-model="backlightBrightnessC4" :min="0" :max="255" style="width: 300px;"></el-slider>
          </div>
        </el-row>
      </div>
      </div>
      <!-- B6: 6-key device -->
      <div v-if="deviceType==='B6'">
      <div class="b6-key-schematic">
        <h4 class="b6-sch-title"><i class="el-icon-magic-stick"></i>{{ $t('index.schematicTitle') }}</h4>
        <div class="b6-sch-grid">
          <div class="b6-sch-key" :class="{ 'b6-sch-active': selectedB6Key === 1 }" :style="{ '--backlight-color': getBacklightColor(backlightColorB6_1) }" @click="selectB6Key(1)">
            <div class="b6-sch-key-num">1</div>
            <div class="b6-sch-key-label">{{ $t('index.b6key1') }}</div>
            <div class="b6-sch-key-value">{{ b6KeyDesc1 }}</div>
          </div>
          <div class="b6-sch-key" :class="{ 'b6-sch-active': selectedB6Key === 2 }" :style="{ '--backlight-color': getBacklightColor(backlightColorB6_2) }" @click="selectB6Key(2)">
            <div class="b6-sch-key-num">2</div>
            <div class="b6-sch-key-label">{{ $t('index.b6key2') }}</div>
            <div class="b6-sch-key-value">{{ b6KeyDesc2 }}</div>
          </div>
          <div class="b6-sch-key" :class="{ 'b6-sch-active': selectedB6Key === 3 }" :style="{ '--backlight-color': getBacklightColor(backlightColorB6_3) }" @click="selectB6Key(3)">
            <div class="b6-sch-key-num">3</div>
            <div class="b6-sch-key-label">{{ $t('index.b6key3') }}</div>
            <div class="b6-sch-key-value">{{ b6KeyDesc3 }}</div>
          </div>
          <div class="b6-sch-key" :class="{ 'b6-sch-active': selectedB6Key === 4 }" :style="{ '--backlight-color': getBacklightColor(backlightColorB6_4) }" @click="selectB6Key(4)">
            <div class="b6-sch-key-num">4</div>
            <div class="b6-sch-key-label">{{ $t('index.b6key4') }}</div>
            <div class="b6-sch-key-value">{{ b6KeyDesc4 }}</div>
          </div>
          <div class="b6-sch-key" :class="{ 'b6-sch-active': selectedB6Key === 5 }" :style="{ '--backlight-color': getBacklightColor(backlightColorB6_5) }" @click="selectB6Key(5)">
            <div class="b6-sch-key-num">5</div>
            <div class="b6-sch-key-label">{{ $t('index.b6key5') }}</div>
            <div class="b6-sch-key-value">{{ b6KeyDesc5 }}</div>
          </div>
          <div class="b6-sch-key" :class="{ 'b6-sch-active': selectedB6Key === 6 }" :style="{ '--backlight-color': getBacklightColor(backlightColorB6_6) }" @click="selectB6Key(6)">
            <div class="b6-sch-key-num">6</div>
            <div class="b6-sch-key-label">{{ $t('index.b6key6') }}</div>
            <div class="b6-sch-key-value">{{ b6KeyDesc6 }}</div>
          </div>
        </div>
      </div>
      <div class="key-card"><el-row><key-config ref="b6key1config" :labelstr="$t('index.b6key1')" :stringshow="true" :mouseoptshow="true" :mouseclickonly="true" :mousedoubleEnable="true" :stringsize="10" :hidden-media-values="compactKeyboardHiddenMediaValues"></key-config></el-row></div>
      <div class="key-card"><el-row><key-config ref="b6key2config" :labelstr="$t('index.b6key2')" :stringshow="true" :mouseoptshow="true" :mouseclickonly="true" :mousedoubleEnable="true" :stringsize="10" :hidden-media-values="compactKeyboardHiddenMediaValues"></key-config></el-row></div>
      <div class="key-card"><el-row><key-config ref="b6key3config" :labelstr="$t('index.b6key3')" :stringshow="true" :mouseoptshow="true" :mouseclickonly="true" :mousedoubleEnable="true" :stringsize="10" :hidden-media-values="compactKeyboardHiddenMediaValues"></key-config></el-row></div>
      <div class="key-card"><el-row><key-config ref="b6key4config" :labelstr="$t('index.b6key4')" :stringshow="true" :mouseoptshow="true" :mouseclickonly="true" :mousedoubleEnable="true" :stringsize="10" :hidden-media-values="compactKeyboardHiddenMediaValues"></key-config></el-row></div>
      <div class="key-card"><el-row><key-config ref="b6key5config" :labelstr="$t('index.b6key5')" :stringshow="true" :mouseoptshow="true" :mouseclickonly="true" :mousedoubleEnable="true" :stringsize="10" :hidden-media-values="compactKeyboardHiddenMediaValues"></key-config></el-row></div>
      <div class="key-card"><el-row><key-config ref="b6key6config" :labelstr="$t('index.b6key6')" :stringshow="true" :mouseoptshow="true" :mouseclickonly="true" :mousedoubleEnable="true" :stringsize="10" :hidden-media-values="compactKeyboardHiddenMediaValues"></key-config></el-row></div>
      <div class="backlight-card">
        <h4 style="margin-top: 0; margin-bottom: 12px; color: #606266;">{{ $t('index.backlightTitle') }}</h4>
        <el-row style="margin-bottom: 12px;">
          <span style="margin-right: 10px; line-height: 40px;">{{ $t('index.backlightMode') }}:</span>
          <el-radio-group v-model="backlightModeB6">
            <el-radio :label="0">{{ $t('index.backlightMode0') }}</el-radio>
            <el-radio :label="1">{{ $t('index.backlightMode1') }}</el-radio>
            <el-radio :label="2">{{ $t('index.backlightMode2') }}</el-radio>
            <el-radio :label="3">{{ $t('index.backlightMode3') }}</el-radio>
            <el-radio :label="4">{{ $t('index.backlightMode4') }}</el-radio>
          </el-radio-group>
        </el-row>
        <el-row style="margin-bottom: 12px;">
          <span style="margin-right: 10px; line-height: 40px;">{{ $t('index.backlightColor1') }}:</span>
          <el-select v-model="backlightColorB6_1" size="small" style="width: 100px;">
            <el-option v-for="c in colorOptions" :key="c.value" :value="c.value" :label="c.label"></el-option>
          </el-select>
          <span style="margin: 0 10px; line-height: 40px;">{{ $t('index.backlightColor2') }}:</span>
          <el-select v-model="backlightColorB6_2" size="small" style="width: 100px;">
            <el-option v-for="c in colorOptions" :key="c.value" :value="c.value" :label="c.label"></el-option>
          </el-select>
          <span style="margin: 0 10px; line-height: 40px;">{{ $t('index.backlightColor3') }}:</span>
          <el-select v-model="backlightColorB6_3" size="small" style="width: 100px;">
            <el-option v-for="c in colorOptions" :key="c.value" :value="c.value" :label="c.label"></el-option>
          </el-select>
        </el-row>
        <el-row style="margin-bottom: 12px;">
          <span style="margin-right: 10px; line-height: 40px;">{{ $t('index.backlightColor4') }}:</span>
          <el-select v-model="backlightColorB6_4" size="small" style="width: 100px;">
            <el-option v-for="c in colorOptions" :key="c.value" :value="c.value" :label="c.label"></el-option>
          </el-select>
          <span style="margin: 0 10px; line-height: 40px;">{{ $t('index.backlightColor5') }}:</span>
          <el-select v-model="backlightColorB6_5" size="small" style="width: 100px;">
            <el-option v-for="c in colorOptions" :key="c.value" :value="c.value" :label="c.label"></el-option>
          </el-select>
          <span style="margin: 0 10px; line-height: 40px;">{{ $t('index.backlightColor6') }}:</span>
          <el-select v-model="backlightColorB6_6" size="small" style="width: 100px;">
            <el-option v-for="c in colorOptions" :key="c.value" :value="c.value" :label="c.label"></el-option>
          </el-select>
        </el-row>
        <el-row>
          <div style="text-align: center;">{{ $t('index.backlightBrightness') }}:</div>
          <div style="display: flex; justify-content: center;">
            <el-slider v-model="backlightBrightnessB6" :min="0" :max="255" style="width: 300px;"></el-slider>
          </div>
        </el-row>
      </div>
      </div>
      <!-- D2: 2-key device with Bluetooth page-turning -->
      <div v-if="deviceType==='D2'">
      <div class="d2-mode-card">
        <h4 class="d2-mode-title"><i class="el-icon-mobile-phone"></i>{{ $t('index.d2RunModeTitle') }}</h4>
        <div class="d2-mode-tip">
          <i class="el-icon-info"></i>
          <div class="d2-mode-tip-body">
            <div class="d2-mode-tip-line"><span class="d2-mode-tip-key">{{ $t('index.d2ModeTipKbd') }}</span>{{ $t('index.d2ModeTipKbdDesc') }}</div>
            <div class="d2-mode-tip-line"><span class="d2-mode-tip-key">{{ $t('index.d2ModeTipPage') }}</span>{{ $t('index.d2ModeTipPageDesc') }}</div>
          </div>
        </div>
        <div class="d2-field-row">
          <span class="d2-field-label">{{ $t('index.d2RunMode') }}:</span>
          <el-radio-group v-model="d2RunMode" class="d2-run-mode-group">
            <el-radio :label="0"><i class="el-icon-keyboard" style="margin-right:4px;"></i>{{ $t('index.d2RunModeKeyboard') }}</el-radio>
            <el-radio :label="1"><i class="el-icon-document-checked" style="margin-right:4px;"></i>{{ $t('index.d2RunModePage') }}</el-radio>
          </el-radio-group>
        </div>
        <div v-if="d2RunMode===1" class="d2-field-row d2-page-row">
          <span class="d2-field-label d2-page-label">{{ $t('index.d2PageAction') }}:</span>
          <el-radio-group v-model="d2PageAction" class="d2-page-action-group">
            <div class="d2-page-action-item">
              <div class="d2-pa-index">1</div>
              <el-radio :label="0"><span class="d2-pa-name">{{ $t('index.d2PagePrev') }}</span></el-radio>
            </div>
            <div class="d2-page-action-item">
              <div class="d2-pa-index">2</div>
              <el-radio :label="1"><span class="d2-pa-name">{{ $t('index.d2PageNext') }}</span></el-radio>
            </div>
            <div class="d2-page-action-item">
              <div class="d2-pa-index">3</div>
              <el-radio :label="2"><span class="d2-pa-name">{{ $t('index.d2PagePlayPause') }}</span></el-radio>
            </div>
            <div class="d2-page-action-item">
              <div class="d2-pa-index">4</div>
              <el-radio :label="3"><span class="d2-pa-name">{{ $t('index.d2PageHome') }}</span></el-radio>
            </div>
          </el-radio-group>
        </div>

        <div v-if="d2RunMode===1" class="d2-firm-block">
          <div class="d2-subtitle"><i class="el-icon-data-analysis"></i>{{ $t('index.d2PageTableTitle') }}</div>
          <table class="d2-firm-table">
            <thead>
              <tr>
                <th style="width: 15%;"><i class="el-icon-magic-stick"></i>&nbsp;{{ $t('index.d2TableFirmware') }}</th>
                <th style="width: 25%;">{{ $t('index.d2TableFunction') }}</th>
                <th style="width: 60%;">{{ $t('index.d2TableScenario') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td class="d2-fw-id d2-fw-1">{{ $t('index.firmware1') }}</td>
                <td>{{ $t('index.d2Firm1Func') }}</td>
                <td>{{ $t('index.d2Firm1Scenario') }}</td>
              </tr>
              <tr>
                <td class="d2-fw-id d2-fw-2">{{ $t('index.firmware2') }}</td>
                <td>{{ $t('index.d2Firm2Func') }}</td>
                <td>{{ $t('index.d2Firm2Scenario') }}</td>
              </tr>
              <tr>
                <td class="d2-fw-id d2-fw-3">{{ $t('index.firmware3') }}</td>
                <td>{{ $t('index.d2Firm3Func') }}</td>
                <td>{{ $t('index.d2Firm3Scenario') }}</td>
              </tr>
              <tr>
                <td class="d2-fw-id d2-fw-4">{{ $t('index.firmware4') }}</td>
                <td>{{ $t('index.d2Firm4Func') }}</td>
                <td>{{ $t('index.d2Firm4Scenario') }}</td>
              </tr>
            </tbody>
          </table>

          <div class="d2-notes-block">
            <div class="d2-subtitle"><i class="el-icon-warning-outline"></i>{{ $t('index.d2NotesTitle') }}</div>
            <div class="d2-note-item d2-note-1">
              <i class="el-icon-apple"></i>
              <div class="d2-note-body">{{ $t('index.d2Note1') }}</div>
            </div>
            <div class="d2-note-item d2-note-2">
              <i class="el-icon-reading"></i>
              <div class="d2-note-body">{{ $t('index.d2Note2') }}</div>
            </div>
            <div class="d2-note-item d2-note-3">
              <i class="el-icon-collection-tag"></i>
              <div class="d2-note-body">{{ $t('index.d2Note3') }}</div>
            </div>
          </div>
        </div>
      </div>

      <div v-show="d2RunMode===0" class="key-card"><el-row><key-config ref="d2key1config" :labelstr="$t('index.d2key1')" :stringshow="true" :mouseoptshow="true" :mouseclickonly="true" :mousedoubleEnable="true"></key-config></el-row></div>
      <div v-show="d2RunMode===0" class="key-card"><el-row><key-config ref="d2key2config" :labelstr="$t('index.d2key2')" :stringshow="true" :mouseoptshow="true" :mouseclickonly="true" :mousedoubleEnable="true"></key-config></el-row></div>
      </div>
      <div v-if="deviceType==='B4'">
      <div class="b4-key-schematic">
        <h4 class="b4-sch-title"><i class="el-icon-magic-stick"></i>{{ $t('index.schematicTitle') }}</h4>
        <div class="b4-sch-grid">
          <div class="b4-sch-key" :class="{ 'b4-sch-active': selectedB4Key === 1 }" :style="{ '--backlight-color': getBacklightColor(backlightColorB4_1) }" @click="selectB4Key(1)">
            <div class="b4-sch-key-num">1</div>
            <div class="b4-sch-key-label">{{ $t('index.b4key1') }}</div>
            <div class="b4-sch-key-value">{{ b4KeyDesc1 }}</div>
          </div>
          <div class="b4-sch-key" :class="{ 'b4-sch-active': selectedB4Key === 2 }" :style="{ '--backlight-color': getBacklightColor(backlightColorB4_2) }" @click="selectB4Key(2)">
            <div class="b4-sch-key-num">2</div>
            <div class="b4-sch-key-label">{{ $t('index.b4key2') }}</div>
            <div class="b4-sch-key-value">{{ b4KeyDesc2 }}</div>
          </div>
          <div class="b4-sch-key" :class="{ 'b4-sch-active': selectedB4Key === 3 }" :style="{ '--backlight-color': getBacklightColor(backlightColorB4_3) }" @click="selectB4Key(3)">
            <div class="b4-sch-key-num">3</div>
            <div class="b4-sch-key-label">{{ $t('index.b4key3') }}</div>
            <div class="b4-sch-key-value">{{ b4KeyDesc3 }}</div>
          </div>
          <div class="b4-sch-key" :class="{ 'b4-sch-active': selectedB4Key === 4 }" :style="{ '--backlight-color': getBacklightColor(backlightColorB4_4) }" @click="selectB4Key(4)">
            <div class="b4-sch-key-num">4</div>
            <div class="b4-sch-key-label">{{ $t('index.b4key4') }}</div>
            <div class="b4-sch-key-value">{{ b4KeyDesc4 }}</div>
          </div>
        </div>
      </div>
      <div class="key-card"><el-row><key-config ref="b4key1config" :labelstr="$t('index.b4key1')" :stringshow="true" :mouseoptshow="true" :mouseclickonly="true" :mousedoubleEnable="true" :stringsize="10" :hidden-media-values="compactKeyboardHiddenMediaValues"></key-config></el-row></div>
      <div class="key-card"><el-row><key-config ref="b4key2config" :labelstr="$t('index.b4key2')" :stringshow="true" :mouseoptshow="true" :mouseclickonly="true" :mousedoubleEnable="true" :stringsize="10" :hidden-media-values="compactKeyboardHiddenMediaValues"></key-config></el-row></div>
      <div class="key-card"><el-row><key-config ref="b4key3config" :labelstr="$t('index.b4key3')" :stringshow="true" :mouseoptshow="true" :mouseclickonly="true" :mousedoubleEnable="true" :stringsize="10" :hidden-media-values="compactKeyboardHiddenMediaValues"></key-config></el-row></div>
      <div class="key-card"><el-row><key-config ref="b4key4config" :labelstr="$t('index.b4key4')" :stringshow="true" :mouseoptshow="true" :mouseclickonly="true" :mousedoubleEnable="true" :stringsize="10" :hidden-media-values="compactKeyboardHiddenMediaValues"></key-config></el-row></div>
      <div class="backlight-card">
        <h4 style="margin-top: 0; margin-bottom: 12px; color: #606266;">{{ $t('index.backlightTitle') }}</h4>
        <el-row style="margin-bottom: 12px;">
          <span style="margin-right: 10px; line-height: 40px;">{{ $t('index.backlightMode') }}:</span>
          <el-radio-group v-model="backlightModeB4">
            <el-radio :label="0">{{ $t('index.backlightMode0') }}</el-radio>
            <el-radio :label="1">{{ $t('index.backlightMode1') }}</el-radio>
            <el-radio :label="2">{{ $t('index.backlightMode2') }}</el-radio>
            <el-radio :label="3">{{ $t('index.backlightMode3') }}</el-radio>
            <el-radio :label="4">{{ $t('index.backlightMode4') }}</el-radio>
          </el-radio-group>
        </el-row>
        <el-row style="margin-bottom: 12px;">
          <span style="margin-right: 10px; line-height: 40px;">{{ $t('index.backlightColor1') }}:</span>
          <el-select v-model="backlightColorB4_1" size="small" style="width: 100px;">
            <el-option :value="1" :label="$t('index.colorBlue')"></el-option>
            <el-option :value="2" :label="$t('index.colorGreen')"></el-option>
            <el-option :value="3" :label="$t('index.colorRed')"></el-option>
            <el-option :value="4" :label="$t('index.colorYellow')"></el-option>
            <el-option :value="5" :label="$t('index.colorCyan')"></el-option>
            <el-option :value="6" :label="$t('index.colorPurple')"></el-option>
            <el-option :value="7" :label="$t('index.colorWhite')"></el-option>
          </el-select>
          <span style="margin: 0 15px; line-height: 40px;">{{ $t('index.backlightColor2') }}:</span>
          <el-select v-model="backlightColorB4_2" size="small" style="width: 100px;">
            <el-option :value="1" :label="$t('index.colorBlue')"></el-option>
            <el-option :value="2" :label="$t('index.colorGreen')"></el-option>
            <el-option :value="3" :label="$t('index.colorRed')"></el-option>
            <el-option :value="4" :label="$t('index.colorYellow')"></el-option>
            <el-option :value="5" :label="$t('index.colorCyan')"></el-option>
            <el-option :value="6" :label="$t('index.colorPurple')"></el-option>
            <el-option :value="7" :label="$t('index.colorWhite')"></el-option>
          </el-select>
          <span style="margin: 0 15px; line-height: 40px;">{{ $t('index.backlightColor3') }}:</span>
          <el-select v-model="backlightColorB4_3" size="small" style="width: 100px;">
            <el-option :value="1" :label="$t('index.colorBlue')"></el-option>
            <el-option :value="2" :label="$t('index.colorGreen')"></el-option>
            <el-option :value="3" :label="$t('index.colorRed')"></el-option>
            <el-option :value="4" :label="$t('index.colorYellow')"></el-option>
            <el-option :value="5" :label="$t('index.colorCyan')"></el-option>
            <el-option :value="6" :label="$t('index.colorPurple')"></el-option>
            <el-option :value="7" :label="$t('index.colorWhite')"></el-option>
          </el-select>
          <span style="margin: 0 15px; line-height: 40px;">{{ $t('index.backlightColor4') }}:</span>
          <el-select v-model="backlightColorB4_4" size="small" style="width: 100px;">
            <el-option :value="1" :label="$t('index.colorBlue')"></el-option>
            <el-option :value="2" :label="$t('index.colorGreen')"></el-option>
            <el-option :value="3" :label="$t('index.colorRed')"></el-option>
            <el-option :value="4" :label="$t('index.colorYellow')"></el-option>
            <el-option :value="5" :label="$t('index.colorCyan')"></el-option>
            <el-option :value="6" :label="$t('index.colorPurple')"></el-option>
            <el-option :value="7" :label="$t('index.colorWhite')"></el-option>
          </el-select>
        </el-row>
        <el-row>
          <div style="text-align: center;">{{ $t('index.backlightBrightness') }}:</div>
          <div style="display: flex; justify-content: center;">
            <el-slider v-model="backlightBrightnessB4" :min="0" :max="255" style="width: 300px;"></el-slider>
          </div>
        </el-row>
      </div>
      </div>
      <!-- E6/D6/D9: 三模键盘 -->
      <div v-if="deviceType==='E6' || deviceType==='D6' || deviceType==='D9'">
      <div class="e6-mode-card">
        <h4 class="e6-mode-title"><i class="el-icon-connection"></i>{{ $t('index.e6ModeTitle') }}</h4>
        <el-row style="margin-bottom: 12px;">
          <span class="e6-field-label">{{ $t('index.e6DeviceMode') }}:</span>
          <el-radio-group v-model="e6DeviceMode" class="e6-device-mode-group">
            <el-radio :label="0x11"><i class="el-icon-wifi" style="margin-right:4px;"></i>{{ $t('index.e6ModeBle') }}</el-radio>
            <el-radio :label="0x24"><i class="el-icon-connection" style="margin-right:4px;"></i>{{ $t('index.e6ModeRf24') }}</el-radio>
            <el-radio :label="0x6b"><i class="el-icon-cpu" style="margin-right:4px;"></i>{{ $t('index.e6ModeUsb') }}</el-radio>
          </el-radio-group>
        </el-row>
        <div class="e6-mode-tip">
          <i class="el-icon-info"></i>{{ $t('index.e6ModeTip') }}
        </div>
      </div>
      <div class="e6-key-schematic">
        <h4 class="e6-sch-title"><i class="el-icon-magic-stick"></i>{{ $t('index.schematicTitle') }}</h4>
        <div class="e6-sch-grid">
          <div class="e6-sch-key" :class="{ 'e6-sch-active': selectedE6Key === 1 }" @click="selectE6Key(1)">
            <div class="e6-sch-key-num">1</div>
            <div class="e6-sch-key-label">{{ $t('index.e6key1') }}</div>
            <div class="e6-sch-key-value">{{ e6KeyDesc1 }}</div>
          </div>
          <div class="e6-sch-key" :class="{ 'e6-sch-active': selectedE6Key === 2 }" @click="selectE6Key(2)">
            <div class="e6-sch-key-num">2</div>
            <div class="e6-sch-key-label">{{ $t('index.e6key2') }}</div>
            <div class="e6-sch-key-value">{{ e6KeyDesc2 }}</div>
          </div>
          <div class="e6-sch-key" :class="{ 'e6-sch-active': selectedE6Key === 3 }" @click="selectE6Key(3)">
            <div class="e6-sch-key-num">3</div>
            <div class="e6-sch-key-label">{{ $t('index.e6key3') }}</div>
            <div class="e6-sch-key-value">{{ e6KeyDesc3 }}</div>
          </div>
          <div class="e6-sch-key" :class="{ 'e6-sch-active': selectedE6Key === 4 }" @click="selectE6Key(4)">
            <div class="e6-sch-key-num">4</div>
            <div class="e6-sch-key-label">{{ $t('index.e6key4') }}</div>
            <div class="e6-sch-key-value">{{ e6KeyDesc4 }}</div>
          </div>
          <div class="e6-sch-key" :class="{ 'e6-sch-active': selectedE6Key === 5 }" @click="selectE6Key(5)">
            <div class="e6-sch-key-num">5</div>
            <div class="e6-sch-key-label">{{ $t('index.e6key5') }}</div>
            <div class="e6-sch-key-value">{{ e6KeyDesc5 }}</div>
          </div>
          <div class="e6-sch-key" :class="{ 'e6-sch-active': selectedE6Key === 6 }" @click="selectE6Key(6)">
            <div class="e6-sch-key-num">6</div>
            <div class="e6-sch-key-label">{{ $t('index.e6key6') }}</div>
            <div class="e6-sch-key-value">{{ e6KeyDesc6 }}</div>
          </div>
          <template v-if="deviceType==='D9'">
            <div class="e6-sch-key" :class="{ 'e6-sch-active': selectedE6Key === 7 }" @click="selectE6Key(7)">
              <div class="e6-sch-key-num">7</div>
              <div class="e6-sch-key-label">{{ $t('index.d9key7') }}</div>
              <div class="e6-sch-key-value">{{ e6KeyDesc7 }}</div>
            </div>
            <div class="e6-sch-key" :class="{ 'e6-sch-active': selectedE6Key === 8 }" @click="selectE6Key(8)">
              <div class="e6-sch-key-num">8</div>
              <div class="e6-sch-key-label">{{ $t('index.d9key8') }}</div>
              <div class="e6-sch-key-value">{{ e6KeyDesc8 }}</div>
            </div>
            <div class="e6-sch-key" :class="{ 'e6-sch-active': selectedE6Key === 9 }" @click="selectE6Key(9)">
              <div class="e6-sch-key-num">9</div>
              <div class="e6-sch-key-label">{{ $t('index.d9key9') }}</div>
              <div class="e6-sch-key-value">{{ e6KeyDesc9 }}</div>
            </div>
          </template>
        </div>
      </div>
      <div id="e6keycard1" class="key-card e6-collapse-card">
  <div class="e6-collapse-head" @click="toggleE6Key(0)">
    <div class="e6-collapse-left">
      <span class="e6-collapse-name">{{ $t('index.e6key1') }}</span>
      <span class="e6-collapse-value">{{ e6KeyDesc1 || $t('keyConfig.notSet') }}</span>
    </div>
    <i :class="e6KeyOpen[0] ? 'el-icon-arrow-up' : 'el-icon-arrow-down'"></i>
  </div>
  <div v-show="e6KeyOpen[0]" class="e6-collapse-body">
    <el-row><key-config ref="e6key1config" :hid-normal-key="deviceType==='D6'||deviceType==='D9'" :labelstr="$t('index.e6key1')" :stringshow="true" :mouseoptshow="true" :mouseclickonly="true" :mousedoubleEnable="true" :stringsize="deviceType==='D6'||deviceType==='D9'?20:30"></key-config></el-row>
  </div>
</div>
      <div id="e6keycard2" class="key-card e6-collapse-card">
  <div class="e6-collapse-head" @click="toggleE6Key(1)">
    <div class="e6-collapse-left">
      <span class="e6-collapse-name">{{ $t('index.e6key2') }}</span>
      <span class="e6-collapse-value">{{ e6KeyDesc2 || $t('keyConfig.notSet') }}</span>
    </div>
    <i :class="e6KeyOpen[1] ? 'el-icon-arrow-up' : 'el-icon-arrow-down'"></i>
  </div>
  <div v-show="e6KeyOpen[1]" class="e6-collapse-body">
    <el-row><key-config ref="e6key2config" :hid-normal-key="deviceType==='D6'||deviceType==='D9'" :labelstr="$t('index.e6key2')" :stringshow="true" :mouseoptshow="true" :mouseclickonly="true" :mousedoubleEnable="true" :stringsize="deviceType==='D6'||deviceType==='D9'?20:30"></key-config></el-row>
  </div>
</div>
      <div id="e6keycard3" class="key-card e6-collapse-card">
  <div class="e6-collapse-head" @click="toggleE6Key(2)">
    <div class="e6-collapse-left">
      <span class="e6-collapse-name">{{ $t('index.e6key3') }}</span>
      <span class="e6-collapse-value">{{ e6KeyDesc3 || $t('keyConfig.notSet') }}</span>
    </div>
    <i :class="e6KeyOpen[2] ? 'el-icon-arrow-up' : 'el-icon-arrow-down'"></i>
  </div>
  <div v-show="e6KeyOpen[2]" class="e6-collapse-body">
    <el-row><key-config ref="e6key3config" :hid-normal-key="deviceType==='D6'||deviceType==='D9'" :labelstr="$t('index.e6key3')" :stringshow="true" :mouseoptshow="true" :mouseclickonly="true" :mousedoubleEnable="true" :stringsize="deviceType==='D6'||deviceType==='D9'?20:30"></key-config></el-row>
  </div>
</div>
      <div id="e6keycard4" class="key-card e6-collapse-card">
  <div class="e6-collapse-head" @click="toggleE6Key(3)">
    <div class="e6-collapse-left">
      <span class="e6-collapse-name">{{ $t('index.e6key4') }}</span>
      <span class="e6-collapse-value">{{ e6KeyDesc4 || $t('keyConfig.notSet') }}</span>
    </div>
    <i :class="e6KeyOpen[3] ? 'el-icon-arrow-up' : 'el-icon-arrow-down'"></i>
  </div>
  <div v-show="e6KeyOpen[3]" class="e6-collapse-body">
    <el-row><key-config ref="e6key4config" :hid-normal-key="deviceType==='D6'||deviceType==='D9'" :labelstr="$t('index.e6key4')" :stringshow="true" :mouseoptshow="true" :mouseclickonly="true" :mousedoubleEnable="true" :stringsize="deviceType==='D6'||deviceType==='D9'?20:30"></key-config></el-row>
  </div>
</div>
      <div id="e6keycard5" class="key-card e6-collapse-card">
  <div class="e6-collapse-head" @click="toggleE6Key(4)">
    <div class="e6-collapse-left">
      <span class="e6-collapse-name">{{ $t('index.e6key5') }}</span>
      <span class="e6-collapse-value">{{ e6KeyDesc5 || $t('keyConfig.notSet') }}</span>
    </div>
    <i :class="e6KeyOpen[4] ? 'el-icon-arrow-up' : 'el-icon-arrow-down'"></i>
  </div>
  <div v-show="e6KeyOpen[4]" class="e6-collapse-body">
    <el-row><key-config ref="e6key5config" :hid-normal-key="deviceType==='D6'||deviceType==='D9'" :labelstr="$t('index.e6key5')" :stringshow="true" :mouseoptshow="true" :mouseclickonly="true" :mousedoubleEnable="true" :stringsize="deviceType==='D6'||deviceType==='D9'?20:30"></key-config></el-row>
  </div>
</div>
      <div id="e6keycard6" class="key-card e6-collapse-card">
  <div class="e6-collapse-head" @click="toggleE6Key(5)">
    <div class="e6-collapse-left">
      <span class="e6-collapse-name">{{ $t('index.e6key6') }}</span>
      <span class="e6-collapse-value">{{ e6KeyDesc6 || $t('keyConfig.notSet') }}</span>
    </div>
    <i :class="e6KeyOpen[5] ? 'el-icon-arrow-up' : 'el-icon-arrow-down'"></i>
  </div>
  <div v-show="e6KeyOpen[5]" class="e6-collapse-body">
    <el-row><key-config ref="e6key6config" :hid-normal-key="deviceType==='D6'||deviceType==='D9'" :labelstr="$t('index.e6key6')" :stringshow="true" :mouseoptshow="true" :mouseclickonly="true" :mousedoubleEnable="true" :stringsize="deviceType==='D6'||deviceType==='D9'?20:30"></key-config></el-row>
  </div>
</div>
      <div v-if="deviceType==='D9'" id="e6keycard7" class="key-card e6-collapse-card">
  <div class="e6-collapse-head" @click="toggleE6Key(6)">
    <div class="e6-collapse-left">
      <span class="e6-collapse-name">{{ $t('index.d9key7') }}</span>
      <span class="e6-collapse-value">{{ e6KeyDesc7 || $t('keyConfig.notSet') }}</span>
    </div>
    <i :class="e6KeyOpen[6] ? 'el-icon-arrow-up' : 'el-icon-arrow-down'"></i>
  </div>
  <div v-show="e6KeyOpen[6]" class="e6-collapse-body">
    <el-row><key-config ref="e6key7config" :hid-normal-key="true" :labelstr="$t('index.d9key7')" :stringshow="true" :mouseoptshow="true" :mouseclickonly="true" :mousedoubleEnable="true" :stringsize="20"></key-config></el-row>
  </div>
</div>
      <div v-if="deviceType==='D9'" id="e6keycard8" class="key-card e6-collapse-card">
  <div class="e6-collapse-head" @click="toggleE6Key(7)">
    <div class="e6-collapse-left">
      <span class="e6-collapse-name">{{ $t('index.d9key8') }}</span>
      <span class="e6-collapse-value">{{ e6KeyDesc8 || $t('keyConfig.notSet') }}</span>
    </div>
    <i :class="e6KeyOpen[7] ? 'el-icon-arrow-up' : 'el-icon-arrow-down'"></i>
  </div>
  <div v-show="e6KeyOpen[7]" class="e6-collapse-body">
    <el-row><key-config ref="e6key8config" :hid-normal-key="true" :labelstr="$t('index.d9key8')" :stringshow="true" :mouseoptshow="true" :mouseclickonly="true" :mousedoubleEnable="true" :stringsize="20"></key-config></el-row>
  </div>
</div>
      <div v-if="deviceType==='D9'" id="e6keycard9" class="key-card e6-collapse-card">
  <div class="e6-collapse-head" @click="toggleE6Key(8)">
    <div class="e6-collapse-left">
      <span class="e6-collapse-name">{{ $t('index.d9key9') }}</span>
      <span class="e6-collapse-value">{{ e6KeyDesc9 || $t('keyConfig.notSet') }}</span>
    </div>
    <i :class="e6KeyOpen[8] ? 'el-icon-arrow-up' : 'el-icon-arrow-down'"></i>
  </div>
  <div v-show="e6KeyOpen[8]" class="e6-collapse-body">
    <el-row><key-config ref="e6key9config" :hid-normal-key="true" :labelstr="$t('index.d9key9')" :stringshow="true" :mouseoptshow="true" :mouseclickonly="true" :mousedoubleEnable="true" :stringsize="20"></key-config></el-row>
  </div>
</div>
      </div>
      <el-row v-if="deviceType" style="margin-bottom: 10px; color: #909399; font-size: 14px; display: flex; align-items: center; justify-content: center; text-align: center; gap: 8px; flex-wrap: wrap;">
        <span>{{ $t('index.deviceTypeLabel') }}: {{ deviceType }}&nbsp;&nbsp;|&nbsp;&nbsp;{{ $t('index.deviceVersionLabel') }}: v{{ deviceVersion }}</span>
        <span v-if="deviceType==='D6' || deviceType==='D9' || deviceType==='E6'">&nbsp;&nbsp;|&nbsp;&nbsp;{{ $t('index.configSeqLabel') }}: {{ e6TimeSeq }}</span>
      </el-row>
      <el-row style="margin-bottom: 12px;">
        <el-button type="primary" @click="sendClick()" :loading="savingV62" :disabled="sendLabel || savingV62" style="width: 300px; height: 60px; font-size: 20px; border-radius: 12px;">{{sendText}}</el-button>   
      </el-row>
      <el-row style="margin-bottom: 30px;">
        <el-button type="warning" plain icon="el-icon-switch-button" :disabled="savingV62" @click="conClick()" style="width: 300px; height: 50px; font-size: 18px; border-radius: 12px;">{{ $t('index.disconnect') }}</el-button>
      </el-row>
      </div>

      <el-dialog
        :title="$t('index.factoryTest')"
        :visible.sync="showFactoryTest"
        width="780px"
        top="6vh"
        custom-class="factory-dialog"
        :close-on-click-modal="false"
        @close="closeFactoryTest">
        <div class="factory-toolbar">
          <el-button type="primary" icon="el-icon-refresh" :loading="factoryReading" :disabled="savingV62" @click="readFactoryTest">{{ $t('index.readStatus') }}</el-button>
          <el-checkbox v-model="factoryAuto" @change="factoryAutoChanged">{{ $t('index.autoRefresh') }}</el-checkbox>
          <span v-if="factoryError" class="factory-error">{{ factoryError }}</span>
        </div>
        <div class="factory-led-tip"><i class="el-icon-warning-outline"></i> {{ $t('index.factoryLedTip') }}</div>
        <div v-if="factoryAbnormal" class="factory-banner fail">{{ $t('index.factoryAbnormal') }}</div>
        <div v-else-if="factoryPass" class="factory-banner pass">{{ $t('index.factoryPassPrefix') }}{{ factoryKeyCount }}{{ $t('index.factoryPassSuffix') }}</div>
        <el-table v-if="factoryData" :data="factoryRows" :row-class-name="factoryRowClass" :cell-style="factoryCellStyle" border size="medium" style="width:100%;">
          <el-table-column prop="name" :label="$t('index.factoryItem')" width="210"></el-table-column>
          <el-table-column prop="value" :label="$t('index.factoryValue')"></el-table-column>
          <el-table-column :label="$t('index.factoryResult')" width="130" align="center">
            <template slot-scope="scope">
              <span v-if="scope.row.result==='pass'" class="factory-result pass"><i class="el-icon-check"></i> {{ $t('index.factoryPassShort') }}</span>
              <span v-else-if="scope.row.result==='fail'" class="factory-result fail"><i class="el-icon-close"></i> {{ $t('index.factoryFailShort') }}</span>
              <span v-else-if="scope.row.result==='wait'" class="factory-result wait"><i class="el-icon-time"></i> {{ $t('index.factoryWait') }}</span>
              <span v-else class="factory-result info">—</span>
            </template>
          </el-table-column>
        </el-table>
        <pre v-if="factoryRaw" class="factory-raw">{{ factoryRaw }}</pre>
        <div class="factory-note">{{ $t('index.factoryNote') }}</div>
      </el-dialog>

      <el-dialog
        :title="$t('workspace.test')"
        :visible.sync="showKeyTester"
        width="860px"
        top="4vh"
        class="key-test-modal"
        :close-on-click-modal="false">
        <keyboard-tester ref="keyTester" :disabled="savingV62"></keyboard-tester>
      </el-dialog>

      <el-dialog
        :title="$t('rf24Pair.title')"
        :visible.sync="showRf24Pair"
        width="720px"
        top="6vh"
        class="rf24-pair-modal"
        :close-on-click-modal="false">
        <rf24-pair ref="rf24Pair"></rf24-pair>
      </el-dialog>

      <el-dialog 
        :title="$t('index.videoTitle')" 
        :visible.sync="showVideoModal" 
        width="800px" 
        top="5vh"
        class="video-modal"
        :close-on-click-modal="false">
        <div class="video-container">
          <video 
            controls 
            class="video-player"
            autoplay
            loop
            muted
            playsinline
            webkit-playsinline
            x5-video-player-type="h5"
            x5-video-player-fullscreen="true">
            <source :src="videoUrl" type="video/mp4">
            {{ $t('index.videoUnsupported') }}
          </video>
        </div>
      </el-dialog>

      <el-row style="text-align: center; padding-top: 30px; border-top: 1px solid #ebeef5; font-size: 18px;">
        <div style="margin-bottom: 8px;">{{ $t('index.keyTestPrompt') }}</div>
        <div style="margin-bottom: 6px;">
          <a :href="$t('index.keyTestUrl')" target="_blank" style="color: #409EFF; font-size: 18px; text-decoration: none; font-weight: bold;">{{ $t('index.keyTestLabel') }}</a>
        </div>
        <div>
          <a :href="$t('index.keyTestUrl2')" target="_blank" style="color: #409EFF; font-size: 18px; text-decoration: none; font-weight: bold;">{{ $t('index.keyTestLabel2') }}</a>
        </div>
        <div style="margin-top: 6px;">
          <a :href="$t('index.keyTestUrl3')" target="_blank" style="color: #67C23A; font-size: 18px; text-decoration: none; font-weight: bold;">{{ $t('index.keyTestLabel3') }}</a>
        </div>
        <div>
          <a :href="$t('index.keyTestUrl4')" target="_blank" style="color: #67C23A; font-size: 18px; text-decoration: none; font-weight: bold;">{{ $t('index.keyTestLabel4') }}</a>
        </div>
      </el-row>
    </div>

    <div v-if="deviceConnecting" class="connect-loading-mask">
      <div class="connect-loading-box">
        <div class="connect-spinner"></div>
        <template v-if="serialPortSelected">
          <div class="connect-loading-title">{{ $t('index.connecting') }}</div>
        </template>
        <template v-else>
          <div class="connect-loading-prompt">{{ $t('index.selectSerialPort') }}</div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import KeyConfig from './components/KeyConfig.vue';
import KeyboardTester from './components/KeyboardTester.vue';
import Rf24Pair from './components/Rf24Pair.vue';
import * as codec from './stability/codec.js';
import * as v62Bridge from './stability/legacy-bridge.js';
import axios from 'axios';
import videoFile from './assets/video_tutorial.mp4';
export default {
  components: { KeyConfig, KeyboardTester, Rf24Pair },
  methods: {   
    // CRC-16/CCITT-FALSE: poly=0x1021, init=0xFFFF, xorout=0x0000
    crc16Ccitt(data,start,endExclusive){
      var crc=0xffff;
      for(var i=start;i<endExclusive;i++){
        crc^=(data[i]<<8);
        for(var bit=0;bit<8;bit++){
          crc=(crc&0x8000) ? ((crc<<1)^0x1021) : (crc<<1);
          crc&=0xffff;
        }
      }
      return crc;
    },
    getV62KeyRefs(type){
      const names={
        B3:['b3key1config','b3key2config','b3key3config'],
        B4:['b4key1config','b4key2config','b4key3config','b4key4config'],
        C4:['c4key1config','c4key2config','c4key3config','c4key4config'],
        B6:['b6key1config','b6key2config','b6key3config','b6key4config','b6key5config','b6key6config']
      };
      return (names[type]||[]).map(name=>this.$refs[name]);
    },
    applyV62Lighting(type,lighting){
      if(type==='B3'){
        this.backlightMode=lighting.mode;
        [this.backlightColor1,this.backlightColor2,this.backlightColor3]=lighting.colors;
        this.backlightBrightness=lighting.brightness;
      }else if(type==='B4'){
        this.backlightModeB4=lighting.mode;
        [this.backlightColorB4_1,this.backlightColorB4_2,this.backlightColorB4_3,this.backlightColorB4_4]=lighting.colors;
        this.backlightBrightnessB4=lighting.brightness;
      }else if(type==='C4'){
        this.backlightModeC4=lighting.mode;
        [this.backlightColorC4_1,this.backlightColorC4_2,this.backlightColorC4_3,this.backlightColorC4_4]=lighting.colors;
        this.backlightBrightnessC4=lighting.brightness;
      }else if(type==='B6'){
        this.backlightModeB6=lighting.mode;
        [this.backlightColorB6_1,this.backlightColorB6_2,this.backlightColorB6_3,this.backlightColorB6_4,this.backlightColorB6_5,this.backlightColorB6_6]=lighting.colors;
        this.backlightBrightnessB6=lighting.brightness;
      }
    },
    async applyV62Snapshot(snapshot){
      const view=v62Bridge.decodePayload(snapshot.type,snapshot.payload);
      this.v62Protocol=true;
      this.v62Status=snapshot.status;
      this.deviceType=snapshot.type;
      this.deviceVersion='07';
      await this.$nextTick();
      const refs=this.getV62KeyRefs(snapshot.type);
      if(refs.length!==view.keys.length||refs.some(ref=>!ref))throw Error(this.$t('index.errPageConfig'));
      view.keys.forEach((item,index)=>refs[index].setKey(item.key,item.func,item.extra,item.text,item.mix));
      this.applyV62Lighting(snapshot.type,view.lighting);
      if(snapshot.type==='C4'){
        const dirs=['CW','CCW','Btn'];
        view.knobs.forEach((item,index)=>{
          const name='ec11'+dirs[index];
          this[name+'_key']=item.key;
          this[name+'_func']=item.func;
          this[name+'_keyF']=item.extra;
          this[name+'_keyMix']=item.mix.slice();
        });
        this.initEc11UI();
        this.updateC4KeyDescs();
      }else if(snapshot.type==='B3')this.updateB3KeyDescs();
      else if(snapshot.type==='B4')this.updateB4KeyDescs();
      else if(snapshot.type==='B6')this.updateB6KeyDescs();
      this.getCommonData();
      if(snapshot.status===1)this.$message.warning(this.$t('workspace.legacy'));
      else if(snapshot.status===2)this.$message.warning(this.$t('workspace.damaged'));
      else if(snapshot.status===3)this.$message.warning(this.$t('workspace.blank'));
    },
    v62Lighting(){
      if(this.deviceType==='B3')return {mode:this.backlightMode,colors:[this.backlightColor1,this.backlightColor2,this.backlightColor3],brightness:this.backlightBrightness};
      if(this.deviceType==='B4')return {mode:this.backlightModeB4,colors:[this.backlightColorB4_1,this.backlightColorB4_2,this.backlightColorB4_3,this.backlightColorB4_4],brightness:this.backlightBrightnessB4};
      if(this.deviceType==='C4')return {mode:this.backlightModeC4,colors:[this.backlightColorC4_1,this.backlightColorC4_2,this.backlightColorC4_3,this.backlightColorC4_4],brightness:this.backlightBrightnessC4};
      return {mode:this.backlightModeB6,colors:[this.backlightColorB6_1,this.backlightColorB6_2,this.backlightColorB6_3,this.backlightColorB6_4,this.backlightColorB6_5,this.backlightColorB6_6],brightness:this.backlightBrightnessB6};
    },
    collectV62Knob(dir){
      const name='ec11'+dir;
      const func=this[name+'_func'];
      const item={key:this.getEc11Key(dir),func:func,extra:this.getEc11KeyF(dir),mix:[255,255,255,255,255]};
      if(func!==5)return item;
      const count=this[name+'_mixCount'];
      if(count<2)throw Error((dir==='CW'?this.$t('index.ec11CW'):dir==='CCW'?this.$t('index.ec11CCW'):this.$t('index.ec11Btn'))+this.$t('keyConfig.mixErr'));
      const show=this[name+'_mixShowArray'],select=this[name+'_mixFuncSelectValue'],texts=this[name+'_mixText'];
      for(let i=0;i<count;i++){
        if(show[i]){
          if(select[i]==='')throw Error(this.$t('keyConfig.mixErr1')+(i+1)+this.$t('keyConfig.mixErr2'));
          item.mix[i]=select[i];
        }else{
          if(texts[i].length!==1||texts[i].charCodeAt(0)>255)throw Error(this.$t('keyConfig.mixErr1')+(i+1)+this.$t('keyConfig.mixErr3'));
          item.mix[i]=texts[i].charCodeAt(0);
        }
      }
      return item;
    },
    collectV62Payload(){
      const results=this.getV62KeyRefs(this.deviceType).map((ref,index)=>{
        const value=ref&&ref.getResult();
        if(!value)throw Error(this.$t('workspace.invalidKey',{n:index+1}));
        return value;
      });
      const knobs=this.deviceType==='C4'?[this.collectV62Knob('CW'),this.collectV62Knob('CCW'),this.collectV62Knob('Btn')]:undefined;
      return v62Bridge.buildPayload(this.deviceType,results,this.v62Lighting(),knobs);
    },
    async readV62Line(timeout){
      let timer;
      try{
        const result=await Promise.race([
          this.reader.read(),
          new Promise((resolve,reject)=>{timer=setTimeout(()=>reject(Error(this.$t('index.errReplyTimeoutUnknown'))),timeout||8000);})
        ]);
        if(result.done)throw Error(this.$t('index.errDisconnected'));
        const line=(result.value||'').trim();
        if(!line)throw Error(this.$t('index.errEmptyReply'));
        return line;
      }catch(error){
        // 超时后底层 reader.read() 仍会挂起，必须断开并取消读取，
        // 否则下一次 ACK/回读会被旧读取吞掉。
        try{ await this.disconnectSerial(); }catch(e){}
        throw error;
      }finally{clearTimeout(timer);}
    },
    async saveV62(){
      if(this.savingV62)return;
      this.savingV62=true;
      try{
        const payload=this.collectV62Payload();
        await this.writer.write(codec.frame(this.deviceType,payload));
        codec.parseAck(await this.readV62Line(8000));
        await this.sendQuery();
        const snapshot=codec.parseQuery(await this.readV62Line(8000));
        if(snapshot.type!==this.deviceType||snapshot.status!==0||codec.hex(snapshot.payload)!==codec.hex(payload))throw Error(this.$t('index.errReadbackMismatch'));
        await this.applyV62Snapshot(snapshot);
        this.$message({message:this.$t('index.success'),type:'success',duration:5000,showClose:true,customClass:'success-message'});
      }catch(error){
        this.$message({message:error.message||String(error),type:'error',duration:7000,showClose:true});
        if(/超时|断开|timeout|timed out|disconnect|Network|InvalidState/i.test(error.message||String(error))){
          try{await this.disconnectSerial();}catch(e){}
          this.sendLabel=true;this.deviceType='';this.v62Protocol=false;
          this.sendText=this.$t('index.connFirst');this.conButton=this.$t('index.connButtonText1');this.conButtonType='primary';
        }
      }finally{this.savingV62=false;}
    },
    async conClick(){     
      if(this.savingV62 || this.deviceConnecting)return;
      if(this.sendLabel==false){
        const wasV62=this.v62Protocol;
        this.sendLabel=true;
        this.sendText=this.$t('index.connFirst');
        this.conButton=this.$t('index.connButtonText1');
        this.conButtonType="primary";
        if(this.deviceType==='B3'){
          this.$refs.b3key1config.keyReset();
          this.$refs.b3key2config.keyReset();
          this.$refs.b3key3config.keyReset();
        }else if(this.deviceType==='C4'){
          this.$refs.c4key1config.keyReset();
          this.$refs.c4key2config.keyReset();
          this.$refs.c4key3config.keyReset();
          this.$refs.c4key4config.keyReset();
        }else if(this.deviceType==='B6'){
          this.$refs.b6key1config.keyReset();
          this.$refs.b6key2config.keyReset();
          this.$refs.b6key3config.keyReset();
          this.$refs.b6key4config.keyReset();
          this.$refs.b6key5config.keyReset();
          this.$refs.b6key6config.keyReset();
        }else if(this.deviceType==='D2'){
          this.$refs.d2key1config.keyReset();
          this.$refs.d2key2config.keyReset();
        }else if(this.deviceType==='B4'){
          this.$refs.b4key1config.keyReset();
          this.$refs.b4key2config.keyReset();
          this.$refs.b4key3config.keyReset();
          this.$refs.b4key4config.keyReset();
        }else if(this.deviceType==='E6' || this.deviceType==='D6' || this.deviceType==='D9'){
          this.$refs.e6key1config.keyReset();
          this.$refs.e6key2config.keyReset();
          this.$refs.e6key3config.keyReset();
          this.$refs.e6key4config.keyReset();
          this.$refs.e6key5config.keyReset();
          this.$refs.e6key6config.keyReset();
          if(this.deviceType==='D9'){
            if(this.$refs.e6key7config) this.$refs.e6key7config.keyReset();
            if(this.$refs.e6key8config) this.$refs.e6key8config.keyReset();
            if(this.$refs.e6key9config) this.$refs.e6key9config.keyReset();
          }
        }else{
          this.$refs.key1config.keyReset();
        }
        this.deviceType='';
        this.v62Protocol=false;
        if(wasV62)this.disconnectSerial();
        else this.sendDisconnect().then(()=>{this.disconnectSerial();});
      }
      else if (navigator.serial) {
        this.deviceConnecting = true;
        this.serialPortSelected = false;
        try {
          await this.connectSerial();
        } finally {
          this.deviceConnecting = false;
          this.serialPortSelected = false;
        }
      } else {
        alert(this.$t('index.browserErr'));
      }
    },
    async sendCommit(){
      const data = new Uint8Array([0x02, 0xff, 0xff, 0xff, 0xff, 0xff, 0xff, 0xff, 0xff, 0xff,
                    0xff, 0xff, 0xff, 0xff, 0xff, 0xff, 0xff, 0xff, 0xff, 0xff, 0xff, 0xff, 0xff, 0xff, 0xff, 0xff, 0xff, 0xff, 0xff, 0xff, 0xff, 0xff, 0xff, 0xff, 0xff, 0xff, 0xff, 0xff, 0xff, 0xff,
                    0xff, 0xff, 0x00, 0x03 ]);
     let r1=this.$refs.key1config.getResult();
     if(!r1){
        return;
     }
     
     // 地址0: key
     data[1]=r1.keyCode;
     // 地址1: keyfunc
     data[2]=r1.funcCode;
     // 地址2: keyf
     data[3]=r1.mediaExtraCode;
     // 地址3-32: custStr (30字节)
     for(var i=0;i<30;i++){
      data[4+i]=r1.stringCode[i];
     }
     // 地址33-37: keymix (5字节)
     for(var i=0;i<5;i++){
      data[34+i]=r1.mixCode[i];
     }
     // 地址38: triggerMode
     data[39]=this.triggerMode;
     // 地址39: timerHour
     data[40]=this.timerHour;
     // 地址40: timerMinute
     data[41]=this.timerMinute;
     // 地址41: timerSecond
     data[42]=this.timerSecond;
     
      //console.log(data);
		  await this.writer.write(data);
      this.commitSent = true;
    },
    async sendQuery(){
      const data = new Uint8Array([0x02,0x03]);
		  await this.writer.write(data);
    },
    async sendDisconnect(){
      const data = new Uint8Array([0x03,0x01]);
      await this.writer.write(data);
    },
    async sendCommitB3(){
      var b3Version=parseInt(this.deviceVersion,10);
      let r1=this.$refs.b3key1config.getResult();
      if(!r1) return;
      let r2=this.$refs.b3key2config.getResult();
      if(!r2) return;
      let r3=this.$refs.b3key3config.getResult();
      if(!r3) return;
      if(!isNaN(b3Version) && b3Version>=1){
        const data = new Uint8Array(151);
        data.fill(0xff);
        data[0]=0x02;
        data[1]=r1.keyCode;
        data[2]=r2.keyCode;
        data[3]=r3.keyCode;
        data[4]=r1.funcCode;
        data[5]=r2.funcCode;
        data[6]=r3.funcCode;
        data[7]=r1.mediaExtraCode;
        data[8]=r2.mediaExtraCode;
        data[9]=r3.mediaExtraCode;
        for(var i=0;i<30;i++) data[10+i]=r1.stringCode[i];
        for(var i=0;i<30;i++) data[40+i]=r2.stringCode[i];
        for(var i=0;i<30;i++) data[70+i]=r3.stringCode[i];
        for(var i=0;i<5;i++) data[100+i]=r1.mixCode[i];
        for(var i=0;i<5;i++) data[105+i]=r2.mixCode[i];
        for(var i=0;i<5;i++) data[110+i]=r3.mixCode[i];
        data[115]=this.backlightMode;
        data[116]=this.backlightColor1;
        data[117]=this.backlightColor2;
        data[118]=this.backlightColor3;
        data[119]=this.backlightBrightness;
        if(b3Version>=6){
          var b3Crc=this.crc16Ccitt(data,1,120);
          data[120]=b3Crc&0xff;
          data[121]=(b3Crc>>8)&0xff;
        }
        data[150]=0x03;
        await this.writer.write(data);
        this.commitSent = true;
      }else{
        const data = new Uint8Array(155);
        data.fill(0xff);
        data[0]=0x02;
        data[1]=r1.keyCode;
        data[2]=r2.keyCode;
        data[3]=r3.keyCode;
        data[4]=r1.funcCode;
        data[5]=r2.funcCode;
        data[6]=r3.funcCode;
        data[7]=r1.mediaExtraCode;
        data[8]=r2.mediaExtraCode;
        data[9]=r3.mediaExtraCode;
        for(var i=0;i<30;i++) data[10+i]=r1.stringCode[i];
        for(var i=0;i<30;i++) data[40+i]=r2.stringCode[i];
        for(var i=0;i<30;i++) data[70+i]=r3.stringCode[i];
        for(var i=0;i<5;i++) data[100+i]=r1.mixCode[i];
        for(var i=0;i<5;i++) data[105+i]=r2.mixCode[i];
        for(var i=0;i<5;i++) data[110+i]=r3.mixCode[i];
        data[115]=this.b3keyall;
        data[116]=this.b3keyallfunc;
        data[117]=this.b3keyallf;
        for(var i=0;i<5;i++) data[148+i]=this.b3keyallmix[i];
        data[153]=this.keyallmode;
        data[154]=0x03;
        await this.writer.write(data);
        this.commitSent = true;
      }
      this.updateB3KeyDescs();
    },
    async sendClick(){
      if(this.savingV62) return;
      if(this.v62Protocol){
        await this.saveV62();
        return;
      }
      this.savingV62 = true;
      this.commitSent = false;
      try{
        if(this.deviceType==='B3'){
          await this.sendCommitB3();
        }else if(this.deviceType==='C4'){
          await this.sendCommitC4();
        }else if(this.deviceType==='B6'){
          await this.sendCommitB6();
        }else if(this.deviceType==='D2'){
          await this.sendCommitD2();
        }else if(this.deviceType==='B4'){
          await this.sendCommitB4();
        }else if(this.deviceType==='E6' || this.deviceType==='D6' || this.deviceType==='D9'){
          await this.sendCommitD6();
        }else{
          await this.sendCommit();
        }
        // 按键校验未通过时不会真正写出配置，不能继续等待 ACK。
        if(!this.commitSent) return;
        // ★ 提交后主动读取 ACK("1")并弹"设置成功"——
        //   不再依赖 connectSerial 常驻读取循环（该循环已在配置解析完成后退出）
        await this.waitAckAndNotify();
      }catch(error){
        this.$message({
          message:error.message||String(error),
          type:"error",
          duration:7000,
          showClose:true
        });
      }finally{
        this.savingV62 = false;
      }
    },
    async waitAckAndNotify(){
      let saved=false;
      let value='';
      let timer=null;
      try{
        const result = await Promise.race([
          this.reader.read(),
          new Promise((resolve,reject)=>{
            timer=setTimeout(()=>reject(new Error(this.$t('index.errConfirmTimeout'))), 5000);
          })
        ]);
        if(result.done) throw new Error(this.$t('index.errDisconnected'));
        value = result.value || '';
      }catch(error){
        this.$message({
          message:error.message||String(error),
          type:"error",
          duration:7000,
          showClose:true
        });
        try{ await this.disconnectSerial(); }catch(e){}
        return;
      }finally{
        if(timer) clearTimeout(timer);
      }
      if(value.trim()==="1"){
        saved=true;
        this.$message({
          message:this.$t('index.success'),
          type:"success",
          duration:5000,
          showClose:true,
          customClass:'success-message'
        });
      }else if(/^T:\d+$/.test(value.trim())){
        const received=parseInt(value.trim().slice(2),10);
        this.$message({message:'设备只收到 '+received+' / 262 字节配置帧',type:"error",duration:7000,showClose:true});
      }else if(['C','V','E','M','T'].includes(value.trim())){
        const reasons={C:'设备收到帧但 CRC 校验失败',V:'设备拒绝了参数配置',E:'Flash 配置写入或回读校验失败',M:'设备模式写入或回读校验失败',T:'设备收到的配置帧不完整'};
        this.$message({message:reasons[value.trim()],type:"error",duration:7000,showClose:true});
      }else if(value.trim()==="0"){
        this.$message({
          message:this.$t('index.saveFailed'),
          type:"error",
          duration:5000,
          showClose:true
        });
      }else{
        this.$message({
          message:this.$t('index.errNoAck'),
          type:"error",
          duration:7000,
          showClose:true
        });
      }
      if(saved && (this.deviceType==='E6'||this.deviceType==='D6'||this.deviceType==='D9') && this.e6DeviceModeAtLoad!==null && this.e6DeviceMode!==this.e6DeviceModeAtLoad){
        setTimeout(()=>window.location.reload(), 1200);
      }
    },
    async sendCommitD2(){
      let r1=this.$refs.d2key1config.getResult();
      if(!r1) return;
      let r2=this.$refs.d2key2config.getResult();
      if(!r2) return;
      const data = new Uint8Array(151);
      data.fill(0xff);
      data[0]=0x02;
      data[1]=0xD2;
      data[2]=parseInt(this.deviceVersion,10)||0;
      data[3]=r1.keyCode; data[4]=r2.keyCode;
      data[5]=r1.funcCode; data[6]=r2.funcCode;
      data[7]=r1.mediaExtraCode; data[8]=r2.mediaExtraCode;
      for(var i=0;i<30;i++) data[9+i]=r1.stringCode[i];
      for(var i=0;i<30;i++) data[39+i]=r2.stringCode[i];
      for(var i=0;i<5;i++) data[69+i]=r1.mixCode[i];
      for(var i=0;i<5;i++) data[74+i]=r2.mixCode[i];
      data[79]=this.d2RunMode;
      data[80]=this.d2PageAction;
      data[150]=0x03;
      await this.writer.write(data);
      this.commitSent = true;
    },
    async sendCommitB6(){
      var b6Version=parseInt(this.deviceVersion,10);
      if(isNaN(b6Version) || b6Version<0){
        return;
      }
      let r1=this.$refs.b6key1config.getResult();
      if(!r1) return;
      let r2=this.$refs.b6key2config.getResult();
      if(!r2) return;
      let r3=this.$refs.b6key3config.getResult();
      if(!r3) return;
      let r4=this.$refs.b6key4config.getResult();
      if(!r4) return;
      let r5=this.$refs.b6key5config.getResult();
      if(!r5) return;
      let r6=this.$refs.b6key6config.getResult();
      if(!r6) return;
      const data = new Uint8Array(b6Version>=6 ? 120 : 118);
      data.fill(0xff);
      data[0]=0x02;
      data[1]=r1.keyCode; data[2]=r2.keyCode; data[3]=r3.keyCode;
      data[4]=r4.keyCode; data[5]=r5.keyCode; data[6]=r6.keyCode;
      data[7]=r1.funcCode; data[8]=r2.funcCode; data[9]=r3.funcCode;
      data[10]=r4.funcCode; data[11]=r5.funcCode; data[12]=r6.funcCode;
      data[13]=r1.mediaExtraCode; data[14]=r2.mediaExtraCode; data[15]=r3.mediaExtraCode;
      data[16]=r4.mediaExtraCode; data[17]=r5.mediaExtraCode; data[18]=r6.mediaExtraCode;
      for(var i=0;i<10;i++) data[19+i]=r1.stringCode[i];
      for(var i=0;i<10;i++) data[29+i]=r2.stringCode[i];
      for(var i=0;i<10;i++) data[39+i]=r3.stringCode[i];
      for(var i=0;i<10;i++) data[49+i]=r4.stringCode[i];
      for(var i=0;i<10;i++) data[59+i]=r5.stringCode[i];
      for(var i=0;i<10;i++) data[69+i]=r6.stringCode[i];
      for(var i=0;i<5;i++) data[79+i]=r1.mixCode[i];
      for(var i=0;i<5;i++) data[84+i]=r2.mixCode[i];
      for(var i=0;i<5;i++) data[89+i]=r3.mixCode[i];
      for(var i=0;i<5;i++) data[94+i]=r4.mixCode[i];
      for(var i=0;i<5;i++) data[99+i]=r5.mixCode[i];
      for(var i=0;i<5;i++) data[104+i]=r6.mixCode[i];
      data[109]=this.backlightModeB6;
      data[110]=this.backlightColorB6_1;
      data[111]=this.backlightColorB6_2;
      data[112]=this.backlightColorB6_3;
      data[113]=this.backlightColorB6_4;
      data[114]=this.backlightColorB6_5;
      data[115]=this.backlightColorB6_6;
      data[116]=this.backlightBrightnessB6;
      if(b6Version>=6){
        var b6Crc=this.crc16Ccitt(data,1,117);
        data[117]=b6Crc&0xff;
        data[118]=(b6Crc>>8)&0xff;
        data[119]=0x03;
      }else{
        data[117]=0x03;
      }
      await this.writer.write(data);
      this.commitSent = true;
      this.updateB6KeyDescs();
    },
    async sendCommitE6(){
      let r1=this.$refs.e6key1config.getResult();
      if(!r1) return;
      let r2=this.$refs.e6key2config.getResult();
      if(!r2) return;
      let r3=this.$refs.e6key3config.getResult();
      if(!r3) return;
      let r4=this.$refs.e6key4config.getResult();
      if(!r4) return;
      let r5=this.$refs.e6key5config.getResult();
      if(!r5) return;
      let r6=this.$refs.e6key6config.getResult();
      if(!r6) return;
      const data = new Uint8Array(151);
      data.fill(0xff);
      data[0]=0x02;
      data[1]=0xE6;
      data[2]=parseInt(this.deviceVersion,10)||0;
      data[3]=r1.keyCode; data[4]=r2.keyCode;
      data[5]=r1.funcCode; data[6]=r2.funcCode;
      data[7]=r1.mediaExtraCode; data[8]=r2.mediaExtraCode;
      for(var i=0;i<30;i++) data[9+i]=r1.stringCode[i];
      for(var i=0;i<30;i++) data[39+i]=r2.stringCode[i];
      for(var i=0;i<5;i++) data[69+i]=r1.mixCode[i];
      for(var i=0;i<5;i++) data[74+i]=r2.mixCode[i];
      data[79]=this.e6RunMode;
      data[80]=this.e6PageAction;
      data[81]=r3.keyCode; data[82]=r4.keyCode; data[83]=r5.keyCode; data[84]=r6.keyCode;
      data[85]=r3.funcCode; data[86]=r4.funcCode; data[87]=r5.funcCode; data[88]=r6.funcCode;
      data[89]=r3.mediaExtraCode; data[90]=r4.mediaExtraCode; data[91]=r5.mediaExtraCode; data[92]=r6.mediaExtraCode;
      for(var i=0;i<5;i++) data[93+i]=r3.mixCode[i];
      for(var i=0;i<5;i++) data[98+i]=r4.mixCode[i];
      for(var i=0;i<5;i++) data[103+i]=r5.mixCode[i];
      for(var i=0;i<5;i++) data[108+i]=r6.mixCode[i];
      data[113]=this.e6DeviceMode;
      data[150]=0x03;
      await this.writer.write(data);
      this.commitSent = true;
      this.updateE6KeyDescs();
    },
    async sendCommitD6(){
      let r1=this.$refs.e6key1config.getResult();
      if(!r1) return;
      let r2=this.$refs.e6key2config.getResult();
      if(!r2) return;
      let r3=this.$refs.e6key3config.getResult();
      if(!r3) return;
      let r4=this.$refs.e6key4config.getResult();
      if(!r4) return;
      let r5=this.$refs.e6key5config.getResult();
      if(!r5) return;
      let r6=this.$refs.e6key6config.getResult();
      if(!r6) return;
      const isD9=this.deviceType==='D9';
      let r7=null, r8=null, r9=null;
      if(isD9){
        r7=this.$refs.e6key7config.getResult();
        if(!r7) return;
        r8=this.$refs.e6key8config.getResult();
        if(!r8) return;
        r9=this.$refs.e6key9config.getResult();
        if(!r9) return;
      }
      // D6: 178 字节; D9: 262 字节(完整 3 键扩展); E6 仍为 30 字节字符串
      const isD6=this.deviceType==='D6' || isD9;
      const textLen=isD6?20:30;
      const cfgLen=54+6*textLen+(isD9?84:0);
      const data = new Uint8Array(cfgLen+4);
      data.fill(0xff);
      data[0]=0x02;
      data[1]=isD9?0xD9:0xD6;                     // [1] 设备类型
      data[2]=parseInt(this.deviceVersion,10)||0; // [2] 版本
      data[3]=this.e6TimeSeq & 0xff;              // [3] 时间序号
      data[4]=this.e6DeviceMode;                  // [4] 三模选择
      data[5]=r1.keyCode; data[6]=r2.keyCode;     // [5][6]
      data[7]=r1.funcCode; data[8]=r2.funcCode;
      data[9]=r1.mediaExtraCode; data[10]=r2.mediaExtraCode;
      const str1Off=11;
      const str2Off=str1Off+textLen;
      const mix1Off=str2Off+textLen;
      const mix2Off=mix1Off+5;
      const runOff=mix2Off+5;
      const pageOff=runOff+1;
      const key3Off=pageOff+1;
      const funcOff=key3Off+4;
      const extraOff=funcOff+4;
      const mix3Off=extraOff+4;
      const str3Off=mix3Off+20;
      const d9Off=str3Off+4*textLen;
      for(var i=0;i<textLen;i++){ data[str1Off+i]=r1.stringCode[i]; data[str2Off+i]=r2.stringCode[i]; }
      for(var i=0;i<5;i++){ data[mix1Off+i]=r1.mixCode[i]; data[mix2Off+i]=r2.mixCode[i]; }
      data[runOff]=this.e6RunMode;
      data[pageOff]=this.e6PageAction;
      data[key3Off]=r3.keyCode; data[key3Off+1]=r4.keyCode; data[key3Off+2]=r5.keyCode; data[key3Off+3]=r6.keyCode;
      data[funcOff]=r3.funcCode; data[funcOff+1]=r4.funcCode; data[funcOff+2]=r5.funcCode; data[funcOff+3]=r6.funcCode;
      data[extraOff]=r3.mediaExtraCode; data[extraOff+1]=r4.mediaExtraCode; data[extraOff+2]=r5.mediaExtraCode; data[extraOff+3]=r6.mediaExtraCode;
      for(var i=0;i<5;i++){ data[mix3Off+i]=r3.mixCode[i]; data[mix3Off+5+i]=r4.mixCode[i]; data[mix3Off+10+i]=r5.mixCode[i]; data[mix3Off+15+i]=r6.mixCode[i]; }
      for(var i=0;i<textLen;i++){ data[str3Off+i]=r3.stringCode[i]; data[str3Off+textLen+i]=r4.stringCode[i]; data[str3Off+2*textLen+i]=r5.stringCode[i]; data[str3Off+3*textLen+i]=r6.stringCode[i]; }
      if(isD9){
        data[d9Off]=r7.keyCode; data[d9Off+1]=r8.keyCode; data[d9Off+2]=r9.keyCode;
        data[d9Off+3]=r7.funcCode; data[d9Off+4]=r8.funcCode; data[d9Off+5]=r9.funcCode;
        data[d9Off+6]=r7.mediaExtraCode; data[d9Off+7]=r8.mediaExtraCode; data[d9Off+8]=r9.mediaExtraCode;
        for(var i=0;i<5;i++){
          data[d9Off+9+i]=r7.mixCode[i];
          data[d9Off+14+i]=r8.mixCode[i];
          data[d9Off+19+i]=r9.mixCode[i];
        }
        for(var i=0;i<textLen;i++){
          data[d9Off+24+i]=r7.stringCode[i];
          data[d9Off+24+textLen+i]=r8.stringCode[i];
          data[d9Off+24+2*textLen+i]=r9.stringCode[i];
        }
      }
      var d6Crc=this.crc16Ccitt(data,1,cfgLen+1); // CRC 覆盖偏移 1~cfgLen
      data[cfgLen+1]=d6Crc&0xff;
      data[cfgLen+2]=(d6Crc>>8)&0xff;
      data[cfgLen+3]=0x03;
      if(isD9){
        // 分块写入，避免 USB/串口实现对单次大于 255 字节写入的截断。
        for(var offset=0;offset<data.length;offset+=64){
          await this.writer.write(data.subarray(offset,Math.min(offset+64,data.length)));
        }
      }else{
        await this.writer.write(data);
      }
      this.commitSent = true;
      this.updateE6KeyDescs();
    },
    async sendCommitB4(){
      var b4Version=parseInt(this.deviceVersion,10)||0;
      let r1=this.$refs.b4key1config.getResult();
      if(!r1) return;
      let r2=this.$refs.b4key2config.getResult();
      if(!r2) return;
      let r3=this.$refs.b4key3config.getResult();
      if(!r3) return;
      let r4=this.$refs.b4key4config.getResult();
      if(!r4) return;
      const data = new Uint8Array(b4Version>=6 ? 82 : 80);
      data.fill(0xff);
      data[0]=0x02;
      data[1]=r1.keyCode; data[2]=r2.keyCode; data[3]=r3.keyCode; data[4]=r4.keyCode;
      data[5]=r1.funcCode; data[6]=r2.funcCode; data[7]=r3.funcCode; data[8]=r4.funcCode;
      data[9]=r1.mediaExtraCode; data[10]=r2.mediaExtraCode; data[11]=r3.mediaExtraCode; data[12]=r4.mediaExtraCode;
      for(var i=0;i<10;i++) data[13+i]=r1.stringCode[i];
      for(var i=0;i<10;i++) data[23+i]=r2.stringCode[i];
      for(var i=0;i<10;i++) data[33+i]=r3.stringCode[i];
      for(var i=0;i<10;i++) data[43+i]=r4.stringCode[i];
      for(var i=0;i<5;i++) data[53+i]=r1.mixCode[i];
      for(var i=0;i<5;i++) data[58+i]=r2.mixCode[i];
      for(var i=0;i<5;i++) data[63+i]=r3.mixCode[i];
      for(var i=0;i<5;i++) data[68+i]=r4.mixCode[i];
      data[73]=this.backlightModeB4;
      data[74]=this.backlightColorB4_1;
      data[75]=this.backlightColorB4_2;
      data[76]=this.backlightColorB4_3;
      data[77]=this.backlightColorB4_4;
      data[78]=this.backlightBrightnessB4;
      if(b4Version>=6){
        var b4Crc=this.crc16Ccitt(data,1,79);
        data[79]=b4Crc&0xff;
        data[80]=(b4Crc>>8)&0xff;
        data[81]=0x03;
      }else{
        data[79]=0x03;
      }
      await this.writer.write(data);
      this.commitSent = true;
      this.updateB4KeyDescs();
    },
    async sendCommitC4(){
      let r1=this.$refs.c4key1config.getResult();
      if(!r1) return;
      let r2=this.$refs.c4key2config.getResult();
      if(!r2) return;
      let r3=this.$refs.c4key3config.getResult();
      if(!r3) return;
      let r4=this.$refs.c4key4config.getResult();
      if(!r4) return;

      var c4Version = parseInt(this.deviceVersion,10)||0;
      var frameSize = c4Version>=3 ? 104 : (c4Version===2 ? 94 : 89);
      const data = new Uint8Array(frameSize);
      data.fill(0xff);
      data[0]=0x02;
      data[1]=r1.keyCode; data[2]=r2.keyCode; data[3]=r3.keyCode; data[4]=r4.keyCode;
      data[5]=r1.funcCode; data[6]=r2.funcCode; data[7]=r3.funcCode; data[8]=r4.funcCode;
      data[9]=r1.mediaExtraCode; data[10]=r2.mediaExtraCode; data[11]=r3.mediaExtraCode; data[12]=r4.mediaExtraCode;
      for(var i=0;i<10;i++) data[13+i]=r1.stringCode[i];
      for(var i=0;i<10;i++) data[23+i]=r2.stringCode[i];
      for(var i=0;i<10;i++) data[33+i]=r3.stringCode[i];
      for(var i=0;i<10;i++) data[43+i]=r4.stringCode[i];
      for(var i=0;i<5;i++) data[53+i]=r1.mixCode[i];
      for(var i=0;i<5;i++) data[58+i]=r2.mixCode[i];
      for(var i=0;i<5;i++) data[63+i]=r3.mixCode[i];
      for(var i=0;i<5;i++) data[68+i]=r4.mixCode[i];
      data[73]=this.backlightModeC4;
      data[74]=this.backlightColorC4_1;
      data[75]=this.backlightColorC4_2;
      data[76]=this.backlightColorC4_3;
      data[77]=this.backlightColorC4_4;
      data[78]=this.backlightBrightnessC4;
      data[79]=this.getEc11Key('CW'); data[80]=this.ec11CW_func; data[81]=this.getEc11KeyF('CW');
      data[82]=this.getEc11Key('CCW'); data[83]=this.ec11CCW_func; data[84]=this.getEc11KeyF('CCW');
      data[85]=this.getEc11Key('Btn'); data[86]=this.ec11Btn_func; data[87]=this.getEc11KeyF('Btn');

      var writeMix = (func, mCount, mShowArr, mSelVal, mText, baseAddr, dirLabel) => {
        if(func!==5) return true;
        if(mCount<2){
          this.$message.error(dirLabel+this.$t('keyConfig.mixErr'));
          return false;
        }
        for(var m=0; m<mCount; m++){
          if(mShowArr[m]){
            if(mSelVal[m]===''){
              this.$message.error(dirLabel+this.$t('keyConfig.mixErr1')+(m+1)+this.$t('keyConfig.mixErr2'));
              return false;
            }
            data[baseAddr+m]=mSelVal[m];
          }else{
            if(mText[m].length!=1){
              this.$message.error(dirLabel+this.$t('keyConfig.mixErr1')+(m+1)+this.$t('keyConfig.mixErr3'));
              return false;
            }
            var mt=mText[m].charCodeAt(0);
            if(mt<0||mt>255){
              this.$message.error(dirLabel+this.$t('keyConfig.mixErr1')+(m+1)+this.$t('keyConfig.mixErr3'));
              return false;
            }
            data[baseAddr+m]=mt;
          }
        }
        return true;
      };

      if(c4Version>=3){
        if(!writeMix(this.ec11Btn_func, this.ec11Btn_mixCount, this.ec11Btn_mixShowArray, this.ec11Btn_mixFuncSelectValue, this.ec11Btn_mixText, 88, this.$t('index.ec11Btn'))) return;
        if(this.ec11Btn_func===5){
          for(var bt=0;bt<5;bt++) this.ec11Btn_keyMix[bt]=data[88+bt];
        }
        if(!writeMix(this.ec11CW_func, this.ec11CW_mixCount, this.ec11CW_mixShowArray, this.ec11CW_mixFuncSelectValue, this.ec11CW_mixText, 93, this.$t('index.ec11CW'))) return;
        if(this.ec11CW_func===5){
          for(var cw=0;cw<5;cw++) this.ec11CW_keyMix[cw]=data[93+cw];
        }
        if(!writeMix(this.ec11CCW_func, this.ec11CCW_mixCount, this.ec11CCW_mixShowArray, this.ec11CCW_mixFuncSelectValue, this.ec11CCW_mixText, 98, this.$t('index.ec11CCW'))) return;
        if(this.ec11CCW_func===5){
          for(var ccw=0;ccw<5;ccw++) this.ec11CCW_keyMix[ccw]=data[98+ccw];
        }
        data[103]=0x03;
      }else if(c4Version===2){
        if(!writeMix(this.ec11Btn_func, this.ec11Btn_mixCount, this.ec11Btn_mixShowArray, this.ec11Btn_mixFuncSelectValue, this.ec11Btn_mixText, 88, this.$t('index.ec11Btn'))) return;
        if(this.ec11Btn_func===5){
          for(var bt2=0;bt2<5;bt2++) this.ec11Btn_keyMix[bt2]=data[88+bt2];
        }
        data[93]=0x03;
      }else{
        data[88]=0x03;
      }
      await this.writer.write(data);
      this.commitSent = true;
      this.updateC4KeyDescs();
    },
    getEc11Key(dir){
      if(dir==='CW'){
        if(this.ec11CW_func===0) return this.ec11CW_keyChar.length>0 ? this.ec11CW_keyChar.charCodeAt(0) : 0xff;
        if(this.ec11CW_func===1) return this.ec11CW_mediaVal & 0xFF;
        if(this.ec11CW_func===2) return this.ec11CW_fnVal;
        if(this.ec11CW_func===3) return this.ec11CW_scrollDir;
        if(this.ec11CW_func===4) return this.ec11CW_mouseBtn;
        if(this.ec11CW_func===5) return 0xff;
      }else if(dir==='CCW'){
        if(this.ec11CCW_func===0) return this.ec11CCW_keyChar.length>0 ? this.ec11CCW_keyChar.charCodeAt(0) : 0xff;
        if(this.ec11CCW_func===1) return this.ec11CCW_mediaVal & 0xFF;
        if(this.ec11CCW_func===2) return this.ec11CCW_fnVal;
        if(this.ec11CCW_func===3) return this.ec11CCW_scrollDir;
        if(this.ec11CCW_func===4) return this.ec11CCW_mouseBtn;
        if(this.ec11CCW_func===5) return 0xff;
      }else if(dir==='Btn'){
        if(this.ec11Btn_func===0) return this.ec11Btn_keyChar.length>0 ? this.ec11Btn_keyChar.charCodeAt(0) : 0xff;
        if(this.ec11Btn_func===1) return this.ec11Btn_mediaVal & 0xFF;
        if(this.ec11Btn_func===2) return this.ec11Btn_fnVal;
        if(this.ec11Btn_func===3) return this.ec11Btn_scrollDir;
        if(this.ec11Btn_func===4) return this.ec11Btn_mouseBtn;
        if(this.ec11Btn_func===5) return 0xff;
      }
      return 0xff;
    },
    getEc11KeyF(dir){
      if(dir==='CW'){
        if(this.ec11CW_func===0) return 0xff;
        if(this.ec11CW_func===1) return (this.ec11CW_mediaVal >> 8) & 0xFF;
        if(this.ec11CW_func===2) return 0xff;
        if(this.ec11CW_func===3) return 0xff;
        if(this.ec11CW_func===4) return this.ec11CW_mouseMode;
        if(this.ec11CW_func===5) return 0xff;
      }else if(dir==='CCW'){
        if(this.ec11CCW_func===0) return 0xff;
        if(this.ec11CCW_func===1) return (this.ec11CCW_mediaVal >> 8) & 0xFF;
        if(this.ec11CCW_func===2) return 0xff;
        if(this.ec11CCW_func===3) return 0xff;
        if(this.ec11CCW_func===4) return this.ec11CCW_mouseMode;
        if(this.ec11CCW_func===5) return 0xff;
      }else if(dir==='Btn'){
        if(this.ec11Btn_func===0) return 0xff;
        if(this.ec11Btn_func===1) return (this.ec11Btn_mediaVal >> 8) & 0xFF;
        if(this.ec11Btn_func===2) return 0xff;
        if(this.ec11Btn_func===3) return 0xff;
        if(this.ec11Btn_func===4) return this.ec11Btn_mouseMode;
        if(this.ec11Btn_func===5) return 0xff;
      }
      return 0xff;
    },
    initEc11UI(){
      this.setEc11UI('CW');
      this.setEc11UI('CCW');
      this.setEc11UI('Btn');
    },
    setEc11UI(dir){
      var func, key, keyF;
      if(dir==='CW'){ func=this.ec11CW_func; key=this.ec11CW_key; keyF=this.ec11CW_keyF; }
      else if(dir==='CCW'){ func=this.ec11CCW_func; key=this.ec11CCW_key; keyF=this.ec11CCW_keyF; }
      else if(dir==='Btn'){ func=this.ec11Btn_func; key=this.ec11Btn_key; keyF=this.ec11Btn_keyF; }
      if(func===0){
        var ch = (key>=0x20 && key<=0x7e) ? String.fromCharCode(key) : '';
        if(dir==='CW') this.ec11CW_keyChar = ch;
        else if(dir==='CCW') this.ec11CCW_keyChar = ch;
        else if(dir==='Btn') this.ec11Btn_keyChar = ch;
      }else if(func===1){
        var mediaVal = (keyF << 8) | key;
        if(dir==='CW') this.ec11CW_mediaVal = mediaVal;
        else if(dir==='CCW') this.ec11CCW_mediaVal = mediaVal;
        else if(dir==='Btn') this.ec11Btn_mediaVal = mediaVal;
      }else if(func===2){
        if(dir==='CW') this.ec11CW_fnVal = key;
        else if(dir==='CCW') this.ec11CCW_fnVal = key;
        else if(dir==='Btn') this.ec11Btn_fnVal = key;
      }else if(func===3){
        if(dir==='CW') this.ec11CW_scrollDir = key;
        else if(dir==='CCW') this.ec11CCW_scrollDir = key;
        else if(dir==='Btn') this.ec11Btn_scrollDir = key;
      }else if(func===4){
        if(dir==='CW'){ this.ec11CW_mouseBtn = key; this.ec11CW_mouseMode = keyF; }
        else if(dir==='CCW'){ this.ec11CCW_mouseBtn = key; this.ec11CCW_mouseMode = keyF; }
        else if(dir==='Btn'){ this.ec11Btn_mouseBtn = key; this.ec11Btn_mouseMode = keyF; }
      }else if(func===5 && dir==='CW'){
        var tmpCWCount = 0;
        for(var miCW=0; miCW<5; miCW++){
          if(this.ec11CW_keyMix[miCW]!==0xff) tmpCWCount=miCW+1;
          else break;
        }
        this.ec11CW_mixCount = tmpCWCount;
        var fnValCW = this.fnKeyOptions.map(f=>f.value);
        for(var mCW=0; mCW<5; mCW++){
          if(mCW < tmpCWCount){
            var mkCW = this.ec11CW_keyMix[mCW];
            var foundCW=false;
            for(var fvCW=0;fvCW<fnValCW.length;fvCW++){
              if(fnValCW[fvCW]===mkCW){
                this.ec11CW_mixShowArray[mCW]=true;
                this.ec11CW_mixFuncSelectValue[mCW]=mkCW;
                this.ec11CW_mixText[mCW]='';
                foundCW=true;
                break;
              }
            }
            if(!foundCW){
              this.ec11CW_mixShowArray[mCW]=false;
              this.ec11CW_mixFuncSelectValue[mCW]='';
              this.ec11CW_mixText[mCW]=(mkCW>=0x20&&mkCW<=0x7e)?String.fromCharCode(mkCW):'';
            }
          }else{
            this.ec11CW_mixShowArray[mCW]=false;
            this.ec11CW_mixFuncSelectValue[mCW]='';
            this.ec11CW_mixText[mCW]='';
          }
        }
        this.ec11CWMixCountChange();
      }else if(func===5 && dir==='CCW'){
        var tmpCCWCount = 0;
        for(var miCCW=0; miCCW<5; miCCW++){
          if(this.ec11CCW_keyMix[miCCW]!==0xff) tmpCCWCount=miCCW+1;
          else break;
        }
        this.ec11CCW_mixCount = tmpCCWCount;
        var fnValCCW = this.fnKeyOptions.map(f=>f.value);
        for(var mCCW=0; mCCW<5; mCCW++){
          if(mCCW < tmpCCWCount){
            var mkCCW = this.ec11CCW_keyMix[mCCW];
            var foundCCW=false;
            for(var fvCCW=0;fvCCW<fnValCCW.length;fvCCW++){
              if(fnValCCW[fvCCW]===mkCCW){
                this.ec11CCW_mixShowArray[mCCW]=true;
                this.ec11CCW_mixFuncSelectValue[mCCW]=mkCCW;
                this.ec11CCW_mixText[mCCW]='';
                foundCCW=true;
                break;
              }
            }
            if(!foundCCW){
              this.ec11CCW_mixShowArray[mCCW]=false;
              this.ec11CCW_mixFuncSelectValue[mCCW]='';
              this.ec11CCW_mixText[mCCW]=(mkCCW>=0x20&&mkCCW<=0x7e)?String.fromCharCode(mkCCW):'';
            }
          }else{
            this.ec11CCW_mixShowArray[mCCW]=false;
            this.ec11CCW_mixFuncSelectValue[mCCW]='';
            this.ec11CCW_mixText[mCCW]='';
          }
        }
        this.ec11CCWMixCountChange();
      }else if(func===5 && dir==='Btn'){
        var tmpMixCount = 0;
        for(var mi=0; mi<5; mi++){
          if(this.ec11Btn_keyMix[mi]!==0xff) tmpMixCount=mi+1;
          else break;
        }
        this.ec11Btn_mixCount = tmpMixCount;
        var fnValues = this.fnKeyOptions.map(f=>f.value);
        for(var m3=0; m3<5; m3++){
          if(m3 < tmpMixCount){
            var mk = this.ec11Btn_keyMix[m3];
            var foundFn=false;
            for(var fv=0;fv<fnValues.length;fv++){
              if(fnValues[fv]===mk){
                this.ec11Btn_mixShowArray[m3]=true;
                this.ec11Btn_mixFuncSelectValue[m3]=mk;
                this.ec11Btn_mixText[m3]='';
                foundFn=true;
                break;
              }
            }
            if(!foundFn){
              this.ec11Btn_mixShowArray[m3]=false;
              this.ec11Btn_mixFuncSelectValue[m3]='';
              this.ec11Btn_mixText[m3]=(mk>=0x20&&mk<=0x7e)?String.fromCharCode(mk):'';
            }
          }else{
            this.ec11Btn_mixShowArray[m3]=false;
            this.ec11Btn_mixFuncSelectValue[m3]='';
            this.ec11Btn_mixText[m3]='';
          }
        }
        this.ec11BtnMixCountChange();
      }
    },
    ec11BtnMixAdd(type){
      if(this.ec11Btn_mixCount>=5){
        this.$message.error(this.$t('keyConfig.mixMaxErr'));
        return;
      }
      if(type===1){
        this.ec11Btn_mixShowArray[this.ec11Btn_mixCount]=true;
      }else if(type===2){
        this.ec11Btn_mixShowArray[this.ec11Btn_mixCount]=false;
      }
      this.ec11Btn_mixCount++;
      this.ec11BtnMixCountChange();
    },
    ec11BtnMixDelete(){
      if(this.ec11Btn_mixCount<=0){
        this.$message.error(this.$t('keyConfig.delErr'));
        return;
      }
      this.ec11Btn_mixCount--;
      this.ec11Btn_mixFuncSelectValue[this.ec11Btn_mixCount]='';
      this.ec11Btn_mixText[this.ec11Btn_mixCount]='';
      this.ec11Btn_mixShowArray[this.ec11Btn_mixCount]=false;
      this.ec11Btn_keyMix[this.ec11Btn_mixCount]=0xff;
      this.ec11BtnMixCountChange();
    },
    ec11BtnMixReset(){
      this.ec11Btn_mixCount=0;
      for(var i=0; i<5; i++){
        this.ec11Btn_mixCountArray[i]=false;
        this.ec11Btn_mixShowArray[i]=false;
        this.ec11Btn_mixFuncSelectValue[i]='';
        this.ec11Btn_mixText[i]='';
        this.ec11Btn_keyMix[i]=0xff;
      }
      this.ec11BtnMixCountChange();
    },
    ec11BtnMixCountChange(){
      switch(this.ec11Btn_mixCount){
        case 0:
          this.ec11Btn_mixCountArray=[false,false,false,false,false];
          break;
        case 1:
          this.ec11Btn_mixCountArray=[true,false,false,false,false];
          break;
        case 2:
          this.ec11Btn_mixCountArray=[true,true,false,false,false];
          break;
        case 3:
          this.ec11Btn_mixCountArray=[true,true,true,false,false];
          break;
        case 4:
          this.ec11Btn_mixCountArray=[true,true,true,true,false];
          break;
        case 5:
          this.ec11Btn_mixCountArray=[true,true,true,true,true];
          break;
        default:
          this.ec11Btn_mixCountArray=[false,false,false,false,false];
          break;
      }
      var mixShowArray=this.ec11Btn_mixShowArray;
      var mixFuncSelectValue=this.ec11Btn_mixFuncSelectValue;
      var mixText=this.ec11Btn_mixText;
      var mixTips='';
      for(var i=0;i<5;i++){
        if(this.ec11Btn_mixCountArray[i]){
          if(mixShowArray[i]){
            if(mixFuncSelectValue[i]==='') mixTips = mixTips + ' '+this.$t('keyConfig.mixErr1')+(i+1)+this.$t('keyConfig.mixErr2')+' ';
          }else{
            if(mixText[i].length!=1) mixTips = mixTips + ' '+this.$t('keyConfig.mixErr1')+(i+1)+this.$t('keyConfig.mixErr3')+' ';
          }
        }
      }
      if(this.ec11Btn_mixCount<2) mixTips=this.$t('keyConfig.mixAdd');
      if(mixTips==='') mixTips=this.$t('keyConfig.mixMax5');
      this.ec11Btn_mixTips=mixTips;
    },
    ec11CWMixAdd(type){
      if(this.ec11CW_mixCount>=5){
        this.$message.error(this.$t('keyConfig.mixMaxErr'));
        return;
      }
      if(type===1){
        this.ec11CW_mixShowArray[this.ec11CW_mixCount]=true;
      }else if(type===2){
        this.ec11CW_mixShowArray[this.ec11CW_mixCount]=false;
      }
      this.ec11CW_mixCount++;
      this.ec11CWMixCountChange();
    },
    ec11CWMixDelete(){
      if(this.ec11CW_mixCount<=0){
        this.$message.error(this.$t('keyConfig.delErr'));
        return;
      }
      this.ec11CW_mixCount--;
      this.ec11CW_mixFuncSelectValue[this.ec11CW_mixCount]='';
      this.ec11CW_mixText[this.ec11CW_mixCount]='';
      this.ec11CW_mixShowArray[this.ec11CW_mixCount]=false;
      this.ec11CW_keyMix[this.ec11CW_mixCount]=0xff;
      this.ec11CWMixCountChange();
    },
    ec11CWMixReset(){
      this.ec11CW_mixCount=0;
      for(var i=0; i<5; i++){
        this.ec11CW_mixCountArray[i]=false;
        this.ec11CW_mixShowArray[i]=false;
        this.ec11CW_mixFuncSelectValue[i]='';
        this.ec11CW_mixText[i]='';
        this.ec11CW_keyMix[i]=0xff;
      }
      this.ec11CWMixCountChange();
    },
    ec11CWMixCountChange(){
      switch(this.ec11CW_mixCount){
        case 0: this.ec11CW_mixCountArray=[false,false,false,false,false]; break;
        case 1: this.ec11CW_mixCountArray=[true,false,false,false,false]; break;
        case 2: this.ec11CW_mixCountArray=[true,true,false,false,false]; break;
        case 3: this.ec11CW_mixCountArray=[true,true,true,false,false]; break;
        case 4: this.ec11CW_mixCountArray=[true,true,true,true,false]; break;
        case 5: this.ec11CW_mixCountArray=[true,true,true,true,true]; break;
        default: this.ec11CW_mixCountArray=[false,false,false,false,false]; break;
      }
      var mixShow=this.ec11CW_mixShowArray, mixVal=this.ec11CW_mixFuncSelectValue, mixTx=this.ec11CW_mixText;
      var tips='';
      for(var i=0;i<5;i++){
        if(this.ec11CW_mixCountArray[i]){
          if(mixShow[i]){ if(mixVal[i]==='') tips = tips + ' '+this.$t('keyConfig.mixErr1')+(i+1)+this.$t('keyConfig.mixErr2')+' '; }
          else{ if(mixTx[i].length!=1) tips = tips + ' '+this.$t('keyConfig.mixErr1')+(i+1)+this.$t('keyConfig.mixErr3')+' '; }
        }
      }
      if(this.ec11CW_mixCount<2) tips=this.$t('keyConfig.mixAdd');
      if(tips==='') tips=this.$t('keyConfig.mixMax5');
      this.ec11CW_mixTips=tips;
    },
    ec11CCWMixAdd(type){
      if(this.ec11CCW_mixCount>=5){
        this.$message.error(this.$t('keyConfig.mixMaxErr'));
        return;
      }
      if(type===1){
        this.ec11CCW_mixShowArray[this.ec11CCW_mixCount]=true;
      }else if(type===2){
        this.ec11CCW_mixShowArray[this.ec11CCW_mixCount]=false;
      }
      this.ec11CCW_mixCount++;
      this.ec11CCWMixCountChange();
    },
    ec11CCWMixDelete(){
      if(this.ec11CCW_mixCount<=0){
        this.$message.error(this.$t('keyConfig.delErr'));
        return;
      }
      this.ec11CCW_mixCount--;
      this.ec11CCW_mixFuncSelectValue[this.ec11CCW_mixCount]='';
      this.ec11CCW_mixText[this.ec11CCW_mixCount]='';
      this.ec11CCW_mixShowArray[this.ec11CCW_mixCount]=false;
      this.ec11CCW_keyMix[this.ec11CCW_mixCount]=0xff;
      this.ec11CCWMixCountChange();
    },
    ec11CCWMixReset(){
      this.ec11CCW_mixCount=0;
      for(var i=0; i<5; i++){
        this.ec11CCW_mixCountArray[i]=false;
        this.ec11CCW_mixShowArray[i]=false;
        this.ec11CCW_mixFuncSelectValue[i]='';
        this.ec11CCW_mixText[i]='';
        this.ec11CCW_keyMix[i]=0xff;
      }
      this.ec11CCWMixCountChange();
    },
    ec11CCWMixCountChange(){
      switch(this.ec11CCW_mixCount){
        case 0: this.ec11CCW_mixCountArray=[false,false,false,false,false]; break;
        case 1: this.ec11CCW_mixCountArray=[true,false,false,false,false]; break;
        case 2: this.ec11CCW_mixCountArray=[true,true,false,false,false]; break;
        case 3: this.ec11CCW_mixCountArray=[true,true,true,false,false]; break;
        case 4: this.ec11CCW_mixCountArray=[true,true,true,true,false]; break;
        case 5: this.ec11CCW_mixCountArray=[true,true,true,true,true]; break;
        default: this.ec11CCW_mixCountArray=[false,false,false,false,false]; break;
      }
      var mixShow=this.ec11CCW_mixShowArray, mixVal=this.ec11CCW_mixFuncSelectValue, mixTx=this.ec11CCW_mixText;
      var tips='';
      for(var i=0;i<5;i++){
        if(this.ec11CCW_mixCountArray[i]){
          if(mixShow[i]){ if(mixVal[i]==='') tips = tips + ' '+this.$t('keyConfig.mixErr1')+(i+1)+this.$t('keyConfig.mixErr2')+' '; }
          else{ if(mixTx[i].length!=1) tips = tips + ' '+this.$t('keyConfig.mixErr1')+(i+1)+this.$t('keyConfig.mixErr3')+' '; }
        }
      }
      if(this.ec11CCW_mixCount<2) tips=this.$t('keyConfig.mixAdd');
      if(tips==='') tips=this.$t('keyConfig.mixMax5');
      this.ec11CCW_mixTips=tips;
    },
    showAbout(){
      this.showMainHelp = false;
      this.showMoreMenu = false;
      const t = this.buildTime || '--';
      this.$alert(this.$t('index.aboutBuildTime') + t, this.$t('index.about'), { type: 'info' });
    },
    async openRf24PairFromHelp(){
      this.showMainHelp = false;
      this.showMoreMenu = false;
      await this.openRf24Pair();
    },
    async openRf24Pair(){
      try{
        if(this.port){ await this.disconnectSerial(); }
      }catch(e){}
      this.showRf24Pair = true;
    },
    async sendFactoryLed(on){
      if(!this.writer) return;
      try{
        await this.writer.write(new Uint8Array([0x02, on ? 0x47 : 0x48]));
      }catch(e){}
    },
    async openFactoryTest(){
      this.factoryData = null;
      this.factoryRaw = '';
      this.factoryError = '';
      this.factoryPressedMask = 0;
      this.factoryAuto = true;
      this.showFactoryTest = true;
      await this.sendFactoryLed(true);
      this.factoryAutoChanged(true);
    },
    async closeFactoryTest(){
      this.stopFactoryTest();
      this.factoryAuto = false;
      await this.sendFactoryLed(false);
    },
    stopFactoryTest(){
      if(this.factoryTimer){
        clearInterval(this.factoryTimer);
        this.factoryTimer = null;
      }
    },
    resetFactoryTest(){
      this.stopFactoryTest();
      this.factoryData = null;
      this.factoryRaw = '';
      this.factoryError = '';
      this.factoryPressedMask = 0;
      this.factoryAuto = true;
      this.showFactoryTest = false;
    },
    factoryAutoChanged(value){
      if(this.factoryTimer){
        clearInterval(this.factoryTimer);
        this.factoryTimer = null;
      }
      if(value){
        this.readFactoryTest();
        this.factoryTimer = setInterval(()=>this.readFactoryTest(), 500);
      }
    },
    parseFactoryLine(line){
      const m=/^FT1:KEY=([0-9A-F]{2,3}),PCT=(\d+),BAT=(\d+),USB=([01]),CHG=([01]),LEDR=([01]),LEDG=([01]),BLE=([01]),RF=([01]),RFEN=([01]),PA=([0-9A-F]{4}),PB=([0-9A-F]{8}),MODE=([0-9A-F]{2})$/i.exec(line.trim());
      if(!m) throw Error(this.$t('index.errFactoryFormat')+line);
      const bits=parseInt(m[1],16);
      const keyCount=this.deviceType==='D2'?2:(this.deviceType==='D9'?9:6);
      return {
        keys:Array.from({length:keyCount},(_,i)=>(bits>>i)&1),
        pct:parseInt(m[2],10),
        bat:parseInt(m[3],10),
        usb:parseInt(m[4],10),
        chg:parseInt(m[5],10),
        ledr:parseInt(m[6],10),
        ledg:parseInt(m[7],10),
        ble:parseInt(m[8],10),
        rf:parseInt(m[9],10),
        rfen:parseInt(m[10],10),
        pa:parseInt(m[11],16),
        pb:parseInt(m[12],16),
        mode:parseInt(m[13],16)
      };
    },
    factoryRowClass({row}){
      if(row.state==='ok') return 'factory-ok';
      if(row.state==='bad') return 'factory-bad';
      return '';
    },
    factoryCellStyle({row}){
      if(row.state==='ok') return {background:'#f0f9eb', color:'#529b2e', fontWeight:'600'};
      if(row.state==='bad') return {background:'#fef0f0', color:'#c45656', fontWeight:'600'};
      return {};
    },
    async readFactoryTest(){
      if(this.factoryReading || this.savingV62) return;
      if(this.sendLabel || !this.writer || !this.reader){
        this.factoryError = this.$t('index.factoryConnectFirst');
        this.stopFactoryTest();
        this.factoryAuto = false;
        return;
      }
      this.factoryReading = true;
      try{
        await this.writer.write(new Uint8Array([0x02,0x46]));
        const line = await this.readV62Line(2000);
        const parsed = this.parseFactoryLine(line);
        parsed.keys.forEach((pressed,i)=>{ if(pressed) this.factoryPressedMask |= (1<<i); });
        this.factoryData = parsed;
        this.factoryRaw = line;
        this.factoryError = '';
      }catch(error){
        this.factoryError = error.message || String(error);
        this.stopFactoryTest();
        this.factoryAuto = false;
      }finally{
        this.factoryReading = false;
      }
    },
    async connectSerial(){
          this.resetFactoryTest();
          this.e6DeviceModeAtLoad = null;
          const filters = [
          { usbVendorId: 0x1209, usbProductId: 0xC550 },
          { usbVendorId: 0x1A86, usbProductId: 0x8041 }, // CH591R 复合设备
          { usbVendorId: 0x1A86, usbProductId: 0x8042 }  // 2.4G Rx Head CDC
        ];	   		      
		    try {
		      this.port = await navigator.serial.requestPort({ filters });
		      await this.port.open({ baudRate: 115200 });		      
		      this.serialPortSelected = true;
			    this.writer = this.port.writable.getWriter();
			    const decoder = new TextDecoderStream();		
		      const readableStreamClosed = this.port.readable.pipeTo(decoder.writable);
          
		      const inputStream = decoder.readable;
          this.reader = decoder.readable.pipeThrough(new TransformStream(new LineBreakTransformer())).getReader();
		      this.sendLabel=false;
          this.sendText=this.$t('index.submit');
          this.conButton=this.$t('index.disconnect');
          this.conButtonType='warning';
          await this.sendQuery();
          let label=0;
          let newProtocol=false;
          let isB3=false;
          let isC4=false;
          let isB6=false;
          let isB4=false;
          let isD2=false;
          let isE6=false;
          let isD6=false;
          let isD9=false;
		  this.v62Protocol=false;
		      while (true) {
            let lineTimer = null;
            let value = '';
            let done = false;
            try{
              const result = await Promise.race([
                this.reader.read(),
                new Promise((resolve,reject)=>{
                  lineTimer = setTimeout(()=>reject(new Error(this.$t('index.errNoResponse'))), 5000);
                })
              ]);
              value = result.value;
              done = result.done;
            }finally{
              if(lineTimer) clearTimeout(lineTimer);
            }
               if(label==0){
                if(v62Bridge.isModernReply(value)){
                  await this.applyV62Snapshot(codec.parseQuery(value));
                  break;
                }else if(value=="1"){
                  this.$message({
                    message:this.$t('index.success'),
                    type:"success",
                    duration:5000,
                    showClose:true,
                    customClass:'success-message'
                  });
                  continue;
                }else if(value=='A1'){
                  this.deviceType='A1';
                  newProtocol=true;
                }else if(value=='B3'){
                  this.deviceType='B3';
                  newProtocol=true;
                  isB3=true;
                }else if(value=='C4'){
                  this.deviceType='C4';
                  newProtocol=true;
                  isC4=true;
                }else if(value=='B6'){
                  this.deviceType='B6';
                  newProtocol=true;
                  isB6=true;
                }else if(value=='D2'){
                  this.deviceType='D2';
                  newProtocol=true;
                  isD2=true;
                }else if(value=='B4'){
                  this.deviceType='B4';
                  newProtocol=true;
                  isB4=true;
                }else if(value=='E6' || value=='D6'){
                  /* E6 与 D6 指向同一台设备，统一使用新的 D6 协议(时间序号+三模选择+CRC16) */
                  this.deviceType=value;
                  newProtocol=true;
                  isD6=true;
                }else if(value=='D9'){
                  /* 9 键：D6 协议扩展，增加 key7/8/9 */
                  this.deviceType='D9';
                  newProtocol=true;
                  isD6=true;
                  isD9=true;
                }else if(value && value!='v3' && value!='v4' && value!='v5' && value!='v6'&& value!='v7'){
                  await this.disconnectSerial();
                  this.sendLabel=true;
                  this.sendText=this.$t('index.submit');
                  this.conButton=this.$t('index.connButtonText1');
                  this.conButtonType="primary";
                  this.$message({
                    type: 'warning',
                    message: this.$t('index.firmwareErr1')
                  });
                  return;
                }
               }else if(newProtocol && label==1){
                this.deviceVersion=value;
               }else if(isB3){
                let dl=label-1;
                if(dl==1) this.b3key1=parseInt(value,16);
                else if(dl==2) this.b3key2=parseInt(value,16);
                else if(dl==3) this.b3key3=parseInt(value,16);
                else if(dl==4) this.b3key1func=parseInt(value,16);
                else if(dl==5) this.b3key2func=parseInt(value,16);
                else if(dl==6) this.b3key3func=parseInt(value,16);
                else if(dl==7) this.b3key1f=parseInt(value,16);
                else if(dl==8) this.b3key2f=parseInt(value,16);
                else if(dl==9) this.b3key3f=parseInt(value,16);
                else if(dl==10) this.b3custStr1=value;
                else if(dl==11) this.b3custStr2=value;
                else if(dl==12) this.b3custStr3=value;
                else if(dl>=13 && dl<=17) this.b3key1mix[dl-13]=parseInt(value,16);
                else if(dl>=18 && dl<=22) this.b3key2mix[dl-18]=parseInt(value,16);
                else if(dl>=23 && dl<=27) this.b3key3mix[dl-23]=parseInt(value,16);
                 else if(parseInt(this.deviceVersion,10)>=1 && dl==28) this.backlightMode=parseInt(value,16);
                 else if(parseInt(this.deviceVersion,10)>=1 && dl==29) this.backlightColor1=parseInt(value,16);
                 else if(parseInt(this.deviceVersion,10)>=1 && dl==30) this.backlightColor2=parseInt(value,16);
                 else if(parseInt(this.deviceVersion,10)>=1 && dl==31) this.backlightColor3=parseInt(value,16);
                 else if(parseInt(this.deviceVersion,10)>=1 && dl==32) this.backlightBrightness=parseInt(value,16);
                else if(dl==28) this.b3keyall=parseInt(value,16);
                else if(dl==29) this.b3keyallfunc=parseInt(value,16);
                else if(dl==30) this.b3keyallf=parseInt(value,16);
                else if(dl==31) {}
                else if(dl>=32 && dl<=36) this.b3keyallmix[dl-32]=parseInt(value,16);
                else if(dl==37) this.keyallmode=parseInt(value,16);
               }else if(isC4){
                let dl=label-1;
                var c4Ver = parseInt(this.deviceVersion,10)||0;
                if(dl==1) this.c4key1=parseInt(value,16);
                else if(dl==2) this.c4key2=parseInt(value,16);
                else if(dl==3) this.c4key3=parseInt(value,16);
                else if(dl==4) this.c4key4=parseInt(value,16);
                else if(dl==5) this.c4key1func=parseInt(value,16);
                else if(dl==6) this.c4key2func=parseInt(value,16);
                else if(dl==7) this.c4key3func=parseInt(value,16);
                else if(dl==8) this.c4key4func=parseInt(value,16);
                else if(dl==9) this.c4key1f=parseInt(value,16);
                else if(dl==10) this.c4key2f=parseInt(value,16);
                else if(dl==11) this.c4key3f=parseInt(value,16);
                else if(dl==12) this.c4key4f=parseInt(value,16);
                else if(dl==13) this.c4custStr1=value;
                else if(dl==14) this.c4custStr2=value;
                else if(dl==15) this.c4custStr3=value;
                else if(dl==16) this.c4custStr4=value;
                else if(dl>=17 && dl<=21) this.c4key1mix[dl-17]=parseInt(value,16);
                else if(dl>=22 && dl<=26) this.c4key2mix[dl-22]=parseInt(value,16);
                else if(dl>=27 && dl<=31) this.c4key3mix[dl-27]=parseInt(value,16);
                else if(dl>=32 && dl<=36) this.c4key4mix[dl-32]=parseInt(value,16);
                else if(dl==37) this.backlightModeC4=parseInt(value,16);
                else if(dl==38) this.backlightColorC4_1=parseInt(value,16);
                else if(dl==39) this.backlightColorC4_2=parseInt(value,16);
                else if(dl==40) this.backlightColorC4_3=parseInt(value,16);
                else if(dl==41) this.backlightColorC4_4=parseInt(value,16);
                else if(dl==42) this.backlightBrightnessC4=parseInt(value,16);
                else if(dl==43) this.ec11CW_key=parseInt(value,16);
                else if(dl==44) this.ec11CW_func=parseInt(value,16);
                else if(dl==45) this.ec11CW_keyF=parseInt(value,16);
                else if(dl==46) this.ec11CCW_key=parseInt(value,16);
                else if(dl==47) this.ec11CCW_func=parseInt(value,16);
                else if(dl==48) this.ec11CCW_keyF=parseInt(value,16);
                else if(dl==49) this.ec11Btn_key=parseInt(value,16);
                else if(dl==50) this.ec11Btn_func=parseInt(value,16);
                else if(dl==51) this.ec11Btn_keyF=parseInt(value,16);
                else if(c4Ver>=2 && dl>=52 && dl<=56) this.ec11Btn_keyMix[dl-52]=parseInt(value,16);
                else if(c4Ver>=3 && dl>=57 && dl<=61) this.ec11CW_keyMix[dl-57]=parseInt(value,16);
                else if(c4Ver>=3 && dl>=62 && dl<=66) this.ec11CCW_keyMix[dl-62]=parseInt(value,16);
                }else if(isB6 && parseInt(this.deviceVersion,10)>=0){
                 let dl=label-1;
                if(dl==1) this.b6key1=parseInt(value,16);
                else if(dl==2) this.b6key2=parseInt(value,16);
                else if(dl==3) this.b6key3=parseInt(value,16);
                else if(dl==4) this.b6key4=parseInt(value,16);
                else if(dl==5) this.b6key5=parseInt(value,16);
                else if(dl==6) this.b6key6=parseInt(value,16);
                else if(dl==7) this.b6key1func=parseInt(value,16);
                else if(dl==8) this.b6key2func=parseInt(value,16);
                else if(dl==9) this.b6key3func=parseInt(value,16);
                else if(dl==10) this.b6key4func=parseInt(value,16);
                else if(dl==11) this.b6key5func=parseInt(value,16);
                else if(dl==12) this.b6key6func=parseInt(value,16);
                else if(dl==13) this.b6key1f=parseInt(value,16);
                else if(dl==14) this.b6key2f=parseInt(value,16);
                else if(dl==15) this.b6key3f=parseInt(value,16);
                else if(dl==16) this.b6key4f=parseInt(value,16);
                else if(dl==17) this.b6key5f=parseInt(value,16);
                else if(dl==18) this.b6key6f=parseInt(value,16);
                else if(dl==19) this.b6custStr1=value;
                else if(dl==20) this.b6custStr2=value;
                else if(dl==21) this.b6custStr3=value;
                else if(dl==22) this.b6custStr4=value;
                else if(dl==23) this.b6custStr5=value;
                else if(dl==24) this.b6custStr6=value;
                else if(dl>=25 && dl<=29) this.b6key1mix[dl-25]=parseInt(value,16);
                else if(dl>=30 && dl<=34) this.b6key2mix[dl-30]=parseInt(value,16);
                else if(dl>=35 && dl<=39) this.b6key3mix[dl-35]=parseInt(value,16);
                else if(dl>=40 && dl<=44) this.b6key4mix[dl-40]=parseInt(value,16);
                else if(dl>=45 && dl<=49) this.b6key5mix[dl-45]=parseInt(value,16);
                else if(dl>=50 && dl<=54) this.b6key6mix[dl-50]=parseInt(value,16);
                else if(dl==55) this.backlightColorB6_1=parseInt(value,16);
                else if(dl==56) this.backlightColorB6_2=parseInt(value,16);
                else if(dl==57) this.backlightColorB6_3=parseInt(value,16);
                else if(dl==58) this.backlightColorB6_4=parseInt(value,16);
                else if(dl==59) this.backlightColorB6_5=parseInt(value,16);
                else if(dl==60) this.backlightColorB6_6=parseInt(value,16);
                else if(dl==61) this.backlightModeB6=parseInt(value,16);
                else if(dl==62) this.backlightBrightnessB6=parseInt(value,16);
               }else if(isB4){
                let dl=label-1;
                if(dl==1) this.b4key1=parseInt(value,16);
                else if(dl==2) this.b4key2=parseInt(value,16);
                else if(dl==3) this.b4key3=parseInt(value,16);
                else if(dl==4) this.b4key4=parseInt(value,16);
                else if(dl==5) this.b4key1func=parseInt(value,16);
                else if(dl==6) this.b4key2func=parseInt(value,16);
                else if(dl==7) this.b4key3func=parseInt(value,16);
                else if(dl==8) this.b4key4func=parseInt(value,16);
                else if(dl==9) this.b4key1f=parseInt(value,16);
                else if(dl==10) this.b4key2f=parseInt(value,16);
                else if(dl==11) this.b4key3f=parseInt(value,16);
                else if(dl==12) this.b4key4f=parseInt(value,16);
                else if(dl==13) this.b4custStr1=value;
                else if(dl==14) this.b4custStr2=value;
                else if(dl==15) this.b4custStr3=value;
                else if(dl==16) this.b4custStr4=value;
                else if(dl>=17 && dl<=21) this.b4key1mix[dl-17]=parseInt(value,16);
                else if(dl>=22 && dl<=26) this.b4key2mix[dl-22]=parseInt(value,16);
                else if(dl>=27 && dl<=31) this.b4key3mix[dl-27]=parseInt(value,16);
                else if(dl>=32 && dl<=36) this.b4key4mix[dl-32]=parseInt(value,16);
                else if(dl==37) this.backlightModeB4=parseInt(value,16);
                else if(dl==38) this.backlightColorB4_1=parseInt(value,16);
                else if(dl==39) this.backlightColorB4_2=parseInt(value,16);
                else if(dl==40) this.backlightColorB4_3=parseInt(value,16);
                else if(dl==41) this.backlightColorB4_4=parseInt(value,16);
                else if(dl==42) this.backlightBrightnessB4=parseInt(value,16);
                }else if(isD2){
                let dl=label-1;
                if(dl==1) this.d2key1=parseInt(value,16);
                else if(dl==2) this.d2key2=parseInt(value,16);
                else if(dl==3) this.d2key1func=parseInt(value,16);
                else if(dl==4) this.d2key2func=parseInt(value,16);
                else if(dl==5) this.d2key1f=parseInt(value,16);
                else if(dl==6) this.d2key2f=parseInt(value,16);
                else if(dl==7) this.d2custStr1=value;
                else if(dl==8) this.d2custStr2=value;
                else if(dl>=9 && dl<=13) this.d2key1mix[dl-9]=parseInt(value,16);
                else if(dl>=14 && dl<=18) this.d2key2mix[dl-14]=parseInt(value,16);
                else if(dl==19) this.d2RunMode=parseInt(value,16);
                else if(dl==20) this.d2PageAction=parseInt(value,16);
                }else if(isE6){
                let dl=label-1;
                if(dl==1) this.e6key1=parseInt(value,16);
                else if(dl==2) this.e6key2=parseInt(value,16);
                else if(dl==3) this.e6key1func=parseInt(value,16);
                else if(dl==4) this.e6key2func=parseInt(value,16);
                else if(dl==5) this.e6key1f=parseInt(value,16);
                else if(dl==6) this.e6key2f=parseInt(value,16);
                else if(dl==7) this.e6custStr1=value;
                else if(dl==8) this.e6custStr2=value;
                else if(dl>=9 && dl<=13) this.e6key1mix[dl-9]=parseInt(value,16);
                else if(dl>=14 && dl<=18) this.e6key2mix[dl-14]=parseInt(value,16);
                else if(dl==19) this.e6RunMode=parseInt(value,16);
                else if(dl==20) this.e6PageAction=parseInt(value,16);
                else if(dl==21) this.e6key3=parseInt(value,16);
                else if(dl==22) this.e6key4=parseInt(value,16);
                else if(dl==23) this.e6key5=parseInt(value,16);
                else if(dl==24) this.e6key6=parseInt(value,16);
                else if(dl==25) this.e6key3func=parseInt(value,16);
                else if(dl==26) this.e6key4func=parseInt(value,16);
                else if(dl==27) this.e6key5func=parseInt(value,16);
                else if(dl==28) this.e6key6func=parseInt(value,16);
                else if(dl==29) this.e6key3f=parseInt(value,16);
                else if(dl==30) this.e6key4f=parseInt(value,16);
                else if(dl==31) this.e6key5f=parseInt(value,16);
                else if(dl==32) this.e6key6f=parseInt(value,16);
                else if(dl>=33 && dl<=37) this.e6key3mix[dl-33]=parseInt(value,16);
                else if(dl>=38 && dl<=42) this.e6key4mix[dl-38]=parseInt(value,16);
                else if(dl>=43 && dl<=47) this.e6key5mix[dl-43]=parseInt(value,16);
                else if(dl>=48 && dl<=52) this.e6key6mix[dl-48]=parseInt(value,16);
                else if(dl==53){ this.e6DeviceMode=parseInt(value,16); this.e6DeviceModeAtLoad=this.e6DeviceMode; }
                }else if(isD6){
                /* D6: [1]类型 [2]版本(由newProtocol分支接收) [3]时间序号 [4]三模选择 [5..]字段 */
                let dl=label-1;
                if(dl==1) this.e6TimeSeq=parseInt(value,16);
                else if(dl==2){ this.e6DeviceMode=parseInt(value,16); this.e6DeviceModeAtLoad=this.e6DeviceMode; }
                else if(dl==3) this.e6key1=parseInt(value,16);
                else if(dl==4) this.e6key2=parseInt(value,16);
                else if(dl==5) this.e6key1func=parseInt(value,16);
                else if(dl==6) this.e6key2func=parseInt(value,16);
                else if(dl==7) this.e6key1f=parseInt(value,16);
                else if(dl==8) this.e6key2f=parseInt(value,16);
                else if(dl==9) this.e6custStr1=value;
                else if(dl==10) this.e6custStr2=value;
                else if(dl>=11 && dl<=15) this.e6key1mix[dl-11]=parseInt(value,16);
                else if(dl>=16 && dl<=20) this.e6key2mix[dl-16]=parseInt(value,16);
                else if(dl==21) this.e6RunMode=parseInt(value,16);
                else if(dl==22) this.e6PageAction=parseInt(value,16);
                else if(dl==23) this.e6key3=parseInt(value,16);
                else if(dl==24) this.e6key4=parseInt(value,16);
                else if(dl==25) this.e6key5=parseInt(value,16);
                else if(dl==26) this.e6key6=parseInt(value,16);
                else if(dl==27) this.e6key3func=parseInt(value,16);
                else if(dl==28) this.e6key4func=parseInt(value,16);
                else if(dl==29) this.e6key5func=parseInt(value,16);
                else if(dl==30) this.e6key6func=parseInt(value,16);
                else if(dl==31) this.e6key3f=parseInt(value,16);
                else if(dl==32) this.e6key4f=parseInt(value,16);
                else if(dl==33) this.e6key5f=parseInt(value,16);
                else if(dl==34) this.e6key6f=parseInt(value,16);
                else if(dl>=35 && dl<=39) this.e6key3mix[dl-35]=parseInt(value,16);
                else if(dl>=40 && dl<=44) this.e6key4mix[dl-40]=parseInt(value,16);
                else if(dl>=45 && dl<=49) this.e6key5mix[dl-45]=parseInt(value,16);
                else if(dl>=50 && dl<=54) this.e6key6mix[dl-50]=parseInt(value,16);
                else if(dl==55) this.e6custStr3=value;
                else if(dl==56) this.e6custStr4=value;
                else if(dl==57) this.e6custStr5=value;
                else if(dl==58) this.e6custStr6=value;
                else if(dl==59) this.e6key7=parseInt(value,16);
                else if(dl==60) this.e6key8=parseInt(value,16);
                else if(dl==61) this.e6key9=parseInt(value,16);
                else if(dl==62) this.e6key7func=parseInt(value,16);
                else if(dl==63) this.e6key8func=parseInt(value,16);
                else if(dl==64) this.e6key9func=parseInt(value,16);
                else if(dl==65) this.e6key7f=parseInt(value,16);
                else if(dl==66) this.e6key8f=parseInt(value,16);
                else if(dl==67) this.e6key9f=parseInt(value,16);
                else if(dl>=68 && dl<=72) this.e6key7mix[dl-68]=parseInt(value,16);
                else if(dl>=73 && dl<=77) this.e6key8mix[dl-73]=parseInt(value,16);
                else if(dl>=78 && dl<=82) this.e6key9mix[dl-78]=parseInt(value,16);
                else if(dl==83) this.e6custStr7=value;
                else if(dl==84) this.e6custStr8=value;
                else if(dl==85) this.e6custStr9=value;
                }else{
                let dataLabel=newProtocol?label-1:label;
                if(dataLabel==1){
                  this.key=parseInt(value,16);
                }else if(dataLabel==2){
                  this.keyfunc=parseInt(value,16);
                }else if(dataLabel==3){
                  this.keyf=parseInt(value,16);
                }else if(dataLabel==4){
                  this.keystr=value;
                }else if(dataLabel==5){
                  this.keymix[0]=parseInt(value,16);
                }else if(dataLabel==6){
                  this.keymix[1]=parseInt(value,16);
                }else if(dataLabel==7){
                  this.keymix[2]=parseInt(value,16);
                }else if(dataLabel==8){
                  this.keymix[3]=parseInt(value,16);
                }else if(dataLabel==9){
                  this.keymix[4]=parseInt(value,16);
                }else if(dataLabel==10){
                  this.triggerMode=parseInt(value,16);
                }else if(dataLabel==11){
                  this.timerHour=parseInt(value,16);
                }else if(dataLabel==12){
                  this.timerMinute=parseInt(value,16);
                }else if(dataLabel==13){
                  this.timerSecond=parseInt(value,16);
                }
               }
		        label++;
            if(isB3 && ((parseInt(this.deviceVersion,10)>=1 ? label>=34 : label>=39))){
              label=0;
              this.$refs.b3key1config.setKey(this.b3key1,this.b3key1func,this.b3key1f,this.b3custStr1,this.b3key1mix);
              this.$refs.b3key2config.setKey(this.b3key2,this.b3key2func,this.b3key2f,this.b3custStr2,this.b3key2mix);
              this.$refs.b3key3config.setKey(this.b3key3,this.b3key3func,this.b3key3f,this.b3custStr3,this.b3key3mix);
              this.updateB3KeyDescs();
              break;   // ★ B3 配置解析完成，退出常驻读取循环
            }else if(isC4 && ((parseInt(this.deviceVersion,10)>=3 ? label>=68 : (parseInt(this.deviceVersion,10)>=2 ? label>=58 : label>=53)))){
              label=0;
              this.$refs.c4key1config.setKey(this.c4key1,this.c4key1func,this.c4key1f,this.c4custStr1,this.c4key1mix);
              this.$refs.c4key2config.setKey(this.c4key2,this.c4key2func,this.c4key2f,this.c4custStr2,this.c4key2mix);
              this.$refs.c4key3config.setKey(this.c4key3,this.c4key3func,this.c4key3f,this.c4custStr3,this.c4key3mix);
              this.$refs.c4key4config.setKey(this.c4key4,this.c4key4func,this.c4key4f,this.c4custStr4,this.c4key4mix);
              this.initEc11UI();
              this.updateC4KeyDescs();
              break;   // ★ C4 配置解析完成，退出常驻读取循环
            }else if(isB6 && label>=64){
              label=0;
              this.$refs.b6key1config.setKey(this.b6key1,this.b6key1func,this.b6key1f,this.b6custStr1,this.b6key1mix);
              this.$refs.b6key2config.setKey(this.b6key2,this.b6key2func,this.b6key2f,this.b6custStr2,this.b6key2mix);
              this.$refs.b6key3config.setKey(this.b6key3,this.b6key3func,this.b6key3f,this.b6custStr3,this.b6key3mix);
              this.$refs.b6key4config.setKey(this.b6key4,this.b6key4func,this.b6key4f,this.b6custStr4,this.b6key4mix);
              this.$refs.b6key5config.setKey(this.b6key5,this.b6key5func,this.b6key5f,this.b6custStr5,this.b6key5mix);
              this.$refs.b6key6config.setKey(this.b6key6,this.b6key6func,this.b6key6f,this.b6custStr6,this.b6key6mix);
              this.updateB6KeyDescs();
              break;   // ★ B6 配置解析完成，退出常驻读取循环
            }else if(isD2 && label>=22){
              label=0;
              this.$refs.d2key1config.setKey(this.d2key1,this.d2key1func,this.d2key1f,this.d2custStr1,this.d2key1mix);
              this.$refs.d2key2config.setKey(this.d2key2,this.d2key2func,this.d2key2f,this.d2custStr2,this.d2key2mix);
              break;   // ★ D2 配置解析完成，退出常驻读取循环
            }else if(isB4 && label>=44){
              label=0;
              this.$refs.b4key1config.setKey(this.b4key1,this.b4key1func,this.b4key1f,this.b4custStr1,this.b4key1mix);
              this.$refs.b4key2config.setKey(this.b4key2,this.b4key2func,this.b4key2f,this.b4custStr2,this.b4key2mix);
              this.$refs.b4key3config.setKey(this.b4key3,this.b4key3func,this.b4key3f,this.b4custStr3,this.b4key3mix);
              this.$refs.b4key4config.setKey(this.b4key4,this.b4key4func,this.b4key4f,this.b4custStr4,this.b4key4mix);
              this.updateB4KeyDescs();
              break;   // ★ B4 配置解析完成，退出常驻读取循环
            }else if(isE6 && label>=55){
              label=0;
              this.$refs.e6key1config.setKey(this.e6key1,this.e6key1func,this.e6key1f,this.e6custStr1,this.e6key1mix);
              this.$refs.e6key2config.setKey(this.e6key2,this.e6key2func,this.e6key2f,this.e6custStr2,this.e6key2mix);
              this.$refs.e6key3config.setKey(this.e6key3,this.e6key3func,this.e6key3f,'',this.e6key3mix);
              this.$refs.e6key4config.setKey(this.e6key4,this.e6key4func,this.e6key4f,'',this.e6key4mix);
              this.$refs.e6key5config.setKey(this.e6key5,this.e6key5func,this.e6key5f,'',this.e6key5mix);
              this.$refs.e6key6config.setKey(this.e6key6,this.e6key6func,this.e6key6f,'',this.e6key6mix);
              this.updateE6KeyDescs();
              break;   // ★ E6 配置解析完成，退出常驻读取循环
            }else if(isD6 && label>=(isD9?87:60)){
              label=0;
              this.$refs.e6key1config.setKey(this.e6key1,this.e6key1func,this.e6key1f,this.e6custStr1,this.e6key1mix);
              this.$refs.e6key2config.setKey(this.e6key2,this.e6key2func,this.e6key2f,this.e6custStr2,this.e6key2mix);
              this.$refs.e6key3config.setKey(this.e6key3,this.e6key3func,this.e6key3f,this.e6custStr3,this.e6key3mix);
              this.$refs.e6key4config.setKey(this.e6key4,this.e6key4func,this.e6key4f,this.e6custStr4,this.e6key4mix);
              this.$refs.e6key5config.setKey(this.e6key5,this.e6key5func,this.e6key5f,this.e6custStr5,this.e6key5mix);
              this.$refs.e6key6config.setKey(this.e6key6,this.e6key6func,this.e6key6f,this.e6custStr6,this.e6key6mix);
              if(isD9 && this.$refs.e6key7config){
                this.$refs.e6key7config.setKey(this.e6key7,this.e6key7func,this.e6key7f,this.e6custStr7,this.e6key7mix);
                this.$refs.e6key8config.setKey(this.e6key8,this.e6key8func,this.e6key8f,this.e6custStr8,this.e6key8mix);
                this.$refs.e6key9config.setKey(this.e6key9,this.e6key9func,this.e6key9f,this.e6custStr9,this.e6key9mix);
              }
              this.updateE6KeyDescs();
              break;   // ★ D6/D9 配置解析完成，退出常驻读取循环
            }else if(!isB3 && !isC4 && !isB6 && !isD2 && !isB4 && !isE6 && !isD6 && ((newProtocol && label>=15) || (!newProtocol && label>=14))){
              label=0;
              this.$refs.key1config.setKey(this.key,this.keyfunc,this.keyf,this.keystr,this.keymix);
              break;   // ★ 默认配置解析完成，退出常驻读取循环
            }
		        if (done) {
              await readableStreamClosed.catch(()=>{});       
		          break;
		        }
		      }
		    } catch (error) {
          if(error.name=='NotFoundError'){
            return;
          }
          else if(error.name=='InvalidStateError'||error.name=='NetworkError'){
            this.$message.error(this.$t('index.conError1'));
          }
          else{
            this.$message.error(this.$t('index.conError2')+error);
          }
          try{ await this.disconnectSerial(); }catch(e){}
		    }
		  },
      async disconnectSerial(){	
        const wasFactoryTest = this.showFactoryTest;
        this.stopFactoryTest();
        if(wasFactoryTest) await this.sendFactoryLed(false);
        this.showFactoryTest = false;
        try{
          if(this.reader){
            await this.reader.cancel();
            await this.reader.releaseLock();
          }
        }catch(e){}
        try{
          if(this.writer){
            await this.writer.close();
            await this.writer.releaseLock();
          }
        }catch(e){}
        try{
          if(this.port) await this.port.close();
        }catch(e){}
        this.reader = null;
        this.writer = null;
        this.port = null;
        this.sendLabel = true;
        this.deviceType = '';
        this.v62Protocol = false;
        this.sendText = this.$t('index.connFirst');
        this.conButton = this.$t('index.connButtonText1');
        this.conButtonType = 'primary';
      },
      getCommonData(){
        var this_app=this;
        axios.get('common.json')
        .then(function (response) {
          let commonkeys=[];
          for(var i=0;i<response.data.length;i++){
            let tmp={};
            if(!tmp.mixkeys){
              tmp.mixkeys=[];
            }
            for(var j=0;j<response.data[i].mixkeys.length;j++){
               tmp.mixkeys[j]=(parseInt(response.data[i].mixkeys[j]));
            }
            if(this_app.$i18n.locale=='zh'){
              tmp.description=response.data[i].description_zh;
            }else if(this_app.$i18n.locale=='en'){
              tmp.description=response.data[i].description_en;
            }
            commonkeys.push(tmp);          
          }
          if(this_app.deviceType==='B3'){
             if(this_app.$refs.b3key1config) this_app.$refs.b3key1config.setCommon(commonkeys);
             if(this_app.$refs.b3key2config) this_app.$refs.b3key2config.setCommon(commonkeys);
             if(this_app.$refs.b3key3config) this_app.$refs.b3key3config.setCommon(commonkeys);
           }else if(this_app.deviceType==='C4'){
             if(this_app.$refs.c4key1config) this_app.$refs.c4key1config.setCommon(commonkeys);
             if(this_app.$refs.c4key2config) this_app.$refs.c4key2config.setCommon(commonkeys);
             if(this_app.$refs.c4key3config) this_app.$refs.c4key3config.setCommon(commonkeys);
             if(this_app.$refs.c4key4config) this_app.$refs.c4key4config.setCommon(commonkeys);
           }else if(this_app.deviceType==='B6'){
             if(this_app.$refs.b6key1config) this_app.$refs.b6key1config.setCommon(commonkeys);
             if(this_app.$refs.b6key2config) this_app.$refs.b6key2config.setCommon(commonkeys);
             if(this_app.$refs.b6key3config) this_app.$refs.b6key3config.setCommon(commonkeys);
             if(this_app.$refs.b6key4config) this_app.$refs.b6key4config.setCommon(commonkeys);
             if(this_app.$refs.b6key5config) this_app.$refs.b6key5config.setCommon(commonkeys);
             if(this_app.$refs.b6key6config) this_app.$refs.b6key6config.setCommon(commonkeys);
           }else if(this_app.deviceType==='D2'){
             if(this_app.$refs.d2key1config) this_app.$refs.d2key1config.setCommon(commonkeys);
             if(this_app.$refs.d2key2config) this_app.$refs.d2key2config.setCommon(commonkeys);
           }else if(this_app.deviceType==='B4'){
             if(this_app.$refs.b4key1config) this_app.$refs.b4key1config.setCommon(commonkeys);
             if(this_app.$refs.b4key2config) this_app.$refs.b4key2config.setCommon(commonkeys);
             if(this_app.$refs.b4key3config) this_app.$refs.b4key3config.setCommon(commonkeys);
             if(this_app.$refs.b4key4config) this_app.$refs.b4key4config.setCommon(commonkeys);
           }else if(this_app.deviceType==='E6' || this_app.deviceType==='D6' || this_app.deviceType==='D9'){
             if(this_app.$refs.e6key1config) this_app.$refs.e6key1config.setCommon(commonkeys);
             if(this_app.$refs.e6key2config) this_app.$refs.e6key2config.setCommon(commonkeys);
             if(this_app.$refs.e6key3config) this_app.$refs.e6key3config.setCommon(commonkeys);
             if(this_app.$refs.e6key4config) this_app.$refs.e6key4config.setCommon(commonkeys);
             if(this_app.$refs.e6key5config) this_app.$refs.e6key5config.setCommon(commonkeys);
             if(this_app.$refs.e6key6config) this_app.$refs.e6key6config.setCommon(commonkeys);
             if(this_app.deviceType==='D9'){
               if(this_app.$refs.e6key7config) this_app.$refs.e6key7config.setCommon(commonkeys);
               if(this_app.$refs.e6key8config) this_app.$refs.e6key8config.setCommon(commonkeys);
               if(this_app.$refs.e6key9config) this_app.$refs.e6key9config.setCommon(commonkeys);
             }
           }else{
            if(this_app.$refs.key1config) this_app.$refs.key1config.setCommon(commonkeys);
          }
        })
        .catch(function (error) {
          console.log(error);
        });
      },
      changeLang(lang){
        this.$i18n.locale = lang;
        document.title = this.$t('index.pageTitle');
        document.documentElement.lang = lang === 'en' ? 'en' : 'zh-CN';
        localStorage.setItem('lang',lang);      
        if(this.deviceType==='B3'){
          if(this.$refs.b3key1config) this.$refs.b3key1config.refreshText();
          if(this.$refs.b3key2config) this.$refs.b3key2config.refreshText();
          if(this.$refs.b3key3config) this.$refs.b3key3config.refreshText();
        }else if(this.deviceType==='C4'){
          if(this.$refs.c4key1config) this.$refs.c4key1config.refreshText();
          if(this.$refs.c4key2config) this.$refs.c4key2config.refreshText();
          if(this.$refs.c4key3config) this.$refs.c4key3config.refreshText();
          if(this.$refs.c4key4config) this.$refs.c4key4config.refreshText();
        }else if(this.deviceType==='B6'){
          if(this.$refs.b6key1config) this.$refs.b6key1config.refreshText();
          if(this.$refs.b6key2config) this.$refs.b6key2config.refreshText();
          if(this.$refs.b6key3config) this.$refs.b6key3config.refreshText();
          if(this.$refs.b6key4config) this.$refs.b6key4config.refreshText();
          if(this.$refs.b6key5config) this.$refs.b6key5config.refreshText();
          if(this.$refs.b6key6config) this.$refs.b6key6config.refreshText();
        }else if(this.deviceType==='D2'){
          if(this.$refs.d2key1config) this.$refs.d2key1config.refreshText();
          if(this.$refs.d2key2config) this.$refs.d2key2config.refreshText();
        }else if(this.deviceType==='E6' || this.deviceType==='D6' || this.deviceType==='D9'){
          if(this.$refs.e6key1config) this.$refs.e6key1config.refreshText();
          if(this.$refs.e6key2config) this.$refs.e6key2config.refreshText();
          if(this.$refs.e6key3config) this.$refs.e6key3config.refreshText();
          if(this.$refs.e6key4config) this.$refs.e6key4config.refreshText();
          if(this.$refs.e6key5config) this.$refs.e6key5config.refreshText();
          if(this.$refs.e6key6config) this.$refs.e6key6config.refreshText();
          if(this.deviceType==='D9'){
            if(this.$refs.e6key7config) this.$refs.e6key7config.refreshText();
            if(this.$refs.e6key8config) this.$refs.e6key8config.refreshText();
            if(this.$refs.e6key9config) this.$refs.e6key9config.refreshText();
          }
        }else{
          if(this.$refs.key1config) this.$refs.key1config.refreshText();
        }
        this.getCommonData();
        if(this.sendLabel==true){
          this.sendText=this.$t('index.connFirst');
          this.conButton=this.$t('index.connButtonText1');
        }else{
          this.sendText=this.$t('index.submit');
          this.conButton=this.$t('index.disconnect');
        }
      },
      selectB3Key(key){
        this.selectedB3Key=key;
      },
      updateB3KeyDescs(){
        if(this.$refs.b3key1config){
          this.b3KeyDesc1=this.$refs.b3key1config.getKeyDescription();
        }
        if(this.$refs.b3key2config){
          this.b3KeyDesc2=this.$refs.b3key2config.getKeyDescription();
        }
        if(this.$refs.b3key3config){
          this.b3KeyDesc3=this.$refs.b3key3config.getKeyDescription();
        }
      },
      selectC4Key(key){
        this.selectedC4Key=key;
      },
      updateC4KeyDescs(){
        if(this.$refs.c4key1config) this.c4KeyDesc1=this.$refs.c4key1config.getKeyDescription();
        if(this.$refs.c4key2config) this.c4KeyDesc2=this.$refs.c4key2config.getKeyDescription();
        if(this.$refs.c4key3config) this.c4KeyDesc3=this.$refs.c4key3config.getKeyDescription();
        if(this.$refs.c4key4config) this.c4KeyDesc4=this.$refs.c4key4config.getKeyDescription();
      },
      getEc11Description(type){
        const funcKeyOptions=this.fnKeyOptions;
        const mediaKeyOptions=this.mediaKeyOptions;
        let func, keyChar, mediaVal, fnVal, scrollDir, mouseBtn, mouseMode;
        let mixCount, mixShowArray, mixFuncSelectValue, mixText;
        if(type==='CW'){
          func=this.ec11CW_func; keyChar=this.ec11CW_keyChar; mediaVal=this.ec11CW_mediaVal; fnVal=this.ec11CW_fnVal; scrollDir=this.ec11CW_scrollDir; mouseBtn=this.ec11CW_mouseBtn; mouseMode=this.ec11CW_mouseMode;
          mixCount=this.ec11CW_mixCount; mixShowArray=this.ec11CW_mixShowArray; mixFuncSelectValue=this.ec11CW_mixFuncSelectValue; mixText=this.ec11CW_mixText;
        }else if(type==='CCW'){
          func=this.ec11CCW_func; keyChar=this.ec11CCW_keyChar; mediaVal=this.ec11CCW_mediaVal; fnVal=this.ec11CCW_fnVal; scrollDir=this.ec11CCW_scrollDir; mouseBtn=this.ec11CCW_mouseBtn; mouseMode=this.ec11CCW_mouseMode;
          mixCount=this.ec11CCW_mixCount; mixShowArray=this.ec11CCW_mixShowArray; mixFuncSelectValue=this.ec11CCW_mixFuncSelectValue; mixText=this.ec11CCW_mixText;
        }else{
          func=this.ec11Btn_func; keyChar=this.ec11Btn_keyChar; mediaVal=this.ec11Btn_mediaVal; fnVal=this.ec11Btn_fnVal; mouseBtn=this.ec11Btn_mouseBtn; mouseMode=this.ec11Btn_mouseMode;
          mixCount=this.ec11Btn_mixCount; mixShowArray=this.ec11Btn_mixShowArray; mixFuncSelectValue=this.ec11Btn_mixFuncSelectValue; mixText=this.ec11Btn_mixText;
        }
        if(func===0) return keyChar||this.$t('keyConfig.notSet');
        if(func===1){
          var m=mediaKeyOptions.find(function(m){return m.value===mediaVal;});
          return m?m.label:this.$t('keyConfig.notSet');
        }
        if(func===2){
          var f=funcKeyOptions.find(function(f){return f.value===fnVal;});
          return f?f.label:this.$t('keyConfig.notSet');
        }
        if(func===3) return scrollDir===0?this.$t('index.ec11ScrollUp'):this.$t('index.ec11ScrollDown');
        if(func===4){
          const btnLabel=mouseBtn===1?this.$t('index.ec11MouseLeft'):(mouseBtn===4?this.$t('index.ec11MouseMiddle'):this.$t('index.ec11MouseRight'));
          const modeLabel=mouseMode===0?this.$t('index.ec11ClickSingle'):(mouseMode===1?this.$t('index.ec11ClickContinuous'):this.$t('index.ec11ClickPress'));
          return btnLabel+' '+modeLabel;
        }
        if(func===5){
          var mixDesc='';
          for(var i=0;i<mixCount;i++){
            if(i>0) mixDesc+='+';
            if(mixShowArray[i]){
              var mf=funcKeyOptions.find(function(f){return f.value===mixFuncSelectValue[i];});
              mixDesc+=mf?mf.label:this.$t('keyConfig.notSet');
            }else{
              mixDesc+=mixText[i]||this.$t('keyConfig.notSet');
            }
          }
          return mixDesc||this.$t('keyConfig.notSet');
        }
        return this.$t('keyConfig.notSet');
      },
      selectB6Key(key){
        this.selectedB6Key=key;
      },
      updateB6KeyDescs(){
        if(this.$refs.b6key1config) this.b6KeyDesc1=this.$refs.b6key1config.getKeyDescription();
        if(this.$refs.b6key2config) this.b6KeyDesc2=this.$refs.b6key2config.getKeyDescription();
        if(this.$refs.b6key3config) this.b6KeyDesc3=this.$refs.b6key3config.getKeyDescription();
        if(this.$refs.b6key4config) this.b6KeyDesc4=this.$refs.b6key4config.getKeyDescription();
        if(this.$refs.b6key5config) this.b6KeyDesc5=this.$refs.b6key5config.getKeyDescription();
        if(this.$refs.b6key6config) this.b6KeyDesc6=this.$refs.b6key6config.getKeyDescription();
      },
      selectB4Key(key){
        this.selectedB4Key=key;
      },
      updateB4KeyDescs(){
        if(this.$refs.b4key1config) this.b4KeyDesc1=this.$refs.b4key1config.getKeyDescription();
        if(this.$refs.b4key2config) this.b4KeyDesc2=this.$refs.b4key2config.getKeyDescription();
        if(this.$refs.b4key3config) this.b4KeyDesc3=this.$refs.b4key3config.getKeyDescription();
        if(this.$refs.b4key4config) this.b4KeyDesc4=this.$refs.b4key4config.getKeyDescription();
      },
      selectE6Key(key){
        this.selectedE6Key=key;
        this.updateE6KeyDescs();
        this.$set(this.e6KeyOpen, key - 1, true);
        this.$nextTick(() => {
          const el = document.getElementById('e6keycard' + key);
          if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        });
      },
      toggleE6Key(index){
        this.updateE6KeyDescs();
        this.$set(this.e6KeyOpen, index, !this.e6KeyOpen[index]);
      },
      updateE6KeyDescs(){
        if(this.$refs.e6key1config) this.e6KeyDesc1=this.$refs.e6key1config.getKeyDescription();
        if(this.$refs.e6key2config) this.e6KeyDesc2=this.$refs.e6key2config.getKeyDescription();
        if(this.$refs.e6key3config) this.e6KeyDesc3=this.$refs.e6key3config.getKeyDescription();
        if(this.$refs.e6key4config) this.e6KeyDesc4=this.$refs.e6key4config.getKeyDescription();
        if(this.$refs.e6key5config) this.e6KeyDesc5=this.$refs.e6key5config.getKeyDescription();
        if(this.$refs.e6key6config) this.e6KeyDesc6=this.$refs.e6key6config.getKeyDescription();
        if(this.deviceType==='D9'){
          if(this.$refs.e6key7config) this.e6KeyDesc7=this.$refs.e6key7config.getKeyDescription();
          if(this.$refs.e6key8config) this.e6KeyDesc8=this.$refs.e6key8config.getKeyDescription();
          if(this.$refs.e6key9config) this.e6KeyDesc9=this.$refs.e6key9config.getKeyDescription();
        }
      },
      getBacklightColor(colorIndex){
        const colors={1:'#409EFF',2:'#67C23A',3:'#F56C6C',4:'#E6A23C',5:'#4ECDC4',6:'#B37FEB',7:'#C0C4CC'};
        return colors[colorIndex]||'#C0C4CC';
      }
  },
  computed:{
    factoryKeyCount(){
      return this.deviceType==='D2'?2:(this.deviceType==='D9'?9:6);
    },
    factoryRows(){
      const d=this.factoryData;
      if(!d) return [];
      const hex=(v,n)=>'0x'+v.toString(16).toUpperCase().padStart(n,'0');
      const rows=[];
      for(let i=0;i<this.factoryKeyCount;i++){
        const pressed=!!d.keys[i];
        const latched=!!(this.factoryPressedMask & (1<<i));
        rows.push({
          name:this.$t('index.factoryKey')+(i+1),
          value:pressed?this.$t('index.factoryPressed'):(latched?this.$t('index.factoryReleasedDetected'):this.$t('index.factoryReleased')),
          state:(pressed||latched)?'ok':'',
          result:latched?'pass':'wait'
        });
      }
      rows.push({name:this.$t('index.factoryBatteryAdc'),value:d.bat+' mV / '+d.pct+'%',state:d.bat>=2000?'ok':'bad',result:d.bat>=2000?'pass':'fail'});
      rows.push({name:this.$t('index.factoryUsbVcc'),value:d.usb?this.$t('index.factoryUsbHigh'):this.$t('index.factoryUsbLow'),state:d.usb?'ok':'',result:d.usb?'pass':'wait'});
      rows.push({name:'CHRG',value:d.chg?this.$t('index.factoryCharging'):this.$t('index.factoryNotCharging'),state:d.chg?'ok':'',result:d.chg?'pass':'wait'});
      rows.push({name:this.$t('index.factoryLedRed'),value:d.ledr?this.$t('index.factoryOn'):this.$t('index.factoryOff'),state:d.ledr?'ok':'',result:d.ledr?'pass':'wait'});
      rows.push({name:this.$t('index.factoryLedGreen'),value:d.ledg?this.$t('index.factoryOn'):this.$t('index.factoryOff'),state:d.ledg?'ok':'',result:d.ledg?'pass':'wait'});
      rows.push({name:this.$t('index.factoryBle'),value:d.ble?this.$t('index.factoryConnected'):this.$t('index.factoryDisconnected'),state:d.ble?'ok':'',result:d.ble?'pass':'wait'});
      if(this.deviceType==='D6'||this.deviceType==='D9'){
        rows.push({name:this.$t('index.factoryRf24'),value:d.rfen?(d.rf?this.$t('index.factoryConnected'):this.$t('index.factoryDisconnected')):this.$t('index.factoryNotEnabled'),state:d.rfen?(d.rf?'ok':''):'',result:d.rfen?(d.rf?'pass':'wait'):'info'});
      }
      rows.push({name:this.$t('index.factoryPaRaw'),value:hex(d.pa,4),result:'info'});
      rows.push({name:this.$t('index.factoryPbRaw'),value:hex(d.pb,8),result:'info'});
      if(this.deviceType==='D6'||this.deviceType==='D9'){
        rows.push({name:this.$t('index.factoryMode'),value:hex(d.mode,2),result:'info'});
      }
      return rows;
    },
    factoryAbnormal(){
      const d=this.factoryData;
      if(!d) return false;
      return !d.keys.some(Boolean) && !d.usb && !d.chg && d.bat < 2000;
    },
    factoryPass(){
      const d=this.factoryData;
      const mask=(1<<this.factoryKeyCount)-1;
      return !!d && (this.factoryPressedMask & mask) === mask && d.bat >= 2000;
    },
    mediaKeyOptions(){
      const keys = this.$t('keyConfig.mediakeys');
      const values = [0xB5, 0xB6, 0xB7, 0xCD, 0xB0, 0xE2, 0xE9, 0xEA, 0x6F, 0x70, 0x183, 0x18A, 0x192, 0x194, 0x223, 0x224, 0x225, 0x227, 0x22A, 0x30, 0x32, 0xB2, 0xB3, 0xB4, 0x19E];
      return keys.map((k, i) => ({ value: values[i], label: k }));
    },
    fnKeyOptions(){
      const keys = this.$t('keyConfig.functionkeys');
      const values = [0x80, 0x81, 0x82, 0x83, 0x84, 0x85, 0x86, 0x87, 0xDA, 0xD9, 0xD8, 0xD7, 0xB2, 0xB3, 0xB0, 0xB1, 0xD1, 0xD4, 0xD3, 0xD6, 0xD2, 0xD5, 0xC1, 0xCE, 0xCF, 0xD0, 0xDB, 0xC2, 0xC3, 0xC4, 0xC5, 0xC6, 0xC7, 0xC8, 0xC9, 0xCA, 0xCB, 0xCC, 0xCD, 0xF0, 0xF1, 0xF2, 0xF3, 0xF4, 0xF5, 0xF6, 0xF7, 0xF8, 0xF9, 0xFA, 0xFB, 0xED, 0xDC, 0xDD, 0xDE, 0xDF, 0xE0, 0xE1, 0xE2, 0xE3, 0xE4, 0xE5, 0xE6, 0xE7, 0xE8, 0xE9, 0xEA, 0xEB];
      return keys.map((k, i) => ({ value: values[i], label: k }));
    },
    colorOptions(){
      return [
        { value: 1, label: this.$t('index.colorBlue') },
        { value: 2, label: this.$t('index.colorGreen') },
        { value: 3, label: this.$t('index.colorRed') },
        { value: 4, label: this.$t('index.colorYellow') },
        { value: 5, label: this.$t('index.colorCyan') },
        { value: 6, label: this.$t('index.colorPurple') },
        { value: 7, label: this.$t('index.colorWhite') },
      ];
    },
    c4Ec11CWDesc(){
      return this.getEc11Description('CW');
    },
    c4Ec11CCWDesc(){
      return this.getEc11Description('CCW');
    },
    c4Ec11BtnDesc(){
      return this.getEc11Description('Btn');
    },
    shortcutGroups(){
      return [
        { name: this.$t('index.scGroupEdit'), items: [
          { key: 'Ctrl+C', desc: this.$t('index.scCopy') },
          { key: 'Ctrl+V', desc: this.$t('index.scPaste') },
          { key: 'Ctrl+X', desc: this.$t('index.scCut') },
          { key: 'Ctrl+Z', desc: this.$t('index.scUndo') },
          { key: 'Ctrl+Y', desc: this.$t('index.scRedo') },
          { key: 'Ctrl+A', desc: this.$t('index.scSelectAll') },
          { key: 'Ctrl+S', desc: this.$t('index.scSave') },
          { key: 'Ctrl+F', desc: this.$t('index.scFind') },
          { key: 'Ctrl+H', desc: this.$t('index.scReplace') },
          { key: 'Ctrl+P', desc: this.$t('index.scPrint') },
        ]},
        { name: this.$t('index.scGroupWin'), items: [
          { key: 'Alt+Tab', desc: this.$t('index.scSwitchWin') },
          { key: 'Win+D', desc: this.$t('index.scDesktop') },
          { key: 'Win+E', desc: this.$t('index.scExplorer') },
          { key: 'Win+L', desc: this.$t('index.scLock') },
          { key: 'Win+I', desc: this.$t('index.scSettings') },
          { key: 'Win+S', desc: this.$t('index.scSearch') },
          { key: 'Alt+F4', desc: this.$t('index.scCloseWin') },
          { key: 'F11', desc: this.$t('index.scFullscreen') },
          { key: 'PrtSc', desc: this.$t('index.scScreenshot') },
          { key: 'Win+Shift+S', desc: this.$t('index.scSnipping') },
        ]},
        { name: this.$t('index.scGroupText'), items: [
          { key: 'Ctrl+B', desc: this.$t('index.scBold') },
          { key: 'Ctrl+I', desc: this.$t('index.scItalic') },
          { key: 'Ctrl+U', desc: this.$t('index.scUnderline') },
          { key: 'Ctrl+Shift+→', desc: this.$t('index.scSelectWord') },
          { key: 'Ctrl+Home', desc: this.$t('index.scHome') },
          { key: 'Ctrl+End', desc: this.$t('index.scEnd') },
          { key: 'Delete', desc: this.$t('index.scDelete') },
        ]},
        { name: this.$t('index.scGroupBrowser'), items: [
          { key: 'Ctrl+T', desc: this.$t('index.scNewTab') },
          { key: 'Ctrl+W', desc: this.$t('index.scCloseTab') },
          { key: 'Ctrl+Tab', desc: this.$t('index.scSwitchTab') },
          { key: 'Ctrl+Shift+T', desc: this.$t('index.scReopenTab') },
          { key: 'Ctrl+D', desc: this.$t('index.scBookmark') },
          { key: 'F5', desc: this.$t('index.scRefresh') },
          { key: 'Ctrl+F5', desc: this.$t('index.scHardRefresh') },
        ]},
        { name: this.$t('index.scGroupDev'), items: [
          { key: 'F12', desc: this.$t('index.scDevTools') },
          { key: 'Ctrl+Shift+I', desc: this.$t('index.scDevTools2') },
          { key: 'F5', desc: this.$t('index.scDebug') },
          { key: 'Ctrl+`', desc: this.$t('index.scTerminal') },
          { key: 'Ctrl+Shift+P', desc: this.$t('index.scCommandPalette') },
          { key: 'Ctrl+Shift+N', desc: this.$t('index.scNewWin') },
        ]},
        { name: this.$t('index.scGroupIDE'), items: [
          { key: 'Ctrl+Shift+K', desc: this.$t('index.scDeleteLine') },
          { key: 'Ctrl+D', desc: this.$t('index.scSelectNext') },
          { key: 'Ctrl+Shift+L', desc: this.$t('index.scSelectAll') },
          { key: 'Alt+↑/↓', desc: this.$t('index.scMoveLine') },
          { key: 'Ctrl+/', desc: this.$t('index.scComment') },
          { key: 'F2', desc: this.$t('index.scRename') },
          { key: 'Ctrl+Shift+F', desc: this.$t('index.scGlobalSearch') },
          { key: 'Ctrl+Shift+E', desc: this.$t('index.scExplorer') },
          { key: 'Ctrl+G', desc: this.$t('index.scGoToLine') },
          { key: 'Alt+Shift+F', desc: this.$t('index.scFormat') },
          { key: 'Ctrl+Space', desc: this.$t('index.scSuggest') },
          { key: 'Ctrl+.', desc: this.$t('index.scQuickFix') },
          { key: 'Ctrl+B', desc: this.$t('index.scToggleSidebar') },
          { key: 'Ctrl+Shift+O', desc: this.$t('index.scGoToSymbol') },
        ]},
        // Cursor AI 快捷键
        { name: this.$t('index.scGroupAIChat'), items: [
          { key: 'Ctrl+Enter', desc: this.$t('index.scAIChatSubmit') },
          { key: 'Shift+Enter', desc: this.$t('index.scAIChatNewline') },
          { key: 'Ctrl+L', desc: this.$t('index.scAIChatClear') },
          { key: 'Ctrl+Shift+Z', desc: this.$t('index.scAIChatUndo') },
          { key: 'Ctrl+Shift+U', desc: this.$t('index.scAIChatRetry') },
          { key: 'Esc', desc: this.$t('index.scAIChatStop') },
        ]},
        { name: this.$t('index.scGroupAICode'), items: [
          { key: 'Ctrl+K', desc: this.$t('index.scAICodeSidebar') },
          { key: 'Ctrl+I', desc: this.$t('index.scAICodeInline') },
          { key: 'Ctrl+Shift+L', desc: this.$t('index.scAICodeSelectBlock') },
          { key: 'Tab', desc: this.$t('index.scAICodeAccept') },
          { key: 'Shift+Tab', desc: this.$t('index.scAICodeReject') },
          { key: 'Ctrl+Shift+R', desc: this.$t('index.scAICodeRefactor') },
        ]},
        { name: this.$t('index.scGroupAIFile'), items: [
          { key: 'Ctrl+Shift+P', desc: this.$t('index.scAIFileCmdPalette') },
          { key: 'Ctrl+O', desc: this.$t('index.scAIFileOpen') },
          { key: 'Ctrl+Shift+F', desc: this.$t('index.scAIFileSearch') },
          { key: 'Ctrl+B', desc: this.$t('index.scAIFileExplorer') },
          { key: 'Ctrl+`', desc: this.$t('index.scAIFileTerminal') },
        ]},
        { name: this.$t('index.scGroupAIWin'), items: [
          { key: 'Ctrl+N', desc: this.$t('index.scAIWinNew') },
          { key: 'Ctrl+Tab', desc: this.$t('index.scAIWinSwitch') },
          { key: 'Ctrl+W', desc: this.$t('index.scAIWinClose') },
          { key: 'Alt+←/→', desc: this.$t('index.scAIWinHistory') },
        ]},
        { name: this.$t('index.scGroupZoomShort'), items: [
          { key: 'Ctrl + +',   desc: this.$t('index.scZoomIn') },
          { key: 'Ctrl + -',   desc: this.$t('index.scZoomOut') },
          { key: 'Ctrl + 0',   desc: this.$t('index.scZoomReset') },
        ]},
        { name: this.$t('index.scGroupOfficeCommon'), items: [
          { key: 'Ctrl+N', desc: this.$t('index.scOfficeNewFile') },
          { key: 'Ctrl+O', desc: this.$t('index.scOfficeOpenFile') },
          { key: 'Ctrl+S', desc: this.$t('index.scOfficeSave') },
          { key: 'Ctrl+Shift+S', desc: this.$t('index.scOfficeSaveAs') },
          { key: 'Ctrl+P', desc: this.$t('index.scOfficePrint') },
          { key: 'Ctrl+W', desc: this.$t('index.scOfficeCloseFile') },
          { key: 'Ctrl+Z', desc: this.$t('index.scOfficeUndo') },
          { key: 'Ctrl+Y', desc: this.$t('index.scOfficeRedo') },
          { key: 'Ctrl+F', desc: this.$t('index.scOfficeFind') },
          { key: 'Ctrl+H', desc: this.$t('index.scOfficeReplace') },
        ]},
        { name: this.$t('index.scGroupOfficeWord'), items: [
          { key: 'Ctrl+N', desc: this.$t('index.scWordNewDoc') },
          { key: 'Ctrl+O', desc: this.$t('index.scWordOpenDoc') },
          { key: 'F12',    desc: this.$t('index.scWordSaveAs') },
          { key: 'Shift+F4', desc: this.$t('index.scWordFindNext') },
          { key: 'Ctrl+G', desc: this.$t('index.scWordGoToPage') },
          { key: 'Ctrl+K', desc: this.$t('index.scWordInsertLink') },
          { key: 'Ctrl+Enter', desc: this.$t('index.scWordInsertPageBreak') },
          { key: 'Ctrl+E', desc: this.$t('index.scWordCenterAlign') },
          { key: 'Ctrl+L', desc: this.$t('index.scWordLeftAlign') },
          { key: 'Ctrl+R', desc: this.$t('index.scWordRightAlign') },
          { key: 'Ctrl+J', desc: this.$t('index.scWordJustifyAlign') },
          { key: 'Ctrl+5', desc: this.$t('index.scWord1_5Line') },
          { key: 'Ctrl+B', desc: this.$t('index.scWordBold') },
          { key: 'Ctrl+U', desc: this.$t('index.scWordUnderline') },
          { key: 'Ctrl+I', desc: this.$t('index.scWordItalic') },
        ]},
        { name: this.$t('index.scGroupOfficeExcel'), items: [
          { key: 'Alt + =',   desc: this.$t('index.scExcelSumAuto') },
          { key: 'Ctrl + R',  desc: this.$t('index.scExcelFillRight') },
          { key: 'Ctrl + D',  desc: this.$t('index.scExcelFillDown') },
          { key: 'Ctrl+Shift+V', desc: this.$t('index.scExcelPasteValue') },
          { key: 'Ctrl+Shift+C', desc: this.$t('index.scExcelPasteFormat') },
          { key: 'Ctrl+Shift++', desc: this.$t('index.scExcelInsertRow') },
          { key: 'Ctrl + -',  desc: this.$t('index.scExcelDeleteRow') },
          { key: 'Ctrl+Shift+L', desc: this.$t('index.scExcelToggleFilter') },
          { key: 'F5',        desc: this.$t('index.scExcelGoToCell') },
          { key: 'Ctrl+F3',   desc: this.$t('index.scExcelNameBox') },
          { key: 'Ctrl + ;',  desc: this.$t('index.scExcelTodayDate') },
          { key: 'Ctrl+Shift+: ', desc: this.$t('index.scExcelNowTime') },
          { key: 'Ctrl + A / Ctrl+*', desc: this.$t('index.scExcelSelectAllRegion') },
          { key: 'F2',        desc: this.$t('index.scExcelEditCell') },
          { key: 'Ctrl+PageDown', desc: this.$t('index.scExcelSwitchSheet') },
        ]},
      ];
    }
  },
  mounted (){
    this.getCommonData();    
    this.videoUrl=videoFile;
    document.title = this.$t('index.pageTitle');
    document.documentElement.lang = this.$i18n.locale === 'en' ? 'en' : 'zh-CN';
  },
  data(){
    return{
      v5version:false,
      v7version:false,
      oldversion:false,
      v62Protocol:false,
      v62Status:0,
      savingV62:false,
      commitSent:false,
      deviceType:'',
      deviceVersion:'',
      writer:{},
      port:{},
      reader:{},
      sendLabel:true,
      sendText:this.$t('index.connFirst'),
      conButton:this.$t('index.connButtonText1'),
      conButtonType:"primary",
      key:0xff,
      keyfunc:0xff,
      keyf:0xff,
      keystr:'',
      keymix:[0xff,0xff,0xff,0xff,0xff],
      triggerMode:0,
      timerHour:0,
      timerMinute:0,
      timerSecond:0,
      b3key1:0xff,
      b3key2:0xff,
      b3key3:0xff,
      b3keyall:0xff,
      b3key1func:0xff,
      b3key2func:0xff,
      b3key3func:0xff,
      b3keyallfunc:0xff,
      b3key1f:0xff,
      b3key2f:0xff,
      b3key3f:0xff,
      b3keyallf:0xff,
      b3key1mix:[0xff,0xff,0xff,0xff,0xff],
      b3key2mix:[0xff,0xff,0xff,0xff,0xff],
      b3key3mix:[0xff,0xff,0xff,0xff,0xff],
      b3keyallmix:[0xff,0xff,0xff,0xff,0xff],
      b3custStr1:'',
      b3custStr2:'',
      b3custStr3:'',
      keyallmode:0,
      backlightMode:0,
      backlightColor1:1,
      backlightColor2:1,
      backlightColor3:1,
      backlightBrightness:128,
      selectedB3Key:0,
      compactKeyboardHiddenMediaValues:[0x18A,0x223,0x224,0x225,0x227,0x22A,0x30,0x32,0x19E],
      b3KeyDesc1:'', b3KeyDesc2:'', b3KeyDesc3:'',
      showVideoModal:false,
      showKeyTester:false,
      showMainHelp:false,
      showMoreMenu:false,
      rightTipClosed:false,
      deviceConnecting:false,
      serialPortSelected:false,
      e6KeyOpen:[false,false,false,false,false,false,false,false,false],
      buildTime: typeof __BUILD_TIME__ !== 'undefined' ? __BUILD_TIME__ : '',
      showRf24Pair:false,
      showFactoryTest:false,
      factoryData:null,
      factoryRaw:'',
      factoryError:'',
      factoryPressedMask:0,
      factoryAuto:true,
      factoryTimer:null,
      factoryReading:false,
      videoUrl:'',
      // C4
      c4key1:0xff, c4key2:0xff, c4key3:0xff, c4key4:0xff,
      c4key1func:0xff, c4key2func:0xff, c4key3func:0xff, c4key4func:0xff,
      c4key1f:0xff, c4key2f:0xff, c4key3f:0xff, c4key4f:0xff,
      c4key1mix:[0xff,0xff,0xff,0xff,0xff],
      c4key2mix:[0xff,0xff,0xff,0xff,0xff],
      c4key3mix:[0xff,0xff,0xff,0xff,0xff],
      c4key4mix:[0xff,0xff,0xff,0xff,0xff],
      c4custStr1:'', c4custStr2:'', c4custStr3:'', c4custStr4:'',
      ec11CW_func:0, ec11CW_key:0xff, ec11CW_keyChar:'', ec11CW_keyF:0xff,
      ec11CW_mediaVal:0xE2, ec11CW_mouseBtn:1, ec11CW_mouseMode:0, ec11CW_fnVal:0xC2, ec11CW_scrollDir:0,
      ec11CW_keyMix:[0xff,0xff,0xff,0xff,0xff],
      ec11CW_mixCount:0,
      ec11CW_mixCountArray:[false,false,false,false,false],
      ec11CW_mixShowArray:[false,false,false,false,false],
      ec11CW_mixFuncSelectValue:['','','','',''],
      ec11CW_mixText:['','','','',''],
      ec11CW_mixTips:'',
      ec11CCW_func:0, ec11CCW_key:0xff, ec11CCW_keyChar:'', ec11CCW_keyF:0xff,
      ec11CCW_mediaVal:0xE9, ec11CCW_mouseBtn:1, ec11CCW_mouseMode:0, ec11CCW_fnVal:0xC2, ec11CCW_scrollDir:1,
      ec11CCW_keyMix:[0xff,0xff,0xff,0xff,0xff],
      ec11CCW_mixCount:0,
      ec11CCW_mixCountArray:[false,false,false,false,false],
      ec11CCW_mixShowArray:[false,false,false,false,false],
      ec11CCW_mixFuncSelectValue:['','','','',''],
      ec11CCW_mixText:['','','','',''],
      ec11CCW_mixTips:'',
      ec11Btn_func:0, ec11Btn_key:0xff, ec11Btn_keyChar:'', ec11Btn_keyF:0xff,
      ec11Btn_mediaVal:0xCD, ec11Btn_mouseBtn:1, ec11Btn_mouseMode:0, ec11Btn_fnVal:0xC2, ec11Btn_scrollDir:0,
      ec11Btn_keyMix:[0xff,0xff,0xff,0xff,0xff],
      ec11Btn_mixCount:0,
      ec11Btn_mixCountArray:[false,false,false,false,false],
      ec11Btn_mixShowArray:[false,false,false,false,false],
      ec11Btn_mixFuncSelectValue:['','','','',''],
      ec11Btn_mixText:['','','','',''],
      ec11Btn_mixTips:'',
      backlightModeC4:0,
      backlightColorC4_1:1, backlightColorC4_2:1,
      backlightColorC4_3:1, backlightColorC4_4:1,
      backlightBrightnessC4:128,
      // C4 Key Descriptions
      c4KeyDesc1:'', c4KeyDesc2:'', c4KeyDesc3:'', c4KeyDesc4:'',
      selectedC4Key:0,
      // B6 Key Descriptions
      b6KeyDesc1:'', b6KeyDesc2:'', b6KeyDesc3:'', b6KeyDesc4:'', b6KeyDesc5:'', b6KeyDesc6:'',
      selectedB6Key:0,
      b6key1:0xff, b6key2:0xff, b6key3:0xff, b6key4:0xff, b6key5:0xff, b6key6:0xff,
      b6key1func:0xff, b6key2func:0xff, b6key3func:0xff, b6key4func:0xff, b6key5func:0xff, b6key6func:0xff,
      b6key1f:0xff, b6key2f:0xff, b6key3f:0xff, b6key4f:0xff, b6key5f:0xff, b6key6f:0xff,
      b6key1mix:[0xff,0xff,0xff,0xff,0xff],
      b6key2mix:[0xff,0xff,0xff,0xff,0xff],
      b6key3mix:[0xff,0xff,0xff,0xff,0xff],
      b6key4mix:[0xff,0xff,0xff,0xff,0xff],
      b6key5mix:[0xff,0xff,0xff,0xff,0xff],
      b6key6mix:[0xff,0xff,0xff,0xff,0xff],
      b6custStr1:'', b6custStr2:'', b6custStr3:'', b6custStr4:'', b6custStr5:'', b6custStr6:'',
      backlightModeB6:0,
      backlightColorB6_1:1, backlightColorB6_2:1, backlightColorB6_3:1,
      backlightColorB6_4:1, backlightColorB6_5:1, backlightColorB6_6:1,
      backlightBrightnessB6:128,
      // D2
      d2key1:0xff, d2key2:0xff,
      d2key1func:0xff, d2key2func:0xff,
      d2key1f:0xff, d2key2f:0xff,
      d2key1mix:[0xff,0xff,0xff,0xff,0xff],
      d2key2mix:[0xff,0xff,0xff,0xff,0xff],
      d2custStr1:'', d2custStr2:'',
      d2RunMode:0,
      d2PageAction:0,
      // B4
      b4key1:0xff, b4key2:0xff, b4key3:0xff, b4key4:0xff,
      b4key1func:0xff, b4key2func:0xff, b4key3func:0xff, b4key4func:0xff,
      b4key1f:0xff, b4key2f:0xff, b4key3f:0xff, b4key4f:0xff,
      b4key1mix:[0xff,0xff,0xff,0xff,0xff],
      b4key2mix:[0xff,0xff,0xff,0xff,0xff],
      b4key3mix:[0xff,0xff,0xff,0xff,0xff],
      b4key4mix:[0xff,0xff,0xff,0xff,0xff],
      b4custStr1:'', b4custStr2:'', b4custStr3:'', b4custStr4:'',
      backlightModeB4:0,
      backlightColorB4_1:1, backlightColorB4_2:1,
      backlightColorB4_3:1, backlightColorB4_4:1,
      backlightBrightnessB4:128,
      selectedB4Key:0,
      b4KeyDesc1:'', b4KeyDesc2:'', b4KeyDesc3:'', b4KeyDesc4:'',
      // E6 (三模键盘 6键)
      e6key1:0xff, e6key2:0xff, e6key3:0xff, e6key4:0xff, e6key5:0xff, e6key6:0xff,
      e6key7:0xff, e6key8:0xff, e6key9:0xff,
      e6key1func:0xff, e6key2func:0xff, e6key3func:0xff, e6key4func:0xff, e6key5func:0xff, e6key6func:0xff,
      e6key7func:0xff, e6key8func:0xff, e6key9func:0xff,
      e6key1f:0xff, e6key2f:0xff, e6key3f:0xff, e6key4f:0xff, e6key5f:0xff, e6key6f:0xff,
      e6key7f:0xff, e6key8f:0xff, e6key9f:0xff,
      e6key1mix:[0xff,0xff,0xff,0xff,0xff],
      e6key2mix:[0xff,0xff,0xff,0xff,0xff],
      e6key3mix:[0xff,0xff,0xff,0xff,0xff],
      e6key4mix:[0xff,0xff,0xff,0xff,0xff],
      e6key5mix:[0xff,0xff,0xff,0xff,0xff],
      e6key6mix:[0xff,0xff,0xff,0xff,0xff],
      e6key7mix:[0xff,0xff,0xff,0xff,0xff],
      e6key8mix:[0xff,0xff,0xff,0xff,0xff],
      e6key9mix:[0xff,0xff,0xff,0xff,0xff],
      e6custStr1:'', e6custStr2:'', e6custStr3:'', e6custStr4:'', e6custStr5:'', e6custStr6:'',
      e6custStr7:'', e6custStr8:'', e6custStr9:'',
      e6RunMode:0,
      e6PageAction:0,
      e6DeviceMode:0x24,
      e6DeviceModeAtLoad:null,
      e6TimeSeq:0,
      selectedE6Key:0,
      e6KeyDesc1:'', e6KeyDesc2:'', e6KeyDesc3:'', e6KeyDesc4:'', e6KeyDesc5:'', e6KeyDesc6:'',
      e6KeyDesc7:'', e6KeyDesc8:'', e6KeyDesc9:''
    }
  }
}
class LineBreakTransformer {
    constructor() {
        // 保存流数据直到新行出现的容器
        this.container = "";
    }

    transform(chunk, controller) {
        // 将新块追加到现有块。
        this.container += chunk;
        // console.log(this.container);
// ACK 也是一行文本；必须等 CRLF 完整到达，不能提前消费单独的 "1"，
        // 否则后续 CRLF 会作为空行留给下一次保存。
        const lines = this.container.split("\r\n");
        this.container = lines.pop();
        lines.forEach((line) => controller.enqueue(line));
    }

    flush(controller) {
        // 当流关闭时，清除所有剩余的块。
        controller.enqueue(this.container);
    }
}
</script>

<style scoped>
#app {
  font-family: 'Segoe UI', Helvetica, sans-serif;
  text-align: center;
  background: #f0f2f5;
  min-height: 100vh;
  padding: 20px 40px;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  gap: 24px;
}
.main-card {
  position: relative;
  max-width: 900px;
  flex: 1;
  padding: 30px 40px;
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 2px 16px rgba(0,0,0,0.08);
}
.main-help-btn {
  position: absolute;
  top: 16px;
  right: 18px;
  z-index: 20;
  width: 34px;
  height: 34px;
  padding: 0;
  font-size: 18px;
  font-weight: 700;
  color: #909399;
  background: #fff;
  border-color: #dcdfe6;
  box-shadow: 0 1px 4px rgba(0,0,0,0.08);
}
.main-help-btn:hover {
  color: #409EFF;
  border-color: #409EFF;
}
.main-help-menu {
  padding: 2px 0;
}
.main-help-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 9px 12px;
  border-radius: 6px;
  color: #303133;
  font-size: 14px;
  cursor: pointer;
}
.main-help-item:hover {
  color: #409EFF;
  background: #ecf5ff;
}
.connect-loading-mask {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 3000;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.35);
}
.connect-loading-box {
  min-width: 270px;
  padding: 26px 30px;
  background: #fff;
  border-radius: 14px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.25);
  text-align: center;
}
.connect-spinner {
  width: 44px;
  height: 44px;
  margin: 0 auto 14px;
  border: 4px solid #d9ecff;
  border-top-color: #409EFF;
  border-radius: 50%;
  animation: connect-spin 0.8s linear infinite;
}
@keyframes connect-spin {
  to { transform: rotate(360deg); }
}
.connect-loading-title {
  font-size: 18px;
  font-weight: 700;
  color: #303133;
}
.connect-loading-prompt {
  max-width: 300px;
  font-size: 18px;
  font-weight: 600;
  line-height: 1.7;
  color: #303133;
}
.connection-tip-row {
  margin: 0 0 18px;
  padding: 10px 14px;
  border-top: 1px solid #ebeef5;
  border-bottom: 1px solid #ebeef5;
  background: #fff9e6;
  color: #8a7340;
  font-size: 13px;
  line-height: 1.7;
  text-align: center;
}
.connection-tip-row i {
  margin-right: 4px;
  color: #E6A23C;
}
.connection-tip-row strong {
  color: #D4880F;
}
.main-help-back {
  margin-bottom: 4px;
  padding: 6px 12px;
  border-bottom: 1px solid #ebeef5;
  color: #909399;
  font-size: 12px;
  cursor: pointer;
}
.main-help-back:hover {
  color: #409EFF;
}
.sidebar-card {
  width: 270px;
  flex-shrink: 0;
  background: linear-gradient(135deg, #ffffff, #fafbff);
  border-radius: 18px;
  padding: 22px;
  box-shadow: 0 4px 24px rgba(64,158,255,0.10);
  font-size: 14px;
  position: sticky;
  top: 20px;
  max-height: calc(100vh - 40px);
  overflow-y: auto;
  text-align: left;
  border: 1px solid #e0e8f5;
}
.right-tip-bar {
  position: fixed;
  right: 14px;
  top: 50%;
  transform: translateY(-50%);
  background: linear-gradient(180deg, #fff9e6, #fff5d6);
  border-radius: 12px;
  padding: 16px 18px;
  box-shadow: 0 4px 16px rgba(212, 136, 15, 0.20);
  border: 1px solid #f5e6b8;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  z-index: 1000;
  width: 210px;
  max-width: 210px;
  animation: slideInRight 0.3s ease-out;
}
@keyframes slideInRight {
  from {
    right: -70px;
    opacity: 0;
  }
  to {
    right: 14px;
    opacity: 1;
  }
}
.right-tip-bar .tip-icon {
  width: 36px;
  height: 36px;
  background: linear-gradient(135deg, #FFB800, #FF9500);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 18px;
  box-shadow: 0 2px 8px rgba(255, 184, 0, 0.4);
  flex-shrink: 0;
}
.right-tip-bar .tip-content {
  text-align: left;
  opacity: 1;
}
.right-tip-bar .tip-title {
  font-weight: bold;
  color: #D4880F;
  font-size: 15px;
  margin-bottom: 6px;
}
.right-tip-bar .tip-desc {
  color: #8a7340;
  font-size: 14px;
  line-height: 1.6;
  white-space: normal;
}
.right-tip-close {
  position: absolute;
  top: 4px;
  right: 4px;
  width: 30px;
  height: 30px;
  padding: 0;
  border: 1px solid rgba(212, 136, 15, 0.35);
  border-radius: 50%;
  background: #fff7df;
  color: #C07800;
  font-size: 24px;
  font-weight: bold;
  line-height: 26px;
  cursor: pointer;
  z-index: 1;
}
.right-tip-close:hover {
  background: #FFE7B0;
  color: #B35C00;
  border-color: #D4880F;
}
.success-message {
  min-width: 280px !important;
  padding: 20px 28px !important;
}
.success-message .el-message__content {
  font-size: 16px !important;
  font-weight: bold !important;
  color: #67C23A !important;
}
.success-message .el-message__icon {
  font-size: 32px !important;
}
.change-language {
  display: flex;
  justify-content: center;
  align-items: center;
}
.el-row {
  margin-bottom: 18px;
}
img {
  width: 500px;
}
.light {
  color: #409EFF;
}
.normal {
  color: #606266;
}
.el-button {
  border-radius: 8px;
}
.el-button--primary {
  border-radius: 10px;
}
.backlight-card {
  margin-bottom: 20px;
  padding: 20px;
  background: #fafafa;
  border: 1px solid #ebeef5;
  border-radius: 12px;
}
.key-card {
  margin-bottom: 16px;
  padding: 16px 20px;
  background: #ffffff;
  border: 1px solid #e4e7ed;
  border-radius: 10px;
  box-shadow: 0 1px 4px rgba(0,0,0,0.04);
}
.e6-collapse-card {
  padding: 0;
  overflow: hidden;
}
.e6-collapse-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 18px;
  background: #fafcff;
  cursor: pointer;
  user-select: none;
}
.e6-collapse-head:hover {
  background: #ecf5ff;
}
.e6-collapse-left {
  display: flex;
  align-items: center;
  gap: 14px;
  min-width: 0;
  text-align: left;
}
.e6-collapse-name {
  flex-shrink: 0;
  color: #303133;
  font-size: 15px;
  font-weight: 700;
}
.e6-collapse-value {
  min-width: 0;
  overflow: hidden;
  color: #409EFF;
  font-size: 16px;
  font-weight: 600;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.e6-collapse-head i {
  flex-shrink: 0;
  color: #409EFF;
  font-size: 18px;
  font-weight: 700;
}
.e6-collapse-body {
  padding: 16px 20px;
  border-top: 1px solid #ebeef5;
}

/* ============ D2 Bluetooth Mode Card ============ */
.d2-mode-card {
  margin-bottom: 22px;
  padding: 22px 26px;
  background: linear-gradient(135deg, #ECF5FF 0%, #F5F7FA 55%, #FFF6EC 100%);
  border: 1px solid #D9ECFF;
  border-radius: 14px;
  box-shadow: 0 2px 8px rgba(64,158,255,0.06);
}
.d2-mode-title {
  margin: 0 0 18px 0;
  font-size: 18px;
  font-weight: 700;
  color: #2B68CB;
  letter-spacing: 0.5px;
  display: flex;
  align-items: center;
  gap: 8px;
}
.d2-mode-title i {
  font-size: 22px;
  color: #409EFF;
}
.d2-mode-tip {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin: -6px 0 16px 0;
  padding: 10px 14px;
  background: #FFF7E6;
  border: 1px solid #FFE1B3;
  border-radius: 8px;
  color: #8A5A00;
  font-size: 13px;
  line-height: 1.7;
}
.d2-mode-tip i {
  flex-shrink: 0;
  margin-top: 2px;
  font-size: 15px;
  color: #E6A23C;
}
.d2-mode-tip-body {
  flex: 1;
  min-width: 0;
}
.d2-mode-tip-line {
  display: flex;
  align-items: flex-start;
  gap: 2px;
}
.d2-mode-tip-line + .d2-mode-tip-line {
  margin-top: 4px;
}
.d2-mode-tip-key {
  flex-shrink: 0;
  font-weight: 700;
  color: #7A4E00;
}
.d2-field-row {
  display: flex;
  align-items: center;
  margin-bottom: 16px;
  gap: 14px;
}
.d2-field-label {
  flex-shrink: 0;
  min-width: 88px;
  font-size: 14px;
  font-weight: 600;
  color: #303133;
  line-height: 40px;
}
.d2-page-label {
  align-self: flex-start;
  line-height: 44px;
}
.d2-run-mode-group .el-radio {
  font-size: 14px;
  margin-right: 22px;
  padding: 6px 14px;
  border: 1px solid transparent;
  border-radius: 8px;
  transition: all .2s ease;
}
.d2-run-mode-group .el-radio:hover {
  background: rgba(64,158,255,0.06);
  border-color: #C6E2FF;
}

/* Page Action: 4 rows 1 col */
.d2-page-action-group {
  display: flex;
  flex-direction: column;
  gap: 10px;
  flex: 1;
  max-width: 560px;
}
.d2-page-action-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 14px;
  background: #ffffff;
  border: 1px solid #EBEEF5;
  border-radius: 10px;
  box-shadow: 0 1px 2px rgba(0,0,0,0.02);
  transition: all .2s ease;
}
.d2-page-action-item:hover {
  border-color: #C6E2FF;
  box-shadow: 0 3px 10px rgba(64,158,255,0.08);
  transform: translateY(-1px);
}
.d2-pa-index {
  flex-shrink: 0;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  color: #fff;
  font-size: 14px;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.d2-page-action-item:nth-child(1) .d2-pa-index { background: #409EFF; }
.d2-page-action-item:nth-child(2) .d2-pa-index { background: #67C23A; }
.d2-page-action-item:nth-child(3) .d2-pa-index { background: #E6A23C; }
.d2-page-action-item:nth-child(4) .d2-pa-index { background: #909399; }
.d2-pa-name {
  font-weight: 600;
  color: #303133;
  font-size: 14px;
}
.d2-page-action-group .el-radio {
  margin-right: 0;
  display: inline-flex;
  align-items: center;
}

/* Subtitle */
.d2-subtitle {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 15px;
  font-weight: 700;
  color: #303133;
  margin-bottom: 12px;
  padding: 4px 12px;
  border-left: 4px solid #409EFF;
  background: rgba(64,158,255,0.05);
  border-radius: 0 6px 6px 0;
}
.d2-subtitle i {
  color: #409EFF;
  font-size: 16px;
}

/* Firmware Table */
.d2-firm-block {
  margin-top: 24px;
}
.d2-firm-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  border-radius: 10px;
  overflow: hidden;
  border: 1px solid #EBEEF5;
  box-shadow: 0 1px 4px rgba(0,0,0,0.03);
  margin-bottom: 20px;
  font-size: 13px;
  background: #fff;
}
.d2-firm-table thead tr {
  background: linear-gradient(90deg, #409EFF 0%, #66B1FF 100%);
}
.d2-firm-table th {
  padding: 11px 14px;
  color: #ffffff;
  font-weight: 600;
  text-align: left;
  letter-spacing: 0.3px;
}
.d2-firm-table th i {
  color: #fff;
}
.d2-firm-table td {
  padding: 11px 14px;
  color: #606266;
  border-bottom: 1px solid #F2F6FC;
  line-height: 1.6;
}
.d2-firm-table tbody tr:last-child td {
  border-bottom: none;
}
.d2-firm-table tbody tr:nth-child(even) {
  background: #FAFBFC;
}
.d2-firm-table tbody tr:hover {
  background: #ECF5FF !important;
  transition: background .15s ease;
}
.d2-fw-id {
  font-weight: 700;
  font-size: 13px;
  letter-spacing: 0.5px;
}
.d2-fw-1 { color: #409EFF; }
.d2-fw-2 { color: #67C23A; }
.d2-fw-3 { color: #E6A23C; }
.d2-fw-4 { color: #909399; }

/* Notes Block */
.d2-notes-block {
  margin-top: 8px;
}
.d2-notes-block > .d2-subtitle {
  border-left-color: #E6A23C;
  background: rgba(230,162,60,0.06);
}
.d2-notes-block > .d2-subtitle i {
  color: #E6A23C;
}
.d2-note-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px 14px;
  border-radius: 10px;
  border-left: 4px solid;
  margin-bottom: 10px;
  font-size: 13px;
  line-height: 1.65;
  color: #606266;
}
.d2-note-item i {
  flex-shrink: 0;
  margin-top: 2px;
  font-size: 18px;
}
.d2-note-1 {
  background: #ECF5FF;
  border-left-color: #409EFF;
}
.d2-note-1 i { color: #409EFF; }
.d2-note-2 {
  background: #F0F9EB;
  border-left-color: #67C23A;
}
.d2-note-2 i { color: #67C23A; }
.d2-note-3 {
  background: #FDF6EC;
  border-left-color: #E6A23C;
}
.d2-note-3 i { color: #E6A23C; }
.d2-note-body {
  flex: 1;
}

/* Responsive */
@media (max-width: 720px) {
  .d2-mode-card { padding: 18px 16px; }
  .d2-field-row { flex-direction: column; align-items: flex-start; gap: 4px; }
  .d2-page-label { line-height: 22px; }
  .d2-field-label { line-height: 24px; }
  .d2-firm-table th, .d2-firm-table td { padding: 9px 10px; font-size: 12px; }
}

/* ============ B3 Key Schematic Card ============ */
.b3-key-schematic {
  margin-bottom: 22px;
  padding: 24px 28px;
  background: #F5F5F5;
  border-radius: 16px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.06);
  position: relative;
  overflow: hidden;
}
.b3-key-schematic::before {
  content: '';
  position: absolute;
  top: -20%; right: -10%;
  width: 80px; height: 80px;
  background: rgba(0,0,0,0.03);
  border-radius: 50%;
}
.b3-key-schematic::after {
  content: '';
  position: absolute;
  bottom: -15%; left: -8%;
  width: 60px; height: 60px;
  background: rgba(0,0,0,0.02);
  border-radius: 50%;
}
.b3-sch-title {
  margin: 0 0 20px 0;
  font-size: 18px;
  font-weight: 700;
  color: #303133;
  letter-spacing: 0.5px;
  display: flex;
  align-items: center;
  gap: 8px;
  position: relative;
  z-index: 1;
}
.b3-sch-title i {
  font-size: 22px;
}
.b3-sch-grid {
  display: flex;
  gap: 16px;
  justify-content: center;
  position: relative;
  z-index: 1;
}
.b3-sch-key {
  flex: 1;
  min-width: 140px;
  max-width: 180px;
  padding: 22px 16px 24px 16px;
  background: rgba(255,255,255,0.95);
  border-radius: 14px;
  cursor: pointer;
  transition: all .25s cubic-bezier(.4,0,.2,1);
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  box-shadow: 0 2px 8px rgba(0,0,0,0.08);
  border: 5px solid var(--backlight-color, #C0C4CC);
  box-sizing: border-box;
}
.b3-sch-key::before {
  content: '';
  position: absolute;
  top: -8px; left: -8px; right: -8px; bottom: -8px;
  border-radius: 19px;
  border: 4px solid var(--backlight-color, #C0C4CC);
  opacity: 0.6;
  pointer-events: none;
  transition: opacity .25s;
}
.b3-sch-key:hover::before {
  opacity: 0.9;
}
.b3-sch-key:hover {
  transform: translateY(-4px) scale(1.02);
  box-shadow: 0 8px 24px rgba(102,126,234,0.25), 0 0 24px var(--backlight-color, #C0C4CC);
}
.b3-sch-key:active {
  transform: translateY(-2px) scale(0.98);
}
.b3-sch-active {
  background: linear-gradient(135deg, #409EFF 0%, #66B1FF 100%) !important;
  box-shadow: 0 6px 20px rgba(64,158,255,0.35) !important;
}
.b3-sch-active .b3-sch-key-num,
.b3-sch-active .b3-sch-key-label,
.b3-sch-active .b3-sch-key-value {
  color: #ffffff !important;
}
.b3-sch-active .b3-sch-key-num {
  background: rgba(255,255,255,0.25) !important;
  border-color: rgba(255,255,255,0.4) !important;
}
.b3-sch-key-num {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: linear-gradient(135deg, #667EEA 0%, #764BA2 100%);
  color: #ffffff;
  font-size: 20px;
  font-weight: 800;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 10px;
  border: 3px solid rgba(102,126,234,0.15);
  box-shadow: 0 3px 8px rgba(102,126,234,0.2);
}
.b3-sch-key-label {
  font-size: 14px;
  font-weight: 600;
  color: #303133;
  margin-bottom: 6px;
}
.b3-sch-key-value {
  font-size: 13px;
  font-weight: 500;
  color: #909399;
  text-align: center;
  line-height: 1.5;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.b3-sch-active .b3-sch-key-value {
  font-weight: 600;
}

/* ============ Main Header & Video Tutorial ============ */
.main-header {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 16px;
  margin-bottom: 20px;
  padding: 0 10px;
}
.main-header h1 {
  width: 100%;
  margin: 0;
  text-align: center;
}
.main-header-actions {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 10px;
}
.main-header-actions .el-button + .el-button {
  margin-left: 0;
}
.key-test-modal .el-dialog__body {
  padding-top: 8px;
}
.video-tutorial-btn {
  font-weight: 700 !important;
  font-size: 16px !important;
  padding: 10px 22px !important;
  min-width: 130px;
  border-radius: 8px !important;
  background: linear-gradient(135deg, #E6A23C 0%, #EEBE77 100%) !important;
  border: 1.5px solid #F5DAB9 !important;
  color: #FFFFFF !important;
  box-shadow: 0 3px 10px rgba(230,162,60,0.3) !important;
  letter-spacing: 0.5px;
  transition: all .25s ease;
}
.video-tutorial-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 5px 16px rgba(230,162,60,0.4) !important;
}
.video-tutorial-btn:active {
  transform: translateY(0);
}
.video-modal .el-dialog__header {
  background: linear-gradient(135deg, #667EEA 0%, #764BA2 100%);
  padding: 20px 24px;
}
.video-modal .el-dialog__title {
  color: #FFFFFF;
  font-size: 18px;
  font-weight: 700;
}
.video-modal .el-dialog__headerbtn .el-dialog__close {
  color: rgba(255,255,255,0.8);
  font-size: 20px;
}
.video-modal .el-dialog__body {
  padding: 0;
}
.video-container {
  width: 100%;
  background: #000000;
  border-radius: 8px;
  overflow: hidden;
}
.video-player {
  width: 100%;
  height: 450px;
  display: block;
}
@media (max-width: 768px) {
  .main-header {
    flex-direction: column;
    gap: 12px;
  }
  .main-header-actions {
    flex-wrap: wrap;
    justify-content: center;
  }
  .key-test-modal {
    width: 96vw !important;
  }
  .video-modal {
    width: 95vw !important;
  }
  .video-player {
    height: 300px;
  }
}

/* ============ C4 Key Schematic Card ============ */
.c4-key-schematic {
  margin-bottom: 22px;
  padding: 24px 28px;
  background: #F5F5F5;
  border-radius: 16px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.06);
  position: relative;
  overflow: hidden;
}
.c4-key-schematic::before {
  content: '';
  position: absolute;
  top: -20%; right: -10%;
  width: 80px; height: 80px;
  background: rgba(0,0,0,0.03);
  border-radius: 50%;
}
.c4-key-schematic::after {
  content: '';
  position: absolute;
  bottom: -15%; left: -8%;
  width: 60px; height: 60px;
  background: rgba(0,0,0,0.02);
  border-radius: 50%;
}
.c4-sch-title {
  margin: 0 0 20px 0;
  font-size: 18px;
  font-weight: 700;
  color: #303133;
  letter-spacing: 0.5px;
  display: flex;
  align-items: center;
  gap: 8px;
  position: relative;
  z-index: 1;
}
.c4-sch-layout {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 30px;
  position: relative;
  z-index: 1;
}
.c4-sch-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
}
.c4-sch-key {
  min-width: 120px;
  max-width: 150px;
  padding: 18px 14px 20px 14px;
  background: rgba(255,255,255,0.95);
  border-radius: 14px;
  cursor: pointer;
  transition: all .25s cubic-bezier(.4,0,.2,1);
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  box-shadow: 0 2px 8px rgba(0,0,0,0.08);
  border: 5px solid var(--backlight-color, #C0C4CC);
  box-sizing: border-box;
}
.c4-sch-key::before {
  content: '';
  position: absolute;
  top: -8px; left: -8px; right: -8px; bottom: -8px;
  border-radius: 19px;
  border: 4px solid var(--backlight-color, #C0C4CC);
  opacity: 0.6;
  pointer-events: none;
  transition: opacity .25s;
}
.c4-sch-key:hover::before {
  opacity: 0.9;
}
.c4-sch-key:hover {
  transform: translateY(-4px) scale(1.02);
  box-shadow: 0 8px 24px rgba(102,126,234,0.25), 0 0 24px var(--backlight-color, #C0C4CC);
}
.c4-sch-key.c4-sch-active {
  background: linear-gradient(135deg, #409EFF 0%, #66B1FF 100%) !important;
  color: #ffffff !important;
  border-color: #409EFF !important;
}
.c4-sch-key.c4-sch-active::before {
  border-color: rgba(255,255,255,0.5) !important;
}
.c4-sch-key-num {
  width: 36px; height: 36px;
  border-radius: 50%;
  background: linear-gradient(135deg, #409EFF 0%, #66B1FF 100%);
  color: #ffffff;
  font-size: 16px;
  font-weight: 800;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 8px;
  box-shadow: 0 3px 8px rgba(64,158,255,0.3);
}
.c4-sch-key.c4-sch-active .c4-sch-key-num {
  background: rgba(255,255,255,0.3) !important;
}
.c4-sch-key-label {
  font-size: 13px;
  color: #606266;
  margin-bottom: 6px;
  font-weight: 500;
}
.c4-sch-key.c4-sch-active .c4-sch-key-label {
  color: rgba(255,255,255,0.9) !important;
}
.c4-sch-key-value {
  font-size: 12px;
  color: #909399;
  text-align: center;
  line-height: 1.5;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.c4-sch-key.c4-sch-active .c4-sch-key-value {
  color: #ffffff !important;
  font-weight: 600;
}
.c4-sch-knob-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}
.c4-sch-knob-row {
  display: flex;
  align-items: center;
  gap: 12px;
}
.c4-sch-knob-arrow {
  width: 100px;
  height: 80px;
  background: transparent;
  cursor: pointer;
  transition: all .25s cubic-bezier(.4,0,.2,1);
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
}
.c4-sch-knob-arrow::before {
  content: '';
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 0;
  height: 0;
  border-top: 28px solid transparent;
  border-bottom: 28px solid transparent;
}
.c4-sch-knob-arrow.left::before {
  border-right: 40px solid rgba(255,255,255,0.95);
}
.c4-sch-knob-arrow.right::before {
  border-left: 40px solid rgba(255,255,255,0.95);
}
.c4-sch-knob-arrow::after {
  content: '';
  position: absolute;
  top: 2px;
  left: 50%;
  transform: translateX(-50%);
  width: 0;
  height: 0;
  border-top: 24px solid transparent;
  border-bottom: 24px solid transparent;
}
.c4-sch-knob-arrow.left::after {
  border-right: 35px solid #909399;
}
.c4-sch-knob-arrow.right::after {
  border-left: 35px solid #909399;
}
.c4-sch-knob-arrow:hover {
  transform: scale(1.08);
}
.c4-sch-knob-arrow:hover::after {
  border-right-color: #409EFF;
}
.c4-sch-knob-arrow.right:hover::after {
  border-left-color: #409EFF;
}
.c4-sch-knob-arrow.c4-sch-active.left::before {
  border-right-color: #409EFF;
}
.c4-sch-knob-arrow.c4-sch-active.right::before {
  border-left-color: #409EFF;
}
.c4-sch-knob-arrow.c4-sch-active.left::after {
  border-right-color: #ffffff;
}
.c4-sch-knob-arrow.c4-sch-active.right::after {
  border-left-color: #ffffff;
}
.c4-sch-knob-arrow-value {
  font-size: 11px;
  color: #909399;
  text-align: center;
  margin-top: 60px;
  max-width: 100px;
  overflow: hidden;
  line-height: 1.4;
  z-index: 1;
}
.c4-sch-knob-arrow.c4-sch-active .c4-sch-knob-arrow-value {
  color: #ffffff;
  font-weight: 600;
}
.c4-sch-knob {
  width: 90px;
  height: 90px;
  border-radius: 50%;
  background: rgba(255,255,255,0.95);
  border: 4px solid #909399;
  cursor: pointer;
  transition: all .25s cubic-bezier(.4,0,.2,1);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(0,0,0,0.08);
}
.c4-sch-knob:hover {
  transform: scale(1.05);
  box-shadow: 0 4px 16px rgba(144,147,153,0.2);
}
.c4-sch-knob.c4-sch-active {
  background: linear-gradient(135deg, #409EFF 0%, #66B1FF 100%);
  border-color: #409EFF;
}
.c4-sch-knob-inner {
  font-size: 36px;
  font-weight: 700;
  color: #67C23A;
  line-height: 1;
}
.c4-sch-knob.c4-sch-active .c4-sch-knob-inner {
  color: #ffffff;
}
.c4-sch-knob-value-inner {
  font-size: 12px;
  color: #909399;
  text-align: center;
  margin-top: 4px;
  max-width: 70px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.c4-sch-knob.c4-sch-active .c4-sch-knob-value-inner {
  color: rgba(255,255,255,0.9);
}
.c4-sch-knob-label {
  font-size: 14px;
  color: #606266;
  font-weight: 500;
}
.c4-sch-knob-container.c4-sch-active .c4-sch-knob-label {
  color: rgba(255,255,255,0.9);
}

/* ============ B6 Key Schematic Card ============ */
.b6-key-schematic {
  margin-bottom: 22px;
  padding: 24px 28px;
  background: #F5F5F5;
  border-radius: 16px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.06);
  position: relative;
  overflow: hidden;
}
.b6-key-schematic::before {
  content: '';
  position: absolute;
  top: -20%; right: -10%;
  width: 80px; height: 80px;
  background: rgba(0,0,0,0.03);
  border-radius: 50%;
}
.b6-key-schematic::after {
  content: '';
  position: absolute;
  bottom: -15%; left: -8%;
  width: 60px; height: 60px;
  background: rgba(0,0,0,0.02);
  border-radius: 50%;
}
.b6-sch-title {
  margin: 0 0 20px 0;
  font-size: 18px;
  font-weight: 700;
  color: #303133;
  letter-spacing: 0.5px;
  display: flex;
  align-items: center;
  gap: 8px;
  position: relative;
  z-index: 1;
}
.b6-sch-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 14px;
  position: relative;
  z-index: 1;
}
.b6-sch-key {
  padding: 18px 12px 20px 12px;
  background: rgba(255,255,255,0.95);
  border-radius: 14px;
  cursor: pointer;
  transition: all .25s cubic-bezier(.4,0,.2,1);
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  box-shadow: 0 2px 8px rgba(0,0,0,0.08);
  border: 5px solid var(--backlight-color, #C0C4CC);
  box-sizing: border-box;
}
.b6-sch-key::before {
  content: '';
  position: absolute;
  top: -8px; left: -8px; right: -8px; bottom: -8px;
  border-radius: 19px;
  border: 4px solid var(--backlight-color, #C0C4CC);
  opacity: 0.6;
  pointer-events: none;
  transition: opacity .25s;
}
.b6-sch-key:hover::before {
  opacity: 0.9;
}
.b6-sch-key:hover {
  transform: translateY(-4px) scale(1.02);
  box-shadow: 0 8px 24px rgba(102,126,234,0.25), 0 0 24px var(--backlight-color, #C0C4CC);
}
.b6-sch-active {
  background: linear-gradient(135deg, #409EFF 0%, #66B1FF 100%) !important;
  color: #ffffff !important;
  border-color: #409EFF !important;
}
.b6-sch-active::before {
  border-color: rgba(255,255,255,0.5) !important;
}
.b6-sch-key-num {
  width: 32px; height: 32px;
  border-radius: 50%;
  background: linear-gradient(135deg, #409EFF 0%, #66B1FF 100%);
  color: #ffffff;
  font-size: 14px;
  font-weight: 800;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 6px;
  box-shadow: 0 3px 8px rgba(64,158,255,0.3);
}
.b6-sch-active .b6-sch-key-num {
  background: rgba(255,255,255,0.3) !important;
}
.b6-sch-key-label {
  font-size: 12px;
  color: #606266;
  margin-bottom: 4px;
  font-weight: 500;
}
.b6-sch-active .b6-sch-key-label {
  color: rgba(255,255,255,0.9) !important;
}
.b6-sch-key-value {
  font-size: 11px;
  color: #909399;
  text-align: center;
  line-height: 1.4;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.b6-sch-active .b6-sch-key-value {
  color: #ffffff !important;
  font-weight: 600;
}

/* ============ B4 Key Schematic Card ============ */
.b4-key-schematic {
  margin-bottom: 22px;
  padding: 24px 28px;
  background: #F5F5F5;
  border-radius: 16px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.06);
  position: relative;
  overflow: hidden;
}
.b4-key-schematic::before {
  content: '';
  position: absolute;
  top: -20%; right: -10%;
  width: 80px; height: 80px;
  background: rgba(0,0,0,0.03);
  border-radius: 50%;
}
.b4-key-schematic::after {
  content: '';
  position: absolute;
  bottom: -15%; left: -8%;
  width: 60px; height: 60px;
  background: rgba(0,0,0,0.02);
  border-radius: 50%;
}
.b4-sch-title {
  margin: 0 0 20px 0;
  font-size: 18px;
  font-weight: 700;
  color: #303133;
  letter-spacing: 0.5px;
  display: flex;
  align-items: center;
  gap: 8px;
  position: relative;
  z-index: 1;
}
.b4-sch-grid {
  display: flex;
  gap: 14px;
  justify-content: center;
  position: relative;
  z-index: 1;
}
.b4-sch-key {
  flex: 1;
  min-width: 100px;
  max-width: 140px;
  padding: 18px 12px 20px 12px;
  background: rgba(255,255,255,0.95);
  border-radius: 14px;
  cursor: pointer;
  transition: all .25s cubic-bezier(.4,0,.2,1);
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  box-shadow: 0 2px 8px rgba(0,0,0,0.08);
  border: 5px solid var(--backlight-color, #C0C4CC);
  box-sizing: border-box;
}
.b4-sch-key::before {
  content: '';
  position: absolute;
  top: -8px; left: -8px; right: -8px; bottom: -8px;
  border-radius: 19px;
  border: 4px solid var(--backlight-color, #C0C4CC);
  opacity: 0.6;
  pointer-events: none;
  transition: opacity .25s;
}
.b4-sch-key:hover::before {
  opacity: 0.9;
}
.b4-sch-key:hover {
  transform: translateY(-4px) scale(1.02);
  box-shadow: 0 8px 24px rgba(102,126,234,0.25), 0 0 24px var(--backlight-color, #C0C4CC);
}
.b4-sch-active {
  background: linear-gradient(135deg, #409EFF 0%, #66B1FF 100%) !important;
  color: #ffffff !important;
  border-color: #409EFF !important;
}
.b4-sch-active::before {
  border-color: rgba(255,255,255,0.5) !important;
}
.b4-sch-key-num {
  width: 36px; height: 36px;
  border-radius: 50%;
  background: linear-gradient(135deg, #409EFF 0%, #66B1FF 100%);
  color: #ffffff;
  font-size: 16px;
  font-weight: 800;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 8px;
  box-shadow: 0 3px 8px rgba(64,158,255,0.3);
}
.b4-sch-active .b4-sch-key-num {
  background: rgba(255,255,255,0.3) !important;
}
.b4-sch-key-label {
  font-size: 13px;
  color: #606266;
  margin-bottom: 6px;
  font-weight: 500;
}
.b4-sch-active .b4-sch-key-label {
  color: rgba(255,255,255,0.9) !important;
}
.b4-sch-key-value {
  font-size: 12px;
  color: #909399;
  text-align: center;
  line-height: 1.5;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.b4-sch-active .b4-sch-key-value {
  color: #ffffff !important;
  font-weight: 600;
}

@media (max-width: 600px) {
  .c4-sch-layout {
    flex-direction: column;
    gap: 20px;
  }
  .c4-sch-grid {
    grid-template-columns: repeat(2, 1fr);
  }
  .c4-sch-key {
    min-width: 100px;
    padding: 14px 10px 16px 10px;
  }
  .c4-sch-key-num {
    width: 28px;
    height: 28px;
    font-size: 14px;
  }
  .c4-sch-knob {
    width: 70px;
    height: 70px;
  }
  .c4-sch-knob-arrow {
    width: 70px;
    height: 70px;
  }
  .c4-sch-knob-arrow::before {
    border-top: 22px solid transparent;
    border-bottom: 22px solid transparent;
  }
  .c4-sch-knob-arrow.left::before {
    border-right: 32px solid rgba(255,255,255,0.95);
  }
  .c4-sch-knob-arrow.right::before {
    border-left: 32px solid rgba(255,255,255,0.95);
  }
  .c4-sch-knob-arrow::after {
    border-top: 19px solid transparent;
    border-bottom: 19px solid transparent;
    top: 2px;
  }
  .c4-sch-knob-arrow.left::after {
    border-right: 28px solid #909399;
  }
  .c4-sch-knob-arrow.right::after {
    border-left: 28px solid #909399;
  }
  .c4-sch-knob-arrow-value {
    margin-top: 48px;
    font-size: 10px;
    max-width: 70px;
  }
  .c4-sch-knob-inner {
    font-size: 28px;
  }
  .b6-sch-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

/* ============ E6 (三模键盘 6键) ============ */
.e6-mode-card {
  margin-bottom: 22px;
  padding: 22px 26px;
  background: linear-gradient(135deg, #E8FFF4 0%, #F5F7FA 55%, #EAF1FF 100%);
  border: 1px solid #C9EDDC;
  border-radius: 14px;
  box-shadow: 0 2px 8px rgba(103,194,58,0.08);
}
.e6-mode-title {
  margin: 0 0 16px 0;
  font-size: 18px;
  font-weight: 700;
  color: #268F4B;
  letter-spacing: 0.5px;
  display: flex;
  align-items: center;
  gap: 8px;
}
.e6-mode-title i {
  font-size: 22px;
  color: #67C23A;
}
.e6-field-label {
  display: inline-block;
  min-width: 88px;
  font-size: 14px;
  font-weight: 600;
  color: #303133;
  line-height: 40px;
}
.e6-device-mode-group .el-radio {
  font-size: 14px;
  margin-right: 22px;
  padding: 6px 14px;
  border: 1px solid transparent;
  border-radius: 8px;
  transition: all .2s ease;
}
.e6-device-mode-group .el-radio:hover {
  background: rgba(103,194,58,0.08);
  border-color: #C2EDD5;
}
.e6-device-mode-group .el-radio.is-checked {
  background: rgba(103,194,58,0.12);
  border-color: #67C23A;
}
.e6-mode-tip {
  margin-top: 6px;
  padding: 10px 14px;
  background: #fff9e6;
  border-radius: 8px;
  color: #8a7340;
  font-size: 13px;
  line-height: 1.6;
  display: flex;
  align-items: flex-start;
  gap: 8px;
  text-align: left;
}
.e6-mode-tip i {
  color: #E6A23C;
  flex-shrink: 0;
  margin-top: 2px;
}
.e6-key-schematic {
  margin-bottom: 22px;
  padding: 24px 28px;
  background: #F5F5F5;
  border-radius: 16px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.06);
  position: relative;
  overflow: hidden;
}
.e6-key-schematic::before {
  content: '';
  position: absolute;
  top: -20%; right: -10%;
  width: 80px; height: 80px;
  background: rgba(0,0,0,0.03);
  border-radius: 50%;
}
.e6-key-schematic::after {
  content: '';
  position: absolute;
  bottom: -15%; left: -8%;
  width: 60px; height: 60px;
  background: rgba(0,0,0,0.02);
  border-radius: 50%;
}
.e6-sch-title {
  margin: 0 0 20px 0;
  font-size: 18px;
  font-weight: 700;
  color: #303133;
  letter-spacing: 0.5px;
  display: flex;
  align-items: center;
  gap: 8px;
  position: relative;
  z-index: 1;
}
.e6-sch-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 14px;
  position: relative;
  z-index: 1;
}
.e6-sch-key {
  padding: 18px 12px 20px 12px;
  background: rgba(255,255,255,0.95);
  border-radius: 14px;
  cursor: pointer;
  transition: all .25s cubic-bezier(.4,0,.2,1);
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  box-shadow: 0 2px 8px rgba(0,0,0,0.08);
  border: 5px solid #67C23A;
  box-sizing: border-box;
}
.e6-sch-key::before {
  content: '';
  position: absolute;
  top: -8px; left: -8px; right: -8px; bottom: -8px;
  border-radius: 19px;
  border: 4px solid #67C23A;
  opacity: 0.6;
  pointer-events: none;
  transition: opacity .25s;
}
.e6-sch-key:hover::before {
  opacity: 0.9;
}
.e6-sch-key:hover {
  transform: translateY(-4px) scale(1.02);
  box-shadow: 0 8px 24px rgba(103,194,58,0.25), 0 0 24px rgba(103,194,58,0.4);
}
.e6-sch-active {
  background: linear-gradient(135deg, #67C23A 0%, #85CE61 100%) !important;
  color: #ffffff !important;
  border-color: #67C23A !important;
}
.e6-sch-active::before {
  border-color: rgba(255,255,255,0.5) !important;
}
.e6-sch-key-num {
  width: 32px; height: 32px;
  border-radius: 50%;
  background: linear-gradient(135deg, #67C23A 0%, #85CE61 100%);
  color: #ffffff;
  font-size: 14px;
  font-weight: 800;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 6px;
  box-shadow: 0 3px 8px rgba(103,194,58,0.3);
}
.e6-sch-active .e6-sch-key-num {
  background: rgba(255,255,255,0.3) !important;
}
.e6-sch-key-label {
  font-size: 12px;
  color: #606266;
  margin-bottom: 4px;
  font-weight: 500;
}
.e6-sch-active .e6-sch-key-label {
  color: rgba(255,255,255,0.9) !important;
}
.e6-sch-key-value {
  font-size: 11px;
  color: #909399;
  text-align: center;
  line-height: 1.4;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.e6-sch-active .e6-sch-key-value {
  color: #ffffff !important;
  font-weight: 600;
}
@media (max-width: 600px) {
  .e6-sch-grid {
    grid-template-columns: repeat(2, 1fr);
  }
  .e6-device-mode-group .el-radio {
    display: block;
    margin-bottom: 8px;
    margin-right: 0;
  }
}
.factory-dialog .el-dialog__title {
  font-size: 22px;
  font-weight: 700;
}
.factory-dialog .el-dialog__body {
  font-size: 16px;
}
.factory-toolbar {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 16px;
  flex-wrap: wrap;
}
.factory-toolbar .el-button {
  font-size: 16px;
  height: 40px;
  padding: 0 18px;
}
.factory-toolbar .el-checkbox__label {
  font-size: 16px;
}
.factory-error {
  color: #F56C6C;
  font-size: 15px;
}
.factory-led-tip {
  margin-bottom: 14px;
  padding: 10px 14px;
  border-radius: 8px;
  background: #fdf6ec;
  border: 1px solid #f5dab1;
  color: #b88230;
  font-size: 15px;
  line-height: 1.6;
}
.factory-banner {
  padding: 13px 16px;
  border-radius: 10px;
  font-weight: 700;
  font-size: 17px;
  margin-bottom: 14px;
}
.factory-banner.pass { background: #67C23A; color: #fff; }
.factory-banner.fail { background: #F56C6C; color: #fff; }
.factory-dialog .el-table {
  font-size: 16px;
}
.factory-dialog .el-table th .cell {
  font-size: 16px;
  font-weight: 700;
}
.factory-dialog .el-table td .cell {
  font-size: 16px;
  line-height: 1.6;
}
.factory-result {
  font-size: 17px;
  font-weight: 700;
  white-space: nowrap;
}
.factory-result.pass { color: #529b2e; }
.factory-result.fail { color: #c45656; }
.factory-result.wait { color: #909399; font-weight: 500; }
.factory-result.info { color: #C0C4CC; font-weight: 500; }
.factory-raw {
  margin-top: 12px;
  background: #f5f7fa;
  padding: 10px;
  font-size: 14px;
  overflow: auto;
}
.factory-note {
  color: #909399;
  font-size: 14px;
  margin-top: 10px;
}
.el-table .factory-ok td { background: #f0f9eb !important; color: #529b2e !important; font-weight: 600; }
.el-table .factory-bad td { background: #fef0f0 !important; color: #c45656 !important; font-weight: 600; }
</style>



// WEBPACK FOOTER //
// src/App.vue






// WEBPACK FOOTER //
// src/App.vue